/**
 * AgentDetailPageV2 — Phase 4 of the Rebirth migration.
 *
 * The deepest authoring surface in the platform. Mounts at /agents/:id
 * when features.rebirth.agentlet-detail.v2 is on. Composes:
 *
 *   Header     — TrustChip + KillSwitchButton + archetype pill + actions
 *   TabNav     — [Registration?] · Build · Decide · Act
 *   Build tab  — PhaseGate · skill list · creation menu · doc upload (4a)
 *   Decide tab — eval review + autonomy decisions (4b — placeholder for now)
 *   Act tab    — assignment + publish + live execution feed (4c — placeholder)
 *
 * Sub-phase scope:
 *   - 4a (this PR): page shell + header + Build tab functional
 *   - 4b: Decide tab implementation
 *   - 4c: Act tab + publish flow
 *
 * Composition rule (§5.3): when the flag is OFF, the route resolves to a
 * Navigate redirect that sends the user to /agents (v1 page with the agent
 * pre-selected via session storage). That preserves every existing v1
 * code path and bookmark.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Plus, Bot, Globe, FileText, Sparkles as SparklesIcon, ExternalLink, FlaskConical, RefreshCw, ChevronDown, ChevronRight as ChevronRightIcon, Target, Send, CheckCircle, Pause, Ban, Copy, Search, Trash2, Zap, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePlatform } from '../contexts/PlatformContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthAgentletDetailEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import {
  Button, Pill, TrustChip, KillSwitchButton, TabNav, PhaseGate, Card,
  Table, EmptyState, MenuDropdown, Skeleton, Modal, Slideout,
  PromoteSlideout,
  /* EXTELET-V3 · Phase 7 — Credentials + Snippet re-show + Rotate /
   * Suspend / Reactivate / Revoke / Edit scope + first-call observer */
  ExteletCredentialsCard,
} from '../components/v2';
import { ImportFromConnectorModalV2 } from '../components/v2/ImportFromConnectorModalV2';
/* #66 — ActSkillShells retired: native Act now uses the shared SkillPromotionCard
 * (surface="act"), same as Build/Decide/ExteLet. */
import { TestRunSidePanelV2 } from '../components/v2/TestRunSidePanelV2';
import { SkillCreateSlideout } from '../components/v2/skill/SkillCreateSlideout';
/* #66 — the ONE shared 2×2 V1-parity skill card, identical for every agentlet
 * type. Native Build/Decide/Act now render this same card (reusing the shared
 * advanceSkill/regressSkill/reviewSkill/deleteSkill/pushSkill BE) instead of the
 * old bespoke Table (Build) / decision matrix (Decide). */
import SkillPromotionCard from '../components/v2/skill/SkillPromotionCard';
/* #66 / conformance generalization — the Decide-phase Trust-baseline (conformance)
 * surface for NATIVE Lets/AutoLets/SysLets, reusing the same scoring-config +
 * golden-baseline BE as the ExteLet Decide tab. AutoLet = source-workflow oracle;
 * Let/SysLet = golden baseline oracle. */
import NativeConformanceCard from '../components/v2/skill/NativeConformanceCard';
/* Step 6 (Platform-Eng workflow) — structured skill invocation form.
 * Opened from the BuildTab per-skill "Run…" button. Bypasses the
 * conversational TestRunSidePanelV2 planner and posts directly to
 * /agents/{id}/skills/{sid}/invoke with operator-supplied
 * (export, args). Currently dispatches wasm_skill only at the
 * backend; non-wasm types surface a clear advisory in the form. */
import { SkillInvocationForm } from '../components/v2/skill/SkillInvocationForm';
/* Step 5 (Platform-Eng workflow) — install an existing skill from
 * the tenant's shared registry catalog onto the current agent.
 * Mounted next to the "New skill" menu on the BuildTab. Closes the
 * "skills are agent-scoped at creation" gap for platform teams
 * running shared skill libraries. */
import { AddExistingSkillSlideout } from '../components/v2/skill/AddExistingSkillSlideout';
/* G395 — Pagination for the publish history + recent executions tables
 * which previously rendered unbounded / silently-capped row sets. */
import { Pagination, usePaginationState } from '../components/common/Pagination';
import EntityLabel from '../components/common/EntityLabel';
import { useMarketplaceCatalogue } from '../data/marketplaceRegistry';
import { findMarketplaceForInstalled } from '../data/marketplaceFromRegistry';
import { AuditHashIntegrity } from '../components/v2/trust';
import { SpiffeIdentityPanel } from '../components/v2/spiffe/SpiffeIdentityPanel';
// IntentProfilesEditor import DELETED in V219 (Phase 5) — see Intent Bindings panel mounted below.
/* Intent Profiles Catalog v1.0 — Phase 1E.3.
 * Mounted alongside (NOT replacing) the inline IntentProfilesEditor.
 * The two coexist intentionally: the inline editor remains the runtime
 * source-of-truth until Phase 1D's
 * `agentlets.intent.runtime-read-from-catalog` feature flag flips. Once
 * flipped, the catalog bindings drive runtime; once Phase 5 removes the
 * inline column entirely, the legacy editor can be retired. */
import { IntentBindingsPanel } from '../components/v2/intent/IntentBindingsPanel';
import { WasmSkillUploader, WasmSandboxBadge } from '../components/v2/skill/WasmSkillUploader';
import { SkillLinkedResourcePanel } from '../components/v2/skill/SkillLinkedResourcePanel';
import { skillTypeOf, isSandboxedKind } from '../constants/skillTypes';
import { SKILL_AUTONOMY_OPTIONS, normalizeAutonomy, autonomyLabel } from '../constants/autonomy';
/* W1 (D-1) — Governance Policy Skills surface: the single ExteLet governance
 * authoring surface (folds the policy wizard into editable policy_skills, each
 * materialized to runtime). The wizard component itself is reused inside this
 * one in policy-skill mode. */
import GovernancePolicySkills from '../components/gel/GovernancePolicySkills';
import ExteletBuildSurface from '../components/v2/extelet/ExteletBuildSurface';
/* ExteletDecideTab + ExteletActTab now render inside ExteletPromote (J1b) —
 * they are no longer top-level tabs here. ExteletBuildSurface stays because
 * RegistrationTab's section="build" variant still renders it. */
import ExteletPromote from '../components/v2/extelet/ExteletPromote';
import AccessPanel from '../components/v2/extelet/ExteletAccessPanel';

const ARCHETYPE_LABEL = { AutoLet: 'AutoLet', Let: 'Let', ExteLet: 'ExteLet', SysLet: 'SysLet' };

function deriveArchetype(agent) {
  if (!agent) return 'Let';
  if (agent.archetype) return agent.archetype;
  if (agent.system === true || agent.kind === 'system' || agent.isSystemAgent === true) return 'SysLet';
  /* Fix G55: backend persists external classification as
   * `agentClassification: 'EXTERNAL'` and `isExternal: true`. The
   * previous test only matched our own internal field names so freshly
   * registered ExteLets fell through to "Let". */
  if (
    agent.external === true ||
    agent.isExternal === true ||
    agent.gel === true ||
    agent.type === 'external' ||
    agent.agentClassification === 'EXTERNAL'
  ) return 'ExteLet';
  /* Fix G54: backend stamps `discoverySource` for both auto-created
   * agents and the discovery flow. Match either signal so AutoLets
   * minted via the modal don't fall through to "Let". The intent
   * passed via `?archetype=autolet` URL is also surfaced (read in the
   * page-level useEffect). */
  /* G80 — backend reliably sets neither `autoCreated` nor
   * `discoverySource` (Round 18 trace: discoverySource came back as
   * 'MANUAL' for a freshly-Auto-Created agent). Per
   * GOLDEN_CEG_RULES §1.6, the canonical signal is the `AutoLets-`
   * **name prefix**. Mirrors v1 `classifyAgentType()` in
   * constants/agentTypes.js line 156. Same fix in
   * AgentsInventoryPageV2.deriveArchetype. */
  if (
    agent.autoCreated === true ||
    agent.source === 'auto-create' ||
    agent.discoverySource === 'auto-create' ||
    agent.discoverySource === 'autolet' ||
    (typeof agent.name === 'string' && agent.name.startsWith('AutoLets-'))
  ) return 'AutoLet';
  return 'Let';
}

function AgentDetailPageV2Inner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { agents, loadAgents } = usePlatform();
  /* G152 — defensive wrapper to keep clicks crash-proof even when the
   * toast context is momentarily unavailable (provider remount, stale
   * bundle, etc.). Worst case: console.warn; never a ReferenceError. */
  const toastCtx = useToast();
  const addToast = React.useCallback((arg, type, duration) => {
    if (toastCtx && typeof toastCtx.addToast === 'function') {
      return toastCtx.addToast(arg, type, duration);
    }
    // eslint-disable-next-line no-console
    console.warn('[AgentDetailPageV2] toast unavailable, dropped:', arg);
    return null;
  }, [toastCtx]);
  const { session, hasPermission } = useAuth();
  const { recordEntity } = useNavigation();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  // Record this Agent in the precise recent-history trail (top-bar "Recent")
  // so the operator can jump straight back to it. Keyed on the clean path.
  useEffect(() => {
    if (agent?.id && agent?.name) {
      recordEntity({ type: 'Agent', id: agent.id, name: agent.name, path: `/agents/${agent.id}` });
    }
  }, [agent?.id, agent?.name, recordEntity]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('build');
  // Native journey: Build/Decide/Act are a sub-nav UNDER the Promote step.
  // promotePhase drives that sub-nav; seeded to the agent's current phase by the
  // landing effect. (ExteLets nest B-D-A inside ExteletPromote instead.)
  const [promotePhase, setPromotePhase] = useState('build');
  const [killSwitchActive, setKillSwitchActive] = useState(false);
  /* G380 — V2 SkillCreateSlideout state. `initialSkillType` lets callers
   * (MenuDropdown items, ?intent=add-skill&type=…) pre-pick a type so the
   * slideout skips its L1 picker step. The slideout owns its own form
   * state — the parent just controls open/close and post-create reload. */
  const [skillModalOpen, setSkillModalOpen] = useState(false);
  const [initialSkillType, setInitialSkillType] = useState(null);
  /* G85 — delete-agent modal with provenance + GOLDEN_CEG_RULES §8.1
   * compliance. Frontend collects a type-to-confirm input + surfaces
   * the impact summary; backend enforces the actual `has_children` /
   * audit-log invariants and returns 409 if a v2/v3 lineage exists. */
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  /* G177 — Test Run side panel (V1 parity). */
  const [testRunOpen, setTestRunOpen] = useState(false);
  /* Phase 9 — Promote affordance state. Slideout queries the source
   * tenant's Promotion Graph and lists enabled outbound paths from the
   * agent's current Instance. Gated by promotion.initiate at button-level. */
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  /* P7J-RUN-037 — Phase 3b regression fix. `registration` is referenced
   * from this outer scope in two places — onAdvancePhase's GEL-route
   * decision (lines ~487-513) and the Skills card "Discover capabilities"
   * gate (~line 3521). It used to be declared only inside the inner
   * RegistrationTab, so any render that touched line 3521 (or any click
   * that fired onAdvancePhase for an ExteLet) threw a ReferenceError and
   * tanked the whole page. State is lifted to this outer component now;
   * RegistrationTab consumes via props (single fetch, no duplication). */
  const [registration, setRegistration] = useState(null);
  const [regLoading, setRegLoading]   = useState(true);

  const reloadAgent = async () => {
    if (!id) return;
    const get = api['getAgent'];
    if (typeof get !== 'function') return;
    const res = await get(id);
    if (res?.ok && res.data) setAgent(res.data);
  };

  /* P7J-RUN-037 — Lifted from RegistrationTab. Loads the
   * external_agent_registration row keyed on agent.id. State semantics
   * preserved: `null` = not yet loaded, `false` = no registration on
   * file (legacy ExteLet that pre-dates the registration table, or a
   * native Let / AutoLet / SysLet), object = registration row. */
  const reloadRegistration = React.useCallback(async () => {
    if (!id) return;
    setRegLoading(true);
    try {
      const fn = api['gelGetRegistrationByAgent'];
      if (typeof fn !== 'function') { setRegistration(false); return; }
      const res = await fn(id);
      if (res && res.ok === false) { setRegistration(false); return; }
      const row = res?.data || res;
      setRegistration(row && row.id ? row : false);
    } catch (e) {
      setRegistration(false);
    } finally {
      setRegLoading(false);
    }
  }, [id]);

  /* P7J-RUN-037 — Phase 3b consolidation. The "Discover capabilities"
   * button lives in the BuildTab Skills card (the canonical inventory),
   * but the actual probe handler + result state used to live inside
   * RegistrationTab. That cross-component closure dependency caused
   * ReferenceError: handleDiscoverInline is not defined the moment the
   * Skills card tried to render the button on an ExteLet.
   *
   * Resolution: lift the discover state + handler to the only scope
   * ancestral to both BuildTab and RegistrationTab. Pass down as props.
   * RegistrationTab's results card (which displays discoveredSkills
   * and offers Save / Add manually / Remove) reads the same lifted
   * state, so a discover fired from either surface populates one
   * canonical list. */
  const [discoveryRunning, setDiscoveryRunning] = useState(false);
  const [discoveredSkills, setDiscoveredSkills] = useState([]);

  const handleDiscoverInline = React.useCallback(async () => {
    if (!registration?.id) return;
    setDiscoveryRunning(true);
    try {
      const res = await api.gelDiscoverCapabilities?.(registration.id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Discovery failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      // The discover-capabilities endpoint returns { capabilities, count, ... }. The old
      // extraction only looked for `skills`, so a successful discovery (capabilities: [...])
      // always parsed to [] and the operator saw "Discovered 0 skill(s)" — leaving skillMappings
      // empty, which blocks proxy-skill materialization and Execution-Ready (BUGS_COWORK_E2E B1).
      // Read `capabilities` first, keep the legacy shapes as fallbacks.
      const list = Array.isArray(res?.data?.capabilities) ? res.data.capabilities
                 : Array.isArray(res?.capabilities) ? res.capabilities
                 : Array.isArray(res?.data?.skills) ? res.data.skills
                 : Array.isArray(res?.skills) ? res.skills
                 : Array.isArray(res?.data) ? res.data
                 : Array.isArray(res) ? res : [];
      setDiscoveredSkills(list);
      addToast({ tone: 'success', title: `Discovered ${list.length} skill(s)`,
        description: list.length > 0 ? 'Review the list, then click Save mappings to persist.'
                                     : 'Connector did not advertise any tools or functions.' });
    } catch (err) {
      addToast({ tone: 'danger', title: 'Discovery failed', description: err?.message || String(err) });
    } finally {
      setDiscoveryRunning(false);
    }
  }, [registration, addToast]);

  useEffect(() => {
    let cancelled = false;

    /* Fix G2: validate id shape BEFORE hitting the backend. The backend
     * rejects non-UUID ids with 400; the previous code treated the
     * exception as recoverable and rendered partial chrome with no agent
     * loaded. Now we short-circuit with a clear error state. */
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Try the cache first (works for non-UUID demo ids too)
        const cached = (agents || []).find((a) => a.id === id);
        if (cached) { if (!cancelled) setAgent(cached); }
        // If the id isn't a UUID, skip the API call entirely — no point
        // hitting an endpoint that always rejects, just show the error.
        if (!UUID_RE.test(id)) {
          if (!cancelled && !cached) {
            setError(`"${id}" is not a valid Agentlet id. Open this page from the Agentlets inventory.`);
          }
          return;
        }
        // Bracket-notation soft-dependency guard — see W134 above.
        const get = api['getAgent'];
        if (typeof get === 'function') {
          /* Fix G17: apiFetch returns an envelope {ok, data, error}.
           * Previously we stored the envelope as `agent`, which made
           * agent.name undefined and rendered an empty header. Unwrap
           * the envelope and surface envelope errors as a real error
           * state. The backend response shape is the raw agent (no
           * intermediate `data` key on the wire), so apiFetch wraps it
           * itself — this unwrapping is correct for every endpoint that
           * doesn't already use { data: ... } in its response body. */
          const res = await get(id);
          if (!cancelled) {
            if (res && res.ok === false) {
              /* DEF-DISC-CANVAS-404-001 — when the id belongs to a
               * DiscoveredAgent (not a governed Agent), getAgent 404s.
               * Bounce the operator into Discovery's staging area with
               * the row pre-selected so they can investigate / promote
               * the agent properly. This stops the Canvas-404 dead-end
               * the user hit when clicking through shadow rows. */
              const looksLikeDiscoveredAgent =
                res.status === 404 ||
                (res.error && /not found|404/i.test(res.error));
              if (looksLikeDiscoveredAgent) {
                navigate(`/discovery?tab=agents&selected=${id}`, { replace: true });
                return;
              }
              setError(res.error || 'Failed to load Agentlet');
            } else if (res && res.ok && res.data) {
              setAgent(res.data);
            } else if (res && (res.id || res.name)) {
              // Some api helpers return the body directly without an envelope.
              setAgent(res);
            }
          }
        } else if (typeof loadAgents === 'function' && !cached) {
          await loadAgents();
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // CRA build doesn't have the react-hooks plugin loaded, so the
    // named directive trips ESLint with "rule not found". Bare
    // `eslint-disable-next-line` swallows whatever rule actually fires
    // here without depending on the plugin. Same fix applied in
    // DashboardLayoutV2.jsx — see W134.
    // eslint-disable-next-line
  }, [id]);

  /* Fix G54/G55 (URL override): when the user just minted an agent via
   * the inventory create modal, we redirect them to the detail page
   * with `?archetype=autolet|extelet|let` so we can render the correct
   * archetype pill even before the backend round-trips the discovery
   * fields. The URL value loses precedence as soon as the agent record
   * carries a real `archetype` / classification signal. */
  const urlArchetype = (() => {
    try {
      const v = new URLSearchParams(window.location.search).get('archetype');
      if (v === 'autolet') return 'AutoLet';
      if (v === 'extelet') return 'ExteLet';
      if (v === 'let')     return 'Let';
      return null;
    } catch { return null; }
  })();
  const derived = deriveArchetype(agent);
  const archetype = (derived === 'Let' && urlArchetype) ? urlArchetype : derived;
  const isExteLet = archetype === 'ExteLet';

  /* P7J-RUN-037 — Trigger the lifted reloadRegistration when the
   * archetype settles on ExteLet. Native Lets / AutoLets / SysLets
   * carry no external_agent_registration row so we short-circuit
   * to `false` (no fetch, no spurious 404). */
  useEffect(() => {
    if (isExteLet && id) {
      reloadRegistration();
    } else if (id) {
      setRegistration(false);
      setRegLoading(false);
    }
    // eslint-disable-next-line
  }, [isExteLet, id, reloadRegistration]);

  // Build the tab list — ExteLets get a leading Registration tab.
  /* G209 — Phase chevron progressive highlight.
   * Build / Decide / Act tabs carry a status dot derived from agent.phase:
   *   • success (green) — this phase's gate has been cleared
   *   • action  (blue)  — this is the current working phase
   *   • undefined       — future phase, not yet reached
   *
   * This mirrors the user-facing UX requirement that once Build's GateCheck
   * passes, the Decide chevron lights up; once Decide's GateCheck passes,
   * Act lights up. The TabNav's underline still tracks the *active* tab
   * (which can differ from the agent's phase if the operator manually
   * navigates), but the dot tracks the *agent's actual phase progression*
   * so the chevron remains an accurate phase indicator regardless of
   * which tab the operator is viewing. */
  const tabs = useMemo(() => {
    const PHASE_ORDER = ['build', 'decide', 'act'];
    const currentPhase = (agent?.phase || 'build').toLowerCase();
    const currentIdx = PHASE_ORDER.indexOf(currentPhase);
    const phaseDot = (phaseId) => {
      const idx = PHASE_ORDER.indexOf(phaseId);
      if (idx < 0 || currentIdx < 0) return undefined;
      if (idx < currentIdx) return 'success'; // gate cleared, agent has moved past
      if (idx === currentIdx) return 'action'; // current working phase
      return undefined; // future phase
    };

    /* ExteLet — the Prove7 journey (J1b). Seven steps replace the legacy
     * Registration/Build/Decide/Act tab set:
     *   1 Register (strict gate) · 2 Identity · 3 Intent · 4 RBAC ·
     *   5 Promote (hosts the B-D-A gate, formerly "Trust Gate") ·
     *   6 Enforce · 7 Audit.
     * Step 1 is a STRICT prerequisite: until a registration row exists there
     * is no agent to mint credentials for / bind intent to / grant roles on,
     * so Steps 2-7 are locked (disabled). Steps 2-4 are free-order; their
     * completion is re-verified by the Promote gate (identity_valid /
     * intent_bound / assignment_present) — belt and suspenders. The id
     * `registration` is preserved (display label "Register") so existing
     * ?tab=registration deep-links and the registration content block are
     * untouched. */
    if (isExteLet) {
      const registered = !!registration;
      const lock = !registered;
      const executionReady = !!agent?.executionReadyAt;
      return [
        { id: 'registration', label: 'Register', dot: registered ? 'success' : 'action' },
        { id: 'identity', label: 'Identity', disabled: lock },
        { id: 'intent',   label: 'Intent',   disabled: lock },
        // Display label is "Access" (the access-control step); the id stays
        // 'rbac' so existing ?tab=rbac deep-links + the render switch are stable.
        { id: 'rbac',     label: 'Access',    disabled: lock },
        { id: 'promote',  label: 'Promote',   disabled: lock,
          dot: executionReady ? 'success' : (registered ? 'action' : undefined) },
        { id: 'enforce',  label: 'Enforce',   disabled: lock },
        { id: 'audit',    label: 'Audit',     disabled: lock },
      ];
    }

    /* Native Lets / AutoLets / SysLets — the SAME Prove7 journey as ExteLets,
     * MINUS Register: they're authored in-platform via +New Agentlet, so there's
     * no external registration gate and no step is locked. Build / Decide / Act
     * are nested UNDER the Promote step (sub-nav), unchanged. Identity / Intent /
     * Access (formerly above-nav collapsibles / not surfaced) are now first-class
     * steps. Enforce → Trust Ontology, Audit → hash chain — same as ExteLet. */
    const promoteDot = phaseDot(currentPhase) === 'success' ? 'success' : 'action';
    return [
      { id: 'identity', label: 'Identity' },
      { id: 'intent',   label: 'Intent' },
      { id: 'rbac',     label: 'Access' },
      { id: 'promote',  label: 'Promote', dot: promoteDot },
      { id: 'enforce',  label: 'Enforce' },
      { id: 'audit',    label: 'Audit' },
    ];
  }, [isExteLet, agent?.phase, agent?.executionReadyAt, registration]);

  // If the active tab is no longer in the list (e.g., archetype changed), reset.
  // Defense-in-depth: B-D-A phase ids are NOT top-level tabs — they are the
  // Promote step's sub-nav. If a phase id ever leaks into activeTab, route it to
  // the Promote step (and pin the sub-nav) instead of bouncing the user to
  // tabs[0] (Identity). This makes the whole "invalid tab → Identity bounce"
  // class impossible even if a future caller regresses.
  useEffect(() => {
    if (tabs.find((t) => t.id === activeTab)) return;
    if (['build', 'decide', 'act'].includes(activeTab)) {
      if (!isExteLet) setPromotePhase(activeTab);
      setActiveTab('promote');
    } else {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab, isExteLet]);

  /* G182 — When the agent loads, default the active tab to the agent's
   * current phase rather than always 'build'. Without this, an agent that's
   * already in `decide` or `act` opens on the Build tab, which is no longer
   * the canonical workspace for that phase. Only fires on initial load /
   * when the agent.phase changes from undefined → known; manual tab clicks
   * are preserved (we don't override user navigation). */
  const phaseLandedRef = React.useRef(false);
  useEffect(() => {
    if (phaseLandedRef.current) return;
    /* ExteLet uses the Prove7 journey, not phase-named top-level tabs — the
     * B-D-A phase lives inside the Promote step (ExteletPromote opens on the
     * agent's current phase itself). Default to the journey entry (Register =
     * tabs[0], placed by the reset effect) and don't chase agent.phase. */
    if (isExteLet) { phaseLandedRef.current = true; return; }
    /* Native journey: B-D-A now live UNDER Promote. Land on the Promote step
     * opened at the agent's current working phase (Q5) so day-to-day work isn't
     * an extra click; the Identity/Intent/Access steps sit to its left. */
    const phase = (agent?.phase || '').toLowerCase();
    if (['build', 'decide', 'act'].includes(phase)) {
      setPromotePhase(phase);
      setActiveTab('promote');
      phaseLandedRef.current = true;
    }
  }, [agent?.phase, tabs, isExteLet]);

  /* R3.2 — Honor `?tab=<id>` so deep-links from the legacy
   * /gel-agents redirect (and from Discovery promote) land directly
   * on the requested tab. Fires once on mount; user clicks afterwards
   * are not overridden. The tab id must exist in the current tabs
   * list (which is archetype-gated) — if a non-ExteLet agent gets a
   * registration deep-link the parameter is silently ignored. */
  const tabParamLandedRef = React.useRef(false);
  useEffect(() => {
    if (tabParamLandedRef.current || !agent?.id) return;
    try {
      const tabParam = new URLSearchParams(window.location.search).get('tab');
      /* Legacy ?tab=build|decide|act now resolve to the Promote step for BOTH
       * ExteLets and native agents — Promote hosts the B-D-A sub-nav. For native
       * we also pin the sub-nav to the requested phase. */
      const legacyPhaseTab = ['build', 'decide', 'act'].includes(tabParam);
      if (legacyPhaseTab) {
        if (!isExteLet) setPromotePhase(tabParam);
        setActiveTab('promote');
        phaseLandedRef.current = true;
        tabParamLandedRef.current = true;
      } else if (tabParam && tabs.find((t) => t.id === tabParam)) {
        setActiveTab(tabParam);
        /* Also short-circuits the phase-landed default so we don't
         * race-overwrite it. */
        phaseLandedRef.current = true;
        tabParamLandedRef.current = true;
      } else if (tabParam) {
        /* Mark as resolved even if invalid so we don't keep re-checking
         * — the user can still navigate via the TabNav. */
        tabParamLandedRef.current = true;
      }
    } catch { /* malformed URL — safe to ignore */ }
  }, [agent?.id, tabs, isExteLet]);

  /* G198 / G380 — Honor `?intent=add-skill[&type=<skillType>]` so callers
   * (SkillCenterPageV2 Add Skill, etc.) can deep-link directly into the
   * skill-create slideout AND pre-select a type. Fires once after the agent
   * finishes loading. */
  const intentHandledRef = React.useRef(false);
  useEffect(() => {
    if (intentHandledRef.current || !agent?.id) return;
    const params = new URLSearchParams(window.location.search);
    const intent = params.get('intent');
    if (intent === 'add-skill') {
      /* Skills are authored on the Build sub-nav UNDER the Promote step, not a
       * top-level 'build' tab. setActiveTab('build') set an invalid top-level id
       * → the tabs-guard bounced to Identity behind the open modal. */
      setActiveTab('promote');
      setPromotePhase('build');
      const typeParam = params.get('type');
      const allowed = ['standard', 'workflow_skill', 'connector_skill', 'policy_skill'];
      setInitialSkillType(allowed.includes(typeParam) ? typeParam : null);
      setSkillModalOpen(true);
      intentHandledRef.current = true;
    }
  }, [agent?.id]);

  /* W4 (A2) — Hydrate the kill-switch state from the backend so the header
   * reflects reality after a reload (e.g. the agent was halted in another
   * session). The engage/release handlers keep it in sync thereafter. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const getKs = api['getKillSwitch'];
      if (!id || typeof getKs !== 'function') return;
      try {
        const res = await getKs(id);
        if (!cancelled && res && res.ok !== false && res.data) {
          setKillSwitchActive(!!res.data.engaged);
        }
      } catch { /* non-fatal — header falls back to default */ }
    })();
    return () => { cancelled = true; };
  }, [id]);

  /* G228 — Kill switch is safety-critical. Earlier code unconditionally
   * fired the "engaged" toast and flipped local killSwitchActive=true
   * even when the api.engageKillSwitch stub returned { ok:false, error:
   * '...not implemented yet' }. Operators saw a red ACTIVE banner while
   * the agent was actually still running — a serious governance lie.
   * We now inspect res.ok before claiming success, and label the failure
   * mode so the operator knows to fall back to platform-admin OOB. */
  const onKillConfirm = async (reason) => {
    try {
      const engage = api['engageKillSwitch'];
      if (typeof engage !== 'function') {
        addToast({
          tone: 'warning',
          title: 'Kill switch NOT engaged — endpoint missing',
          description: `${agent?.name || id} continues to run. api.engageKillSwitch is not wired in this build.`,
        });
        return;
      }
      const res = await engage(id, reason);
      if (res && res.ok === false) {
        const stubbed = /not implemented/i.test(res.error || res.data?.error || '');
        addToast({
          tone: stubbed ? 'warning' : 'danger',
          title: stubbed ? 'Kill switch NOT engaged — backend endpoint pending' : 'Kill switch FAILED',
          description: stubbed
            ? `${agent?.name || id} continues to run. Backend kill-switch endpoint not yet implemented; engage via platform-admin out-of-band procedures.`
            : (res.error || res.data?.error || `Backend rejected the request (HTTP ${res?.status || '?'}). Agent is still running.`),
        });
        return;
      }
      setKillSwitchActive(true);
      addToast({ tone: 'danger', title: 'Kill switch engaged', description: `${agent?.name || id} · ${reason}` });
    } catch (err) {
      addToast({ tone: 'danger', title: 'Kill switch FAILED — agent still running', description: err?.message || String(err) });
    }
  };

  const onKillDeactivate = async () => {
    try {
      const release = api['releaseKillSwitch'];
      if (typeof release !== 'function') {
        addToast({
          tone: 'warning',
          title: 'Release NOT issued — endpoint missing',
          description: `${agent?.name || id} kill switch state unchanged. api.releaseKillSwitch is not wired in this build.`,
        });
        return;
      }
      const res = await release(id);
      if (res && res.ok === false) {
        const stubbed = /not implemented/i.test(res.error || res.data?.error || '');
        addToast({
          tone: stubbed ? 'warning' : 'danger',
          title: stubbed ? 'Release NOT issued — backend endpoint pending' : 'Release FAILED',
          description: stubbed
            ? `Kill switch state unchanged. Backend release endpoint not yet implemented.`
            : (res.error || res.data?.error || `Backend rejected the request (HTTP ${res?.status || '?'}).`),
        });
        return;
      }
      setKillSwitchActive(false);
      addToast({ tone: 'success', title: 'Kill switch released', description: agent?.name || id });
    } catch (err) {
      addToast({ tone: 'danger', title: 'Release failed', description: err?.message || String(err) });
    }
  };

  /* G85 — Delete agent per GOLDEN_CEG_RULES §8.1.
   * Frontend obligations:
   *   1. Show a type-to-confirm modal that surfaces the impact
   *      (skill count, AutoLet status, runbook lineage).
   *   2. POST DELETE /api/v1/agents/{id}.
   *   3. If response is 409 has_children → display the children
   *      list + the "delete v2 first" hint inline (don't dismiss
   *      the modal so the user can read it).
   *   4. On success → navigate back to /agents and toast.
   * Backend enforces the actual cascade-to-CEG + audit-log rules.
   * Frontend never tries to bypass — that's the correct boundary. */
  const onDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const del = api['deleteAgent'];
      if (typeof del !== 'function') throw new Error('deleteAgent endpoint not exposed in this build.');
      const res = await del(id);
      if (res?.ok) {
        addToast({ tone: 'success', title: `${agent?.name || id} deleted`, description: 'A record of this deletion is preserved in the audit trail.' });
        setDeleteModalOpen(false);
        navigate('/agents');
        return;
      }
      /* Backend returns structured 409 has_children body when the
       * agent has child lineage rows (parent_agent_id chain). */
      const msg = res?.error || res?.message || '';
      if (/has_children|child|409/i.test(msg)) {
        setDeleteError(`This Agentlet has newer versions that must be deleted first. Open the latest version (v2 or higher), delete it, then come back here.`);
      } else {
        setDeleteError(msg || 'Backend rejected the delete.');
      }
    } catch (err) {
      setDeleteError(err?.message || String(err));
    } finally {
      setDeleting(false);
    }
  };

  /* Fix G59: lifted advance-phase handler so the BuildTab + DecideTab
   * subcomponents can call it via prop without needing id/session/
   * addToast/setActiveTab in their scope. */
  const onAdvancePhase = async (toPhase) => {
    /* DEF-EXTELET-V3-ADVANCE-ROUTE-001 — ExteLets are advanced via the
     * GEL lifecycle endpoints scoped to the registration row, NOT via
     * the native /agents/{id}/advance-phase endpoint. Native endpoint
     * explicitly throws "External agents must be advanced through the
     * GEL lifecycle API" so we must branch here.
     *
     * Coded + MCP ExteLets both use the same /lifecycle/advance-decide
     * and /lifecycle/advance-act routes — they're path-agnostic, just
     * keyed on the registration id. */
    const isExteletAgent = !!(agent?.isExternal || registration?.id);
    addToast({ tone: 'info', title: `Advancing to ${toPhase}…`, description: 'Recording phase advancement in the audit trail.' });
    try {
      let res;
      if (isExteletAgent && registration?.id) {
        const gelAdvance = toPhase === 'Decide' ? api.gelAdvanceToDecide : api.gelAdvanceToAct;
        if (typeof gelAdvance !== 'function') {
          addToast({ tone: 'warning', title: 'Advance unavailable', description: `api.${toPhase === 'Decide' ? 'gelAdvanceToDecide' : 'gelAdvanceToAct'} isn't wired in this build.` });
          return;
        }
        res = await gelAdvance(registration.id);
      } else {
        const adv = api['advancePhase'];
        if (typeof adv !== 'function') {
          addToast({ tone: 'warning', title: 'Advance unavailable', description: 'api.advancePhase isn\'t wired in this build.' });
          return;
        }
        res = await adv(id, session?.user?.id || session?.userId);
      }
      /* apiFetch returns { ok, data, error } on the GEL endpoints.
       * Native endpoint returns the agent body directly or { agent, gate }. */
      const errMsg = res?.error || res?.data?.error;
      /* G274 — The NATIVE advance-phase endpoint returns HTTP 200 for a REFUSED gate too
       * (body carries { gate } with status 'failed' and NO `agent`). apiFetch wraps every
       * 2xx as { ok: true }, so `res?.ok === true` was true on refusal and this reported
       * "Now in Decide" while the agent stayed in Build. Read the body via the single
       * shared reader. The GEL/ExteLet branch keeps its own shape — different endpoint,
       * different contract — so it is left exactly as it was. */
      const succeeded = isExteletAgent
        ? (res?.ok === true || res?.id || res?.phase || res?.data?.registrationStatus || res?.data?.id)
        : api.readAdvanceResult(res).advanced;
      if (succeeded && !errMsg) {
        addToast({ tone: 'success', title: `Now in ${toPhase}`, description: toPhase === 'Decide' ? 'Attach governance policies, then advance to Act.' : 'Assign users + publish to start governed execution.' });
        await reloadAgent();
        if (isExteletAgent && typeof reloadRegistration === 'function') {
          await reloadRegistration();
        }
        /* NAV RC: B-D-A are NOT top-level tabs — they are the `promotePhase`
         * sub-nav UNDER the Promote step (activeTab='promote'). Calling
         * setActiveTab('decide'|'act') set an invalid top-level tab id, so the
         * tabs-guard effect bounced the user to tabs[0] (Identity). Keep them on
         * the Promote step and move the sub-nav to the phase we just advanced to.
         * (ExteLets nest B-D-A inside ExteletPromote, which reads the agent's
         * phase itself — promotePhase is inert for them, so setting it is safe.) */
        setActiveTab('promote');
        if (!isExteletAgent) setPromotePhase(toPhase.toLowerCase());
      } else {
        /* Surface the gate's own failed checks verbatim — "Backend rejected the request"
         * told the operator nothing about WHICH check blocked them. */
        const failed = isExteletAgent ? [] : api.readAdvanceResult(res).failedChecks;
        const detail = errMsg
          || failed.map((c) => c.message || c.check).filter(Boolean).join('; ')
          || 'Backend rejected the request.';
        addToast({ tone: 'danger', title: 'Could not advance', description: detail });
      }
    } catch (err) {
      addToast({ tone: 'danger', title: 'Could not advance', description: err?.message || String(err) });
    }
  };

  if (loading && !agent) {
    return (
      <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1400px] mx-auto">
        <div className="space-y-3" aria-busy="true">
          <Skeleton variant="rect" height={80} />
          <Skeleton variant="rect" height={48} />
          <Skeleton variant="rect" height={300} />
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1400px] mx-auto">
        <Button variant="ghost" iconLeft={<ArrowLeft />} onClick={() => navigate('/agents')}>Back to inventory</Button>
        <div className="mt-6">
          <EmptyState
            title={error ? 'Failed to load Agentlet' : 'Agentlet not found'}
            description={error || `No Agentlet matches the id "${id}".`}
            primaryAction={<Button variant="primary" onClick={() => navigate('/agents')}>Back to inventory</Button>}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-v2-page text-v2-ink">
      {/* ─── Page header ─────────────────────────────────────────── */}
      <header className="bg-v2-surface border-b border-v2-border-subtle px-7 py-5">
        <div className="max-w-[1400px] mx-auto">
          <Button variant="ghost" size="sm" iconLeft={<ArrowLeft />} onClick={() => navigate('/agents')} className="mb-3">
            All Agentlets
          </Button>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-v2-md bg-v2-cyan-soft text-v2-cyan flex items-center justify-center font-bold text-[16px] flex-shrink-0">
              {(agent.name || 'A').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <Pill tone={isExteLet ? 'trust' : archetype === 'AutoLet' ? 'cyan' : archetype === 'SysLet' ? 'muted' : 'success'} size="sm">
                  {ARCHETYPE_LABEL[archetype] || archetype}
                </Pill>
                {(agent.category || agent.type) && <Pill tone="muted" size="sm">{agent.category || agent.type}</Pill>}
                {agent.phase && (
                  /* Fix G19: backend returns lowercase 'act'/'build'/'decide' — title-case for display */
                  <Pill tone="cyan" size="sm">{String(agent.phase).charAt(0).toUpperCase() + String(agent.phase).slice(1).toLowerCase()}</Pill>
                )}
                {agent.status && <Pill tone={agent.status === 'healthy' ? 'success' : agent.status === 'degraded' ? 'warning' : 'muted'} dot size="sm">{agent.status}</Pill>}
                {/* Readiness is DERIVED from executionReadyAt (single source of truth), never from a
                    stored status string that can drift. For an ExteLet the header shows the live
                    admissibility so it can't claim "ready" while governed traffic is fail-closed. */}
                {isExteLet && (
                  <Pill tone={agent.executionReadyAt ? 'success' : 'muted'} dot size="sm">
                    {agent.executionReadyAt ? 'Execution Ready' : 'Not Execution Ready'}
                  </Pill>
                )}
              </div>
              <h1 className="text-[24px] font-bold tracking-tight m-0 text-v2-ink">{agent.name || agent.displayName || agent.id}</h1>
              {/* P7J-RUN-016 reverted — the agent UUID is intentionally
                * exposed by ExteletCredentialsCard (snippet copy + per-row
                * fields) as part of the registration/governance setup flow.
                * A duplicate header chip on every Let detail was redundant. */}
              {agent.description && (
                <p className="text-[13px] text-v2-ink-secondary leading-relaxed mt-1 max-w-2xl">{agent.description}</p>
              )}
              <div className="text-[11.5px] text-v2-ink-muted mt-1 flex items-center gap-3 flex-wrap">
                {agent.agentVersion && <span>v{agent.agentVersion}</span>}
                {agent.skillCount != null && <span>· {agent.skillCount} skill{agent.skillCount === 1 ? '' : 's'}</span>}
                {agent.gatewayUrl && <span>· <code className="font-mono-v2">{String(agent.gatewayUrl).replace(/^https?:\/\//, '')}</code></span>}
                {/* G85 — lineage chain. Per GOLDEN_CEG_RULES §3 every
                  * AutoLet has a versioning chain: parent_agent_id →
                  * runbook_document_id → runbook_version. Surface the
                  * fragments we have so the operator can correlate
                  * against the audit trail. */}
                {agent.runbookVersion != null && (
                  <span>· <strong className="text-v2-ink">source v{agent.runbookVersion}</strong></span>
                )}
                {/* RC-25 / R1 — Lineage chips render via EntityLabel for a
                    consistent "{name} ({shortid})" composition. The names
                    are populated by AgentService.decorateLineageNames at
                    read time (transient fields on Agent.java); FE falls
                    back gracefully to "agent/runbook/workflow (shortid)"
                    when a name fails to resolve (deleted parent, missing FK). */}
                {agent.parentAgentId && (
                  <span>· parent <EntityLabel
                    kind="agent"
                    id={agent.parentAgentId}
                    name={agent.parentAgentName}
                    linkTo={`/agents/${agent.parentAgentId}`}
                    size="sm"
                  /></span>
                )}
                {agent.runbookDocumentId && (
                  <span>· runbook doc <EntityLabel
                    kind="runbook"
                    id={agent.runbookDocumentId}
                    name={agent.runbookDocumentName}
                    size="sm"
                    tone="muted"
                  /></span>
                )}
                {agent.sourceWorkflowId && (
                  <span>· source workflow <EntityLabel
                    kind="workflow"
                    id={agent.sourceWorkflowId}
                    name={agent.sourceWorkflowName}
                    linkTo={`/canvas?workflowId=${agent.sourceWorkflowId}`}
                    size="sm"
                  />{agent.sourceWorkflowVersion ? ` v${agent.sourceWorkflowVersion}` : ''}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* The canonical Trust Score is the freshly-computed complianceScore
                  (ComplianceScoreService writes agents.compliance_score on every
                  recalc). agents.trust_score is the eval-ramp's FIXED baseline
                  (external 50 / native 75), not a live value — reading it first
                  showed a stale 50 here while the Trust Center showed the real
                  score. Prefer complianceScore so every surface agrees. */}
              {/* Canonical: complianceScore (null = Unrated). NO fallback to the
                  trustScore baseline / 0 — that fabricated a posture. TrustChip renders
                  "Unrated" for null. See docs/design/TRUST_SCORE_SINGLE_SOURCE_RC.md. */}
              <TrustChip score={agent.complianceScore} autonomy={agent.autonomy || agent.autonomyLevel} />
              <KillSwitchButton
                agentName={agent.name || agent.id}
                isActive={killSwitchActive}
                onConfirm={onKillConfirm}
                onDeactivate={onKillDeactivate}
              />
              {/* G86 — Open in Canvas. The workflow graph is bound 1:1
                * to the Agentlet; deep-link the user straight to the
                * editor so they can see/inspect the full graph. The
                * Canvas page reads ?agentId= and locates the matching
                * workflow. Always visible regardless of archetype —
                * for AutoLets the graph is read-only, for Lets it
                * opens in edit mode (the Canvas page enforces this). */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/canvas?agentId=${id}`)}
                iconLeft={<ExternalLink size={13} />}
                title="Open this Agentlet's workflow graph in the Canvas editor"
              >
                Open in Canvas
              </Button>
              {/* Phase 9 — Promote this Agentlet to another Instance.
               * Gated by promotion.initiate AND a known source instance
               * (agents pinned to ORG/TENANT precision can't be promoted
               * because the Promotion Graph is between Instances). The
               * slideout itself loads the tenant's enabled outbound
               * PromotionPaths and shows a clear empty state when none
               * are declared, so showing the button without a path
               * declared is acceptable — the slideout walks the operator
               * through. */}
              {hasPermission && hasPermission('promotion.initiate') && agent?.instanceId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPromoteOpen(true)}
                  iconLeft={<Send size={13} />}
                  title="Promote this Agentlet to another instance (Dev → Staging → Prod, etc.)"
                >
                  Promote
                </Button>
              )}
              {/* G177/G209 — Test Run side panel parity with V1's TestRunSidePanel.
               * Per platform policy, Test Run is only available once the agent
               * is in Act phase — running it earlier would invoke skills that
               * haven't yet cleared the Decide-phase governance gate. Disabled
               * with an explanatory tooltip in Build/Decide. */}
              {(() => {
                const inAct = (agent?.phase || '').toLowerCase() === 'act';
                return (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setTestRunOpen(true)}
                    iconLeft={<FlaskConical size={13} />}
                    disabled={!inAct}
                    title={inAct
                      ? "Open Test Run side panel — chat with this agent with policy enforcement live"
                      : `Test Run is gated to the Act phase. Advance through Build → Decide → Act (current phase: ${(agent?.phase || 'build').toLowerCase()}) to enable it.`}
                  >
                    Test Run
                  </Button>
                );
              })()}
              {/* G85 — Delete agent button. Hidden for SysLets (per
                * §1; system agents aren't deletable from the UI) but
                * present for every other archetype. Modal carries
                * the type-to-confirm + impact summary; backend
                * returns 409 has_children if a v2/v3 lineage exists.
                *
                * DEF-FE-DELETE-GATE-001 — additionally gate by
                * hasPermission('agent.delete') so roles without delete
                * authority (Developer / Operator / Viewer / Approver /
                * Compliance / Auditor) don't see a button that would
                * only 403 at submit-time. Backend AgentService.deleteAgent
                * + RbacService.requireRowScope remain the authoritative
                * gates (defense-in-depth). Sibling fix to DEF-FE-CUSTOMER-GATE-001. */}
              {archetype !== 'SysLet' && hasPermission && hasPermission('agent.delete') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setDeleteConfirmText(''); setDeleteError(null); setDeleteModalOpen(true); }}
                  className="!text-v2-danger hover:!bg-v2-danger-soft"
                  title="Delete this Agentlet (a record is preserved in the audit trail)"
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ─── Audit hash chain integrity (P0-12, agent-scoped) ───────
          DV-2 Phase B audit log is hash-chained per (tenant_id, agent_id).
          Surfacing the verification + certificate download as an
          always-visible band gives auditors and security engineers
          one-click proof that THIS Agentlet's audit trail is intact
          before they look at any of its skills or executions. Scoped
          via agentId so the backend re-walks only this agent's chain
          (not the full tenant log). */}
      <div className="bg-v2-surface border-b border-v2-border-subtle px-7 py-2.5">
        <div className="max-w-[1400px] mx-auto">
          <AuditHashIntegrity
            compact
            autoVerify
            verifyFn={api.verifyAuditIntegrity}
            downloadFn={api.downloadAuditCertificate}
            agentId={id}
            timeRange="30d"
          />
        </div>
      </div>

      {/* ─── SPIFFE identity panel (P0-3 Sprint 1) ────────────────
          Surfaces the agent's canonical SPIFFE ID + AI System SVID
          extension claims, plus a one-click SPIRE registration entry
          download. Auto-shows a "preview / configure trust domain to
          enable" banner when agentlets.spiffe.trust-domain is at the
          default — see SpiffeIdentityPanel's render-mode logic.
          Placed under the audit bar so the zero-trust identity layer
          is visible from the moment the operator opens the agent.
          UX-1: now collapsible (default collapsed) so the Build/Decide/
          Act tabs are visible above-the-fold. State is persisted per
          agent via localStorage so power users don't fight the toggle
          on every nav. */}
      {/* J1c — SPIFFE identity is now the dedicated Identity journey step for
          native agents too (rendered in the tab content below), no longer an
          above-nav collapsible. (ExteLets already used the Identity step.) */}

      {/* ─── Intent profiles editor ───────────────────────────────
          Operator-curated list of business intents this agent is
          authorised to declare. When the list is empty the agent
          can call any of its skills without an intent declaration;
          once one or more profiles are configured, every call must
          declare its intent and request a skill allowed under that
          intent. Placed under the SPIFFE panel because intent
          authorization is the policy layer that consumes the
          SPIFFE-verified caller identity.
          DEF-FE-COPY-INTERNAL-JARGON-LEAK-001 — plain-English copy
          on the rightHint.
          Phase 5 — the legacy IntentProfilesEditor (inline JSONB
          authoring) was deleted together with the column it edited.
          The Intent Bindings panel below is now the sole intent-
          configuration surface; runtime authorization is sourced
          exclusively from the catalog. */}

      {/* Intent Profiles Catalog v1.0 — Phase 1E.3 / Phase 5.
       *
       * Per-agent bindings panel — sole intent-configuration surface
       * post-Phase-5. Authors catalog bindings; the runtime path
       * (IntentAuthorizationService.readProfilesFromBindings) reads
       * exclusively from these bindings.
       *
       * Permission gating is per-action inside the panel — read for
       * everyone with agent.read; write actions (bind/unbind/narrow/rebind)
       * gated by agent.write OR compliance.admin OR policy.admin to match
       * the backend IntentBindingController exactly. */}
      {/* J1c — Intent bindings is now the dedicated Intent journey step for
          native agents too (rendered in the tab content below). */}

      {/* ─── Tab nav ─────────────────────────────────────────────── */}
      <div className="bg-v2-surface px-7">
        <div className="max-w-[1400px] mx-auto">
          <TabNav tabs={tabs} activeId={activeTab} onChange={setActiveTab} ariaLabel={`${agent?.name || 'Agentlet'} phases`} />
        </div>
      </div>

      {/* ─── Tab content ─────────────────────────────────────────── */}
      <div className="px-7 py-6">
        <div className="max-w-[1400px] mx-auto">
          {activeTab === 'registration' && (
            <RegistrationTab agent={agent} section="registration"
              registration={registration} setRegistration={setRegistration}
              regLoading={regLoading} reloadRegistration={reloadRegistration}
              discoveryRunning={discoveryRunning} discoveredSkills={discoveredSkills}
              setDiscoveredSkills={setDiscoveredSkills} handleDiscoverInline={handleDiscoverInline} />
          )}
          {/* Native Promote step — hosts the Build / Decide / Act sub-nav. The
              three native phase surfaces below render UNCHANGED; only their gate
              moved from a top-level `?tab` to `activeTab==='promote' &&
              promotePhase===<phase>`. (ExteLets nest B-D-A in ExteletPromote.) */}
          {!isExteLet && activeTab === 'promote' && (
            <div className="mb-4">
              <div className="inline-flex bg-v2-muted rounded-v2-md p-1 gap-1">
                {['build', 'decide', 'act'].map((ph) => (
                  <button
                    key={ph}
                    type="button"
                    onClick={() => setPromotePhase(ph)}
                    className={`text-[12.5px] font-semibold px-3.5 py-1.5 rounded-v2-sm capitalize transition-colors ${
                      promotePhase === ph
                        ? 'bg-v2-surface text-v2-ink shadow-sm'
                        : 'text-v2-ink-muted hover:text-v2-ink'
                    }`}>
                    {ph}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!isExteLet && activeTab === 'promote' && promotePhase === 'build' && (
            <BuildTab
              agent={agent}
              /* P7J-RUN-037 — pass the lifted registration row + discover
               * state so BuildTab's Skills card can gate AND fire the
               * "Discover capabilities" button without any cross-component
               * closure references. handleDiscoverInline + discoveryRunning
               * are owned by AgentDetailPageV2Inner; this tab and
               * RegistrationTab both consume the same instances. */
              registration={registration}
              discoveryRunning={discoveryRunning}
              handleDiscoverInline={handleDiscoverInline}
              onAddSkill={(type) => {
                /* G380 — Pass the picked type so the slideout can skip its
                 * own L1 picker. Falls back to null (show picker) when the
                 * caller doesn't pre-pick (EmptyState primary action). */
                setInitialSkillType(typeof type === 'string' ? type : null);
                setSkillModalOpen(true);
              }}
              onAdvanceToDecide={() => onAdvancePhase('Decide')}
              archetype={archetype}
              reloadAgentFromParent={reloadAgent}
              /* R2.b / V3.5 — Remediation jump. Originally always switched
               * to Registration tab; now decides per-cardId because the
               * three build-phase cards (skill-mappings, governance,
               * integration-tests) moved from Registration → Build under
               * V3.5 Step 1. Only `registration-form` still routes to
               * Registration; the rest stay on Build (already active here)
               * and just scroll. Card-id strings match `data-card-id`
               * markers placed on the cards. */
              onJumpToRegistrationCard={(cardId) => {
                const BUILD_SECTION_CARDS = new Set([
                  'skill-mappings',
                  'governance',
                  'integration-tests',
                ]);
                if (!BUILD_SECTION_CARDS.has(cardId)) {
                  setActiveTab('registration');
                }
                /* Wait a tick so the tab content mounts, then scroll. */
                setTimeout(() => {
                  const el = document.querySelector(`[data-card-id="${cardId}"]`);
                  if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    /* Brief flash so the operator knows where they
                     * landed — the page is dense, a silent scroll is
                     * easy to miss. */
                    el.classList.add('ring-2', 'ring-v2-action', 'rounded-v2-md');
                    setTimeout(() => {
                      el.classList.remove('ring-2', 'ring-v2-action', 'rounded-v2-md');
                    }, 1600);
                  }
                }, 80);
              }}
            />
          )}
          {!isExteLet && activeTab === 'promote' && promotePhase === 'decide' && (
            <DecideTab
              agent={agent}
              onAdvanceToAct={() => onAdvancePhase('Act')}
              reloadAgentFromParent={reloadAgent}
            />
          )}
          {!isExteLet && activeTab === 'promote' && promotePhase === 'act' && (
            <ActTab agent={agent} killSwitchActive={killSwitchActive} reloadAgentFromParent={reloadAgent} />
          )}

          {/* ── ExteLet Prove7 journey steps (J1b) ──────────────────────
              Identity / Intent / RBAC are now first-class steps (relocated out
              of the Registration tab, the above-nav collapsibles, and the Act
              tab respectively). Promote hosts the B-D-A gate; Enforce + Audit
              link to the External Trust Center / Audit scoped to this agent. */}
          {activeTab === 'identity' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-[14px] font-bold tracking-tight m-0 text-v2-ink">Identity</h3>
                <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                  Cryptographic identity for this agent — its SPIFFE/SVID name and the
                  Gateway / SDK / MCP credentials it authenticates with.
                </p>
              </div>
              <SpiffeIdentityPanel agentId={id} />
              {registration && registration.id && (
                <ExteletCredentialsCard
                  agent={agent}
                  registration={registration}
                  onRegistrationRefresh={reloadRegistration}
                />
              )}
            </div>
          )}
          {activeTab === 'intent' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-[14px] font-bold tracking-tight m-0 text-v2-ink">Intent</h3>
                <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                  Bind this agent to APPROVED Intent template versions. Runtime authorization
                  is sourced exclusively from these bindings.
                </p>
              </div>
              <IntentBindingsPanel agentId={id} agentPhase={agent?.phase} />
            </div>
          )}
          {activeTab === 'rbac' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-[14px] font-bold tracking-tight m-0 text-v2-ink">Access</h3>
                <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                  Grant users or groups access to this agent. At least one grant is required
                  before the Promote gate's “assignment present” check passes.
                </p>
              </div>
              <AccessPanel agentId={id} />
            </div>
          )}
          {isExteLet && activeTab === 'promote' && (
            <ExteletPromote
              agent={agent}
              registration={registration}
              reloadRegistration={reloadRegistration}
              reloadAgent={reloadAgent}
              discoveryRunning={discoveryRunning}
              discoveredSkills={discoveredSkills}
              setDiscoveredSkills={setDiscoveredSkills}
              handleDiscoverInline={handleDiscoverInline}
              onAdvancePhase={onAdvancePhase}
            />
          )}
          {isExteLet && activeTab === 'enforce' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-[14px] font-bold tracking-tight m-0 text-v2-ink">Enforce</h3>
                <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                  Live runtime enforcement of this agent's intent + control-policy bindings, plus
                  its Trust Ontology (the seven-prove radial). Every protected call passes the gate.
                </p>
              </div>
              <Card>
                <Card.Body>
                  <div className="flex items-center gap-3">
                    <Zap size={18} className="text-v2-cyan" aria-hidden="true" />
                    <div className="flex-1">
                      <p className="text-[13px] font-semibold m-0 text-v2-ink">Live Trust &amp; Trust Ontology</p>
                      <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                        Open the External Trust Center scoped to this agent to watch live enforcement
                        and the seven-prove ontology.
                      </p>
                    </div>
                    <Button variant="secondary" size="sm"
                      onClick={() => navigate(`/external-trust?agentId=${id}&tab=live`)}>
                      Open Trust Center
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
          {isExteLet && activeTab === 'audit' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-[14px] font-bold tracking-tight m-0 text-v2-ink">Audit</h3>
                <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                  Hash-chained event log for this agent — every lifecycle, enforcement, and review
                  action sealed and replayable.
                </p>
              </div>
              <AuditHashIntegrity
                autoVerify
                verifyFn={api.verifyAuditIntegrity}
                downloadFn={api.downloadAuditCertificate}
                agentId={id}
                timeRange="30d"
              />
              <Card>
                <Card.Body>
                  <div className="flex items-center gap-3">
                    <ClipboardCheck size={18} className="text-v2-cyan" aria-hidden="true" />
                    <div className="flex-1">
                      <p className="text-[13px] font-semibold m-0 text-v2-ink">Full audit trail</p>
                      <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                        Open this agent's protected event history — every enforcement decision and
                        tool call, sealed and replayable.
                      </p>
                    </div>
                    <Button variant="secondary" size="sm"
                      onClick={() => navigate(`/workflow-audit?agentId=${id}`)}>
                      Open Audit trail
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}

          {/* Native Enforce step (Q3) — deep-links to this agent's Trust Ontology
              (the seven-prove radial), the native equivalent of the ExteLet
              Enforce step's external-trust link. */}
          {!isExteLet && activeTab === 'enforce' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-[14px] font-bold tracking-tight m-0 text-v2-ink">Enforce</h3>
                <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                  Live runtime enforcement of this agent's intent + control-policy bindings, plus its
                  Trust Ontology (the seven-prove radial). Every protected call passes the gate.
                </p>
              </div>
              <Card>
                <Card.Body>
                  <div className="flex items-center gap-3">
                    <Zap size={18} className="text-v2-cyan" aria-hidden="true" />
                    <div className="flex-1">
                      <p className="text-[13px] font-semibold m-0 text-v2-ink">Trust Ontology</p>
                      <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                        Open this agent's seven-prove Trust Ontology to watch live enforcement and replay
                        sealed proves.
                      </p>
                    </div>
                    <Button variant="secondary" size="sm"
                      onClick={() => navigate(`/trust?tab=ontology&agentId=${id}`)}>
                      Open Trust Ontology
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}

          {/* Native Audit step — same component as the always-on band, now a
              dedicated journey step + link to the native Audit trail. */}
          {!isExteLet && activeTab === 'audit' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-[14px] font-bold tracking-tight m-0 text-v2-ink">Audit</h3>
                <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                  Hash-chained event log for this agent — every lifecycle, enforcement, and review action
                  sealed and replayable.
                </p>
              </div>
              <AuditHashIntegrity
                autoVerify
                verifyFn={api.verifyAuditIntegrity}
                downloadFn={api.downloadAuditCertificate}
                agentId={id}
                timeRange="30d"
              />
              <Card>
                <Card.Body>
                  <div className="flex items-center gap-3">
                    <ClipboardCheck size={18} className="text-v2-cyan" aria-hidden="true" />
                    <div className="flex-1">
                      <p className="text-[13px] font-semibold m-0 text-v2-ink">Full audit trail</p>
                      <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                        Open the Audit trail for the complete, replayable event history —
                        every enforcement decision and tool call, sealed and replayable.
                      </p>
                    </div>
                    <Button variant="secondary" size="sm"
                      onClick={() => navigate(`/workflow-audit?agentId=${id}`)}>
                      Open Audit trail
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* G380 — V2 +New Skill: replaced two-step Modal with full SkillCreateSlideout.
          The slideout hosts the entire wizard (L1 type pick → L2 type-specific
          config → L3 universal metadata → L4 inline review) in the right rail.
          Backend endpoints are unchanged from V1 (addSkill, getWorkflows,
          getAssignedConnectorIds, linkSkillConnector). The Workflow skill type
          now shows a dropdown of agent-linked workflows instead of navigating
          to /canvas and abandoning the wizard. */}
      <SkillCreateSlideout
        isOpen={skillModalOpen}
        onClose={() => { setSkillModalOpen(false); setInitialSkillType(null); }}
        agentId={id}
        agentName={agent?.name}
        initialType={initialSkillType}
        addToast={addToast}
        onCreated={async () => {
          await reloadAgent();
        }}
      />

      {/* G85 — Delete agent confirmation modal.
        * Per GOLDEN_CEG_RULES §8.1: delete-with-children is refused
        * by the backend; user must walk lineage in reverse (v3 → v2
        * → v1). Each deletion preserves an immortal audit row.
        * Type-to-confirm guards against muscle-memory clicks. */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => { if (!deleting) setDeleteModalOpen(false); }}
        title="Delete agent"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>Cancel</Button>
            <Button
              variant="danger"
              onClick={onDelete}
              disabled={deleting || deleteConfirmText.trim() !== (agent?.name || '').trim() || !(agent?.name)}
            >
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-[13px] text-v2-ink-secondary leading-relaxed">
          <div className="px-3 py-3 rounded-v2-md border border-v2-danger bg-v2-danger-soft">
            <strong className="text-v2-danger text-[13px] block mb-1">This action cannot be undone.</strong>
            Deleting <strong className="text-v2-ink">{agent?.name || id}</strong> will permanently remove:
            <ul className="mt-2 ml-4 list-disc space-y-0.5 text-[12px]">
              <li>The Agentlet and its {agent?.skillCount ?? (agent?.skills?.length || 0)} skill{(agent?.skillCount || agent?.skills?.length || 0) === 1 ? '' : 's'}</li>
              {(archetype === 'AutoLet' || (typeof agent?.name === 'string' && agent.name.startsWith('AutoLets-'))) && (
                <li>The Agentlet's workflow graph (the AutoLet and its workflow are deleted together)</li>
              )}
              <li>All eval suites and run history for this Agentlet</li>
              <li>All Test sandbox workflows for this Agentlet</li>
            </ul>
            <div className="mt-2 text-[11.5px] text-v2-ink-secondary">
              A record of this deletion is preserved in the audit trail. Your runbook document is <strong>not</strong> deleted — it stays in your org's runbook library.
            </div>
          </div>

          {agent?.parentAgentId && (
            <div className="text-[12px] text-v2-ink-secondary">
              <strong className="text-v2-ink">Version chain:</strong> this Agentlet is a newer version of <button type="button" className="text-v2-action hover:underline" onClick={() => navigate(`/agents/${agent.parentAgentId}`)}>an earlier Agentlet</button>. Deleting this version leaves the older one untouched.
            </div>
          )}

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Type the agent name to confirm</label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={agent?.name || ''}
              autoComplete="off"
              className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink font-mono-v2 text-[12px]"
              disabled={deleting}
            />
          </div>

          {deleteError && (
            <div role="alert" className="px-3 py-2 rounded-v2-sm border border-v2-danger bg-v2-danger-soft text-v2-danger text-[12px]">
              <strong>Delete failed:</strong> {deleteError}
            </div>
          )}
        </div>
      </Modal>

      {/* G177 — Test Run side panel (V1 parity). */}
      <TestRunSidePanelV2
        isOpen={testRunOpen}
        agent={agent}
        onClose={() => setTestRunOpen(false)}
        onTestPassed={() => {
          // Refresh the agent so the publish gate re-reads the just-recorded run.
          // The backend sets lastTestRunPassed/At correctly, but ActTab's gate is
          // a cached validatePublish snapshot — it must re-fetch to reflect it.
          // Mirrors AgentsPage's loadAgents() and every other Act action's
          // reloadAgent(); the V2 port had stubbed this to a no-op (the regression
          // where "Successful Test Run recorded" never cleared after a run).
          reloadAgent();
        }}
      />
      {/* Phase 9 — Promote Agentlet slideout. Loads enabled outbound
       * PromotionPaths from the agent's source instance, lets the
       * operator pick target + conflict-resolution + notes, submits
       * to POST /api/v1/promotions. On approval-required targets the
       * row queues into the Approval Center; on auto-execute targets
       * the clone materializes immediately. */}
      <PromoteSlideout
        isOpen={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        resourceType="AGENT"
        resourceId={agent?.id}
        resourceName={agent?.name}
        sourceInstanceId={agent?.instanceId}
        sourceInstanceName={agent?.instanceName}
        sourceTenantId={agent?.tenantId}
        onSubmitted={() => { /* PromoteSlideout handles its own toast; the agent row itself doesn't change. */ }}
      />
    </div>
  );
}

/* DEF-GEL-FE-NONARRAY-001 / R3.3.b — pickArrayExt picks the first
 * Array-typed argument and falls back to []. Defensive against the
 * Hibernate JSONB serializer emitting `{}` instead of `[]` for empty
 * lists; the previous `(x || []).map(...)` pattern crashed with
 * "x.map is not a function" the moment the slideout loaded a row
 * whose JSONB column was {}. Mirrors the same helper in
 * GelExternalAgentsPageV2.jsx. */
function pickArrayExt(...candidates) {
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

/* ── Registration tab (ExteLet only) ───────────────────────────
 *
 * EXTELET-V3.5 Step 1 — the same component now powers two surfaces.
 *   section='registration' → external_agent_registration row + Edit form,
 *     Invocation metrics, ExteletCredentialsCard (Gateway/SDK/MCP keys
 *     + Java/Python/MCP snippets), Verify/Suspend/Revoke action triad.
 *   section='build'        → ONLY the three ExteLet-specific build-phase
 *     cards (Skill mappings / Governance policies / Integration tests).
 *
 * Rationale: for ExteLets, functional capabilities are owned by the
 * external Agent Framework. Agentlets does NOT control them. The only
 * skills worth managing here are *policy skills* (governance bindings on
 * agent runs). Those + integration tests belong in Build, not Registration.
 * Co-locating them inside RegistrationTab (rather than duplicating the
 * 1100+ lines of state hooks into BuildTab) keeps all the existing data
 * fetches, error handling, and modal wiring intact. Build just mounts the
 * same component with a different `section` flag. */
function RegistrationTab({
  agent,
  section = 'registration',
  /* P7J-RUN-037 — registration state is now lifted to the parent
   * AgentDetailPageV2Inner so both this tab and the parent's
   * onAdvancePhase / Skills card "Discover capabilities" gate read
   * from the same source. Props are passed in by both callsites. */
  registration,
  setRegistration,
  regLoading,
  reloadRegistration,
  /* P7J-RUN-037 Phase 3b — discover state + handler also lifted
   * to the parent so BuildTab's Skills-card "Discover capabilities"
   * button and this tab's results card share one canonical list. */
  discoveryRunning,
  discoveredSkills,
  setDiscoveredSkills,
  handleDiscoverInline,
}) {
  /* Fix G50: backend currently silently drops `endpointUrl`, `apiKey`,
   * `vendorName` etc. on agent creation (the Spring Boot validator
   * doesn't recognize those columns yet). Surface that gap explicitly
   * here instead of just rendering "—" — the user otherwise wonders
   * if the form even submitted.
   *
   * G197 — Add Edit modal so operators can fill these in post-creation
   * without dropping into V1. Calls api.updateAgent (PUT /api/v1/agents/{id}).
   *
   * R3.3.a — Enriched with the slideout-only affordances that previously
   * lived on /gel-agents (Verify probe, Suspend, Revoke, Agent UUID copy,
   * Invocation metrics). The data source is the external_agent_registration
   * row (fetched via gelGetRegistrationByAgent), which carries the
   * registration-side fields (status, totalInvocations, lastVerifiedAt,
   * skillMappings, etc.). The agent row stays the source of truth for
   * registration meta-fields (endpointUrl, apiKey, vendorName). */
  const toastCtx = useToast();
  const addToast = React.useCallback((arg, type, duration) => {
    if (toastCtx && typeof toastCtx.addToast === 'function') return toastCtx.addToast(arg, type, duration);
    return null;
  }, [toastCtx]);
  const hasRegistrationData = !!(agent.endpointUrl || agent.gatewayUrl || agent.apiKey || agent.vendorName);

  /* V277 — path-aware registration card. Both V3 integration paths are INBOUND
   * (the agent authenticates INTO the Agentlets gateway with a minted key),
   * so neither records an outbound endpoint URL / API key. A connector binding
   * (platformConnectorId) is the same inbound story even for legacy rows where
   * integration_path is NULL. Only a true direct call-out registration (no
   * path, no connector) needs an endpoint + key — that's the only case where
   * the "details not on file" warning is honest. */
  const integrationPath = registration?.integrationPath || null; // 'CODED' | 'MCP' | null
  const isConnectorBound = !!(registration && registration.platformConnectorId);
  const isInboundExteLet = integrationPath === 'MCP' || integrationPath === 'CODED' || isConnectorBound;
  const integrationLabel =
    integrationPath === 'MCP'   ? 'MCP Gateway (off-the-shelf)' :
    integrationPath === 'CODED' ? 'Coded — embedded SDK (@governed)' :
    isConnectorBound            ? 'Connector-bound (inbound gateway)' :
    'Direct call-out';
  const inboundKeyLabel = integrationPath === 'CODED' ? 'SDK key' : 'gateway key';

  /* P7J-RUN-037 — registration / setRegistration / regLoading /
   * reloadRegistration are now props (lifted to AgentDetailPageV2Inner).
   * The fetch + mount-time useEffect live in the parent so the parent's
   * outer-scope references (onAdvancePhase + Discover-capabilities gate)
   * see the same data without a duplicate fetch or ReferenceError. */

  /* R3.3.a — Verify / Suspend / Revoke action handlers, lifted from the
   * GelExternalAgentsPageV2 slideout. They operate on registration.id
   * (NOT agent.id); the agent ↔ registration mapping is owned by
   * gelGetRegistrationByAgent above. */
  const [actBusy, setActBusy] = useState(null); // 'verify' | 'suspend' | 'revoke' | 'acknowledge' | null
  /* R2.3.a/b — Last probe outcome, surfaced under the Verify button so the
   * operator sees the HTTP status code + latency + body snippet without
   * leaving the page. Cleared on every reload. */
  const [lastProbe, setLastProbe] = useState(null);

  const handleVerify = async () => {
    if (!registration?.id) return;
    setActBusy('verify');
    setLastProbe(null);
    try {
      const res = await api.gelProbeRegistration?.(registration.id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Probe failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      const outcome = res?.data || res || {};
      setLastProbe(outcome);
      if (outcome.verified) {
        addToast({ tone: 'success', title: 'Probe succeeded',
          description: `HTTP ${outcome.statusCode} in ${outcome.latencyMs}ms — registration marked VERIFIED.` });
      } else {
        addToast({ tone: 'warning', title: 'Probe did NOT verify',
          description: outcome.statusCode
            ? `Endpoint returned HTTP ${outcome.statusCode}. Status unchanged.`
            : (outcome.snippet || 'Probe transport error. Status unchanged.') });
      }
      await reloadRegistration();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Verify failed', description: err?.message || String(err) });
    } finally { setActBusy(null); }
  };

  /* R2.3.b — Acknowledge fallback. For code-discovered ExteLets the
   * registration is on file but there's no reachable endpoint to probe
   * (e.g. a langchain skeleton found in a GitHub repo). Acknowledge
   * hits the legacy PUT /verify endpoint which stamps VERIFIED without
   * a network round-trip. Audit trail still captures who acknowledged. */
  const handleAcknowledge = async () => {
    if (!registration?.id) return;
    setActBusy('acknowledge');
    try {
      const res = await api.gelVerifyRegistration?.(registration.id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Acknowledge failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'info', title: 'Registration acknowledged',
        description: 'Marked VERIFIED without a live probe. Audit trail recorded.' });
      setLastProbe(null);
      await reloadRegistration();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Acknowledge failed', description: err?.message || String(err) });
    } finally { setActBusy(null); }
  };

  /* R2.3.b — Distinguish ExteLets that can be probed live vs ones that
   * are code-discovered (no reachable endpoint). Code-discovered rows
   * surface the Acknowledge action instead of probe. The agent record
   * carries discoverySource via the inventory row; the registration
   * also exposes its source. */
  const isCodeDiscovered = (() => {
    const src = String(agent.discoverySource || registration?.discoverySource || '').toUpperCase();
    return src === 'CODE_REPO' || src === 'CODE_REPOSITORY';
  })();

  /* Suspend requires an operator-typed reason (audit-trail requirement).
   * Modal stays open while submitting and closes on success. */
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const confirmSuspend = async () => {
    if (!registration?.id || !suspendReason.trim()) return;
    setActBusy('suspend');
    try {
      const res = await api.gelSuspendRegistration?.(registration.id, suspendReason.trim());
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Suspend failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'info', title: 'Registration suspended' });
      setSuspendOpen(false);
      setSuspendReason('');
      await reloadRegistration();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Suspend failed', description: err?.message || String(err) });
    } finally { setActBusy(null); }
  };

  /* Revoke is irreversible — type-to-confirm guards muscle-memory clicks.
   * Mirrors the G85 delete-agent pattern. */
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeConfirmText, setRevokeConfirmText] = useState('');
  const confirmRevoke = async () => {
    if (!registration?.id || revokeConfirmText.trim().toUpperCase() !== 'REVOKE') return;
    setActBusy('revoke');
    try {
      const res = await api.gelRevokeRegistration?.(registration.id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Revoke failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'danger', title: 'Registration revoked',
        description: 'Audit trail preserved. This Agentlet can no longer execute.' });
      setRevokeOpen(false);
      setRevokeConfirmText('');
      await reloadRegistration();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Revoke failed', description: err?.message || String(err) });
    } finally { setActBusy(null); }
  };

  /* R3.3.b — Skill mappings state. The slideout's "Skills" tab content
   * (capability discovery + manual add + per-skill remove) is lifted
   * verbatim from GelExternalAgentsPageV2.jsx:998-1098 with the only
   * difference being that we already have `registration` from the
   * R3.3.a fetch; we don't need a `selected` shim. All persistence
   * calls hit gelUpdateSkillMappings(registration.id, ...). */
  /* P7J-RUN-037 Phase 3b — discoveryRunning / discoveredSkills /
   * setDiscoveredSkills / handleDiscoverInline are now props (lifted
   * to AgentDetailPageV2Inner so BuildTab's Skills-card button and
   * this tab's results card share one canonical list). Only the
   * manual-add form state stays local to this tab — that affordance
   * doesn't surface anywhere else. */
  const [manualSkillForm, setManualSkillForm] = useState({ name: '', description: '', inputSchema: '' });
  const [manualSkillSubmitting, setManualSkillSubmitting] = useState(false);

  const handlePersistDiscovered = async () => {
    if (!registration?.id || discoveredSkills.length === 0) return;
    try {
      /* Merge the freshly-discovered skills with whatever is already on
       * file. The slideout overwrote on Save which was destructive when
       * an operator had manually-curated entries. Merge dedupes by
       * `name || skillName`, preferring the discovered version (it's
       * more authoritative for risk/inputSchema). */
      const existing = pickArrayExt(registration.skillMappings);
      const byName = new Map();
      for (const s of existing) {
        const key = s?.name || s?.skillName;
        if (key) byName.set(key, s);
      }
      for (const s of discoveredSkills) {
        const key = s?.name || s?.skillName;
        if (key) byName.set(key, { ...byName.get(key), ...s, discoveredFromPlatform: true });
      }
      const merged = Array.from(byName.values());
      const res = await api.gelUpdateSkillMappings?.(registration.id, merged);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Save failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: 'Skill mappings saved',
        description: `${merged.length} total skill mapping(s) on file` });
      setDiscoveredSkills([]);
      await reloadRegistration();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Save failed', description: err?.message || String(err) });
    }
  };

  const handleAddManualSkill = async () => {
    if (!registration?.id || !manualSkillForm.name.trim()) return;
    setManualSkillSubmitting(true);
    try {
      const existing = pickArrayExt(registration.skillMappings);
      let inputSchemaObj = { type: 'object', properties: {} };
      const rawSchema = (manualSkillForm.inputSchema || '').trim();
      if (rawSchema) {
        try { inputSchemaObj = JSON.parse(rawSchema); }
        catch (e) {
          addToast({ tone: 'danger', title: 'Invalid input schema',
            description: 'Must be valid JSON: ' + e.message });
          setManualSkillSubmitting(false);
          return;
        }
      }
      const updated = [...existing, {
        name: manualSkillForm.name.trim(),
        description: manualSkillForm.description.trim(),
        inputSchema: inputSchemaObj,
      }];
      const res = await api.gelUpdateSkillMappings?.(registration.id, updated);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Add failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: `Skill "${manualSkillForm.name.trim()}" added` });
      setManualSkillForm({ name: '', description: '', inputSchema: '' });
      await reloadRegistration();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Add failed', description: err?.message || String(err) });
    } finally {
      setManualSkillSubmitting(false);
    }
  };

  const handleRemoveSkill = async (idx) => {
    if (!registration?.id) return;
    const existing = [...pickArrayExt(registration.skillMappings)];
    if (idx < 0 || idx >= existing.length) return;
    const removed = existing.splice(idx, 1)[0];
    try {
      const res = await api.gelUpdateSkillMappings?.(registration.id, existing);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Remove failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'info', title: 'Skill mapping removed',
        description: `"${removed?.name || removed?.skillName || 'mapping'}" no longer attached` });
      await reloadRegistration();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Remove failed', description: err?.message || String(err) });
    }
  };

  /* R4 — Inline runtime bindings. The unified-view endpoint returns the
   * cross-cutting view that the Governance Center previously owned:
   * GEL bridge-created rules, framework bindings, proxy skill configs,
   * and the active enforcement count. Renders below the policy
   * pill/empty-state in the Governance card so operators can see what
   * actually fires at runtime without leaving the page. */
  const [bindings, setBindings] = useState(null);
  const [bindingsLoading, setBindingsLoading] = useState(false);
  const reloadBindings = React.useCallback(async () => {
    if (!registration?.id) return;
    setBindingsLoading(true);
    try {
      const fn = api['gelGetUnifiedEnforcementView'];
      if (typeof fn !== 'function') { setBindings(false); return; }
      const res = await fn(registration.id);
      if (res && res.ok === false) { setBindings(false); return; }
      const data = res?.data || res;
      setBindings(data && typeof data === 'object' ? data : false);
    } catch {
      setBindings(false);
    } finally {
      setBindingsLoading(false);
    }
  }, [registration?.id]);
  useEffect(() => { reloadBindings(); }, [reloadBindings]);

  /* R3.3.d — Integration tests state. Operators record BUILD-gate test
   * results (name + PASSED/FAILED + free-text details) which are
   * persisted on the registration row's integrationTestResults JSONB
   * column. At least one PASSED test is required to advance to Decide
   * per the slideout's documentation. */
  const [testForm, setTestForm] = useState({ testName: '', status: 'PASSED', details: '' });
  const [testSubmitting, setTestSubmitting] = useState(false);
  /* P7J-RUN-036 — live probe in-flight flag for the Integration Tests card. */
  const [probeRunning, setProbeRunning] = useState(false);

  const handleRecordTest = async () => {
    if (!registration?.id) return;
    if (!testForm.testName.trim()) {
      addToast({ tone: 'danger', title: 'Test name required' });
      return;
    }
    setTestSubmitting(true);
    try {
      /* DEF-GEL-TEST-BODY-001 — BE expects `details` to be a JSON object
       * (Map<String,Object> on the Spring side → JSONB column). Sending
       * a raw string trips Jackson with "Request body could not be
       * parsed" and apiFetch returns { ok: false } rather than throw,
       * so we must envelope-check res.ok or the FE lies with a fake
       * success toast. Wrap free-text into notes/timestamp shape
       * (matches V1's GelExternalAgentsPage.jsx:459-462). */
      const detailsObj = {
        notes: testForm.details || 'Integration connectivity test',
        timestamp: new Date().toISOString(),
      };
      const res = await api.gelRecordIntegrationTest?.(
        registration.id, testForm.testName.trim(), testForm.status, detailsObj);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Record failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success',
        title: `Test "${testForm.testName.trim()}" recorded as ${testForm.status}` });
      setTestForm({ testName: '', status: 'PASSED', details: '' });
      await reloadRegistration();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Record failed', description: err?.message || String(err) });
    } finally {
      setTestSubmitting(false);
    }
  };

  /* R3.3.a — Agent UUID copy widget. The UUID is what operators paste
   * into the Gateway Keys page's `scopedAgentIds` field to mint a key
   * restricted to this specific ExteLet. */
  const copyAgentUuid = async () => {
    try {
      await navigator.clipboard?.writeText(agent.id);
      addToast({ tone: 'success', title: 'Copied',
        description: 'Agent UUID copied — paste into Gateway Keys → scoped agent IDs' });
    } catch {
      addToast({ tone: 'warning', title: 'Copy failed',
        description: 'Clipboard unavailable — copy manually from the field above' });
    }
  };

  /* Derive a registration-status pill that prefers the registration
   * record's authoritative status over the agent row's verifiedAt
   * fallback. ACTIVE/VERIFIED = success, SUSPENDED = warning,
   * REVOKED = danger, PENDING_VERIFICATION = warning.
   *
   * EXTELET-V3 · Phase 7 — bind to registrationStatus (the BE's field
   * name) not status (which doesn't exist on the entity). Same for
   * verifiedAt vs lastVerifiedAt. Previously the pill always read
   * undefined → fell through to "PENDING_VERIFICATION" regardless of
   * the actual status, so even after auto-verify flipped the row to
   * VERIFIED the operator saw a stale amber pill until refresh. */
  const statusInfo = (() => {
    const st = registration?.registrationStatus;
    if (st === 'VERIFIED' || (st == null && agent.verifiedAt)) {
      return { tone: 'success', label: 'VERIFIED' };
    }
    if (st === 'ACTIVE')    return { tone: 'success', label: 'ACTIVE' };
    if (st === 'SUSPENDED') return { tone: 'warning', label: 'SUSPENDED' };
    if (st === 'REVOKED')   return { tone: 'danger',  label: 'REVOKED' };
    return { tone: 'warning', label: 'PENDING_VERIFICATION' };
  })();
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    endpointUrl: agent.endpointUrl || agent.gatewayUrl || '',
    apiKey: '',
    vendorName: agent.vendorName || '',
    agentClassification: agent.agentClassification || 'EXTERNAL',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const openEdit = () => {
    setForm({
      endpointUrl: agent.endpointUrl || agent.gatewayUrl || '',
      apiKey: '', // never prefill secrets — operator types fresh if rotating
      vendorName: agent.vendorName || '',
      agentClassification: agent.agentClassification || 'EXTERNAL',
    });
    setSaveError(null);
    setEditOpen(true);
  };
  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        endpointUrl: form.endpointUrl.trim() || null,
        vendorName: form.vendorName.trim() || null,
        agentClassification: form.agentClassification || null,
      };
      /* Only include apiKey if the operator actually typed one — empty
       * string would otherwise wipe the existing key. */
      if (form.apiKey.trim()) payload.apiKey = form.apiKey.trim();
      const res = await api.updateAgent(agent.id, payload);
      if (!res || res.ok === false) {
        setSaveError(res?.error || res?.data?.error || `Backend rejected the update (HTTP ${res?.status || '?'}).`);
        return;
      }
      addToast({ tone: 'success', title: 'Registration updated' });
      setEditOpen(false);
      window.location.reload();
    } catch (e) {
      setSaveError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    {/* EXTELET-V3.5 Step 1 — Registration-side surfaces (registration row
     * + Edit form, invocation metrics, credentials + snippet card). Skill
     * mappings, Governance, and Integration tests now live under Build. */}
    {section === 'registration' && (
    <>
    <Card data-card-id="registration-form">
      <Card.Header
        title="External agent registration"
        actions={
          <div className="flex items-center gap-2">
            <Pill tone={statusInfo.tone} dot>{statusInfo.label}</Pill>
            {/* R3.3.a — Verify / Suspend / Revoke action triad. Buttons
             * are gated on registration existence; while the registration
             * record is loading, only the Edit button shows so the operator
             * can still fix endpoint/key fields. Suspend/Revoke are hidden
             * when status is already REVOKED. */}
            {registration && registration.id && (
              <>
                <Button
                  variant="primary" size="sm"
                  iconLeft={<CheckCircle size={13} />}
                  onClick={handleVerify}
                  loading={actBusy === 'verify'}
                  disabled={!!actBusy}
                  title="Run a live HTTP probe against the connector's health endpoint and stamp VERIFIED on 2xx"
                >
                  Verify
                </Button>
                {/* R2.3.b — Acknowledge fallback. Only surface for
                 * code-discovered ExteLets where there's no live endpoint
                 * to probe. Operator can override-stamp VERIFIED with an
                 * audit-logged action. */}
                {isCodeDiscovered && (
                  <Button
                    variant="ghost" size="sm"
                    onClick={handleAcknowledge}
                    loading={actBusy === 'acknowledge'}
                    disabled={!!actBusy}
                    title="Stamp VERIFIED without a network probe (code-discovered ExteLets only)"
                  >
                    Acknowledge
                  </Button>
                )}
                {registration.status !== 'REVOKED' && registration.status !== 'SUSPENDED' && (
                  <Button
                    variant="ghost" size="sm"
                    iconLeft={<Pause size={13} />}
                    onClick={() => { setSuspendReason(''); setSuspendOpen(true); }}
                    disabled={!!actBusy}
                    title="Temporarily block execution. Requires a reason for the audit trail."
                  >
                    Suspend
                  </Button>
                )}
                {registration.status !== 'REVOKED' && (
                  <Button
                    variant="ghost" size="sm"
                    className="!text-v2-danger"
                    iconLeft={<Ban size={13} />}
                    onClick={() => { setRevokeConfirmText(''); setRevokeOpen(true); }}
                    disabled={!!actBusy}
                    title="Permanently revoke this registration. Cannot be undone."
                  >
                    Revoke
                  </Button>
                )}
              </>
            )}
            <Button variant="ghost" size="sm" onClick={openEdit}>Edit</Button>
          </div>
        }
      />
      <Card.Body>
        {isInboundExteLet ? (
          /* V277 — Inbound ExteLet (MCP / Coded / connector-bound): no outbound
           * endpoint or API key exists by design. Show the honest binding
           * instead of the misleading "details not on file" warning. */
          <div className="mb-4 px-3 py-2 rounded-v2-sm border border-v2-border-subtle bg-v2-info-soft text-v2-ink-secondary text-[12px] leading-relaxed">
            <strong>{integrationLabel}.</strong> This Agentlet authenticates <em>inbound</em> to the Agentlets
            gateway with its minted {inboundKeyLabel}
            {agent.externalPlatformType ? <> (bound to the <strong>{agent.externalPlatformType}</strong> connector)</> : null}
            {' '}— there is no outbound endpoint URL or API key to record. Registration is complete; it auto-verifies on the
            first gateway call.
          </div>
        ) : (!hasRegistrationData && (
          <div className="mb-4 px-3 py-2 rounded-v2-sm border border-v2-warning bg-v2-warning-soft text-v2-warning text-[12px] leading-relaxed">
            <strong>Registration details aren't on file yet.</strong> This is a direct call-out registration —
            its Endpoint URL and API key haven't been recorded. Use <em>Edit</em> to add them.
          </div>
        ))}
        {regLoading === false && registration === false && hasRegistrationData && !isInboundExteLet && (
          <div className="mb-4 px-3 py-2 rounded-v2-sm border border-v2-border-subtle bg-v2-muted text-v2-ink-secondary text-[12px] leading-relaxed">
            <strong>Legacy ExteLet.</strong> Endpoint + key are on file but no <code>external_agent_registration</code>
            row exists yet, so Verify / Suspend / Revoke aren't available. Edit + save will trigger the
            registration record on the next backend sync.
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          {isInboundExteLet ? (
            <>
              <Field label="Integration" value={integrationLabel} />
              <Field label="Platform / framework" value={agent.externalPlatformType || registration?.platformType || '—'} />
            </>
          ) : (
            <>
              <Field label="Endpoint URL" value={agent.endpointUrl || agent.gatewayUrl || '—'} mono />
              <Field label="API key" value={agent.apiKey ? '••••••••••' + (agent.apiKey || '').slice(-4) : '—'} mono />
            </>
          )}
          <Field label="GEL policy bound" value={agent.policyName || agent.gelPolicyName || 'None bound'} />
          <Field label="Last verified" value={
            /* EXTELET-V3 · Phase 7 — bind to verifiedAt (BE field name), not
             * lastVerifiedAt (which doesn't exist). Falls back to the agent's
             * lastHealthCheckAt for legacy ExteLets, then 'never'. */
            registration?.verifiedAt
              ? new Date(registration.verifiedAt).toLocaleString()
              : (agent.lastHealthCheckAt ? new Date(agent.lastHealthCheckAt).toLocaleString() : 'never')} />
          <Field label="Vendor" value={agent.vendorName || registration?.vendorName || '—'} />
          <Field label="Classification" value={agent.agentClassification || '—'} />
        </div>
        {/* R2.3.b — Last probe outcome. Surfaced inline so the operator
         * sees exactly what the live verify probe found without leaving
         * the page. Cleared on Acknowledge / reload. */}
        {lastProbe && (
          <div
            className={
              'mt-4 px-3 py-2 rounded-v2-sm border text-[11.5px] leading-relaxed font-mono-v2 ' +
              (lastProbe.verified
                ? 'border-v2-success bg-v2-success-soft text-v2-success'
                : 'border-v2-warning bg-v2-warning-soft text-v2-warning')
            }
          >
            <strong className="block mb-0.5">
              {lastProbe.verified ? '✓ Probe verified' : '⚠ Probe did not verify'}
            </strong>
            <span className="text-v2-ink-secondary">
              {lastProbe.probedUrl
                ? <>GET <code>{lastProbe.probedUrl}</code> → </>
                : null}
              {lastProbe.statusCode
                ? <>HTTP {lastProbe.statusCode} in {lastProbe.latencyMs}ms</>
                : <>transport error</>}
            </span>
            {lastProbe.snippet && (
              <div className="mt-1 text-v2-ink-muted whitespace-pre-wrap break-all">
                {lastProbe.snippet}
              </div>
            )}
          </div>
        )}
        {/* R3.3.a — Agent UUID copy widget. The UUID is what operators
         * paste into the Gateway Keys → scopedAgentIds field to mint a
         * gateway key restricted to this ExteLet. */}
        <div className="mt-5 pt-4 border-t border-v2-border-subtle">
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Agent UUID</div>
          <div className="flex items-center gap-2">
            <code className="text-v2-ink font-mono-v2 text-[11px] flex-1 truncate">{agent.id}</code>
            <Button
              variant="ghost" size="sm"
              iconLeft={<Copy size={13} />}
              onClick={copyAgentUuid}
              title="Copy the agent UUID to paste into Gateway Keys → scoped agent IDs"
            >
              Copy
            </Button>
          </div>
          <p className="text-[11px] text-v2-ink-muted mt-1.5 leading-relaxed">
            Paste this UUID into <strong>Gateway Keys → Scoped agent IDs</strong> when minting a key
            restricted to this Agentlet.
          </p>
        </div>
        <p className="text-[12.5px] text-v2-ink-secondary mt-5 leading-relaxed">
          Once verification passes, this Agentlet enters the standard B-D-A flow. The Build / Decide / Act
          tabs above behave identically to a native Let from this point forward.
        </p>
      </Card.Body>

      {/* G197 / Phase 5B — Edit registration: Modal → Slideout per G378.
        * Form has 5+ fields (endpoint URL, API key, vendor, classification,
        * plus contextual hints), slideout-shaped. */}
      <Slideout
        isOpen={editOpen}
        onClose={saving ? undefined : () => setEditOpen(false)}
        width={620}
        ariaLabel="Edit registration details"
        header={<h2 className="text-[15px] font-bold m-0">Edit registration details</h2>}
        footer={
          <div className="flex items-center justify-end gap-2 w-full">
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-[12.5px]">
          {isInboundExteLet ? (
            /* V277 — Endpoint URL + API key don't apply to an inbound ExteLet
             * (MCP / Coded / connector-bound): it calls INTO the gateway with
             * its minted {inboundKeyLabel}. Hide the fields and say so, rather
             * than inviting the operator to enter values that have no effect. */
            <div className="px-3 py-2 rounded-v2-sm border border-v2-border-subtle bg-v2-muted text-v2-ink-secondary leading-relaxed">
              <strong>{integrationLabel}.</strong> No endpoint URL or API key to set — this Agentlet authenticates
              inbound with its minted {inboundKeyLabel}. Rotate that key from <strong>Protected Gateway → API Keys</strong>.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Endpoint URL</label>
                <input
                  type="url"
                  value={form.endpointUrl}
                  onChange={(e) => setForm({ ...form, endpointUrl: e.target.value })}
                  placeholder="https://your-agent.example.com"
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px] font-mono-v2"
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
                  API key {agent.apiKey ? <span className="text-v2-ink-muted normal-case">(leave blank to keep existing)</span> : null}
                </label>
                <input
                  type="password"
                  value={form.apiKey}
                  onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                  placeholder={agent.apiKey ? '•••••••••• (existing key preserved if blank)' : 'paste your API key'}
                  autoComplete="off"
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px] font-mono-v2"
                  disabled={saving}
                />
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Vendor</label>
              <input
                type="text"
                value={form.vendorName}
                onChange={(e) => setForm({ ...form, vendorName: e.target.value })}
                placeholder="e.g. OpenAI, Anthropic, custom"
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px]"
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Classification</label>
              <select
                value={form.agentClassification}
                onChange={(e) => setForm({ ...form, agentClassification: e.target.value })}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px]"
                disabled={saving}
              >
                <option value="EXTERNAL">EXTERNAL</option>
                <option value="INTERNAL">INTERNAL</option>
                <option value="VENDOR_SAAS">VENDOR_SAAS</option>
                <option value="PARTNER">PARTNER</option>
              </select>
            </div>
          </div>
          {saveError && (
            <div className="rounded-v2-sm border border-v2-danger-border bg-v2-danger-soft px-3 py-2 text-[12px] text-v2-danger">
              {saveError}
            </div>
          )}
          <div className="text-[11px] text-v2-ink-muted">
            GEL policy binding is managed from the Protection Center → External Agents page once verified.
          </div>
        </div>
      </Slideout>
    </Card>

    {/* R3.3.a — Invocation metrics card. V1 surfaced these in both the
     * expanded row and the View modal; the slideout's "Invocation
     * metrics" tile lives in GelExternalAgentsPageV2.jsx:954-978.
     * Sourced from the registration record (totalInvocations,
     * totalBlocked, lastInvocationAt). Renders below the registration
     * card only when a registration exists. */}
    {registration && registration.id && (
      <Card padding="sm" className="mt-3">
        <Card.Header title="Invocation metrics" />
        <Card.Body>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-center">
            <div>
              <dd className="text-[20px] font-bold tabular-nums leading-tight">{registration.totalInvocations ?? '—'}</dd>
              <dt className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted mt-1">Total invocations</dt>
            </div>
            <div>
              <dd className="text-[20px] font-bold tabular-nums leading-tight">{registration.totalBlocked ?? '—'}</dd>
              <dt className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted mt-1">Blocked</dt>
            </div>
            <div>
              <dd className="text-[12.5px] tabular-nums leading-tight">
                {registration.lastInvocationAt ? new Date(registration.lastInvocationAt).toLocaleString() : '—'}
              </dd>
              <dt className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted mt-1">Last invoked</dt>
            </div>
          </dl>
        </Card.Body>
      </Card>
    )}

    {/* EXTELET-V3 · Phase 7 — Credentials + Snippet card RELOCATED (J1b).
     * The Gateway/SDK/MCP credentials card (+ snippets + first-call observer)
     * now lives on the dedicated Identity journey step, rendered directly in
     * AgentDetailPageV2Inner's tab content (activeTab === 'identity'). It was
     * moved out of the Register step so credentials sit with the identity
     * proving they authenticate, per the Prove7-journey redesign. */}

    </>
    )}

    {/* EXTELET-V3.5 Step 1 — Build-phase surfaces (Skill mappings,
     * Governance, Integration tests). These three cards used to live on
     * Registration but functionally belong in Build:
     *   · Skill mappings ARE the policy-skill catalog the SDK binds to
     *   · Governance attaches policy rule + DPG profile + rate limit
     *     to each policy skill (this is what Agentlets actually controls
     *     for ExteLets — the external Agent Framework owns the functional
     *     capabilities)
     *   · Integration tests are the BUILD-gate evidence that each policy
     *     skill is wired and reachable
     * Mounted from BuildTab via `<RegistrationTab section="build" />`. */}
    {section === 'build' && (
      <ExteletBuildSurface
        agent={agent}
        registration={registration}
        reloadRegistration={reloadRegistration}
        discoveryRunning={discoveryRunning}
        discoveredSkills={discoveredSkills}
        setDiscoveredSkills={setDiscoveredSkills}
        handleDiscoverInline={handleDiscoverInline}
      />
    )}

    {/* EXTELET-V3.5 Step 1 — Modals stay at the top of the fragment so
     * they're available from either section: Suspend/Revoke are launched
     * from the Registration-section action triad, but keeping them
     * unguarded means the registration form's open-state survives a
     * Build → Registration tab switch (and vice-versa). */}

    {/* R3.3.a — Suspend modal. Reason is required for the audit trail. */}
    <Modal
      isOpen={suspendOpen}
      onClose={actBusy === 'suspend' ? undefined : () => setSuspendOpen(false)}
      title="Suspend external agent"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => setSuspendOpen(false)} disabled={actBusy === 'suspend'}>Cancel</Button>
          <Button
            /* R3.3.a — Button doesn't ship a 'warning' variant. Suspend
             * is a reversible action, so `primary` is the right confirm
             * affordance once the operator is already inside the modal;
             * the orange warning banner inside the body conveys the
             * stakes. */
            variant="primary"
            onClick={confirmSuspend}
            disabled={actBusy === 'suspend' || !suspendReason.trim()}
            loading={actBusy === 'suspend'}
          >
            {actBusy === 'suspend' ? 'Suspending…' : 'Suspend'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[12.5px] text-v2-ink-secondary leading-relaxed">
        <div className="px-3 py-2 rounded-v2-sm border border-v2-warning bg-v2-warning-soft text-v2-warning">
          <strong>{agent?.name || 'This ExteLet'}</strong> will stop accepting invocations until you resume it.
          Existing executions complete; new ones are rejected at the gateway.
        </div>
        <div>
          <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
            Reason <span className="text-v2-danger">*</span>
          </label>
          <textarea
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            placeholder="e.g. Investigating elevated 5xx rate from upstream endpoint"
            rows={3}
            className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-2 text-[12.5px]"
            disabled={actBusy === 'suspend'}
          />
          <p className="text-[11px] text-v2-ink-muted mt-1">
            Recorded verbatim in the audit log. Visible to platform admins.
          </p>
        </div>
      </div>
    </Modal>

    {/* R3.3.a — Revoke modal. Type-to-confirm guard against muscle-memory. */}
    <Modal
      isOpen={revokeOpen}
      onClose={actBusy === 'revoke' ? undefined : () => setRevokeOpen(false)}
      title="Revoke registration"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => setRevokeOpen(false)} disabled={actBusy === 'revoke'}>Cancel</Button>
          <Button
            variant="danger"
            onClick={confirmRevoke}
            disabled={actBusy === 'revoke' || revokeConfirmText.trim().toUpperCase() !== 'REVOKE'}
            loading={actBusy === 'revoke'}
          >
            {actBusy === 'revoke' ? 'Revoking…' : 'Revoke permanently'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[12.5px] text-v2-ink-secondary leading-relaxed">
        <div className="px-3 py-2 rounded-v2-sm border border-v2-danger bg-v2-danger-soft text-v2-danger">
          <strong className="block mb-1">This action cannot be undone.</strong>
          Revoking <strong>{agent?.name || 'this ExteLet'}</strong> permanently disables it. The audit trail
          is preserved but no further invocations will be accepted, ever. Re-onboarding requires a fresh registration.
        </div>
        <div>
          <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
            Type <code className="font-mono-v2 text-v2-ink">REVOKE</code> to confirm
          </label>
          <input
            type="text"
            value={revokeConfirmText}
            onChange={(e) => setRevokeConfirmText(e.target.value)}
            placeholder="REVOKE"
            autoComplete="off"
            className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-2 text-[12.5px] font-mono-v2"
            disabled={actBusy === 'revoke'}
          />
        </div>
      </div>
    </Modal>
    </>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10.5px] text-v2-ink-muted uppercase tracking-wider font-semibold mb-1">{label}</div>
      <div className={'text-[13px] text-v2-ink ' + (mono ? 'font-mono-v2' : '')}>{value}</div>
    </div>
  );
}

/* ── Collapsible section primitive (UX-1) ─────────────────────────
 * Used for the SPIFFE identity + Intent profiles panels above the
 * phase tabs on the Agent Detail page. Both panels were taking ~200px
 * of vertical real-estate each, pushing the Build / Decide / Act tabs
 * (the operator's primary workspace) below the fold.
 *
 * Semantics:
 *   - Default collapsed (callers pass `defaultCollapsed`). The header
 *     remains visible so operators can see "what's here" without
 *     dropping the whole identity layer into a sub-menu.
 *   - State persisted to localStorage keyed by `${agentId}:${sectionKey}`.
 *     Power users who do want SPIFFE visible while they work get it
 *     back on the next visit to the same agent.
 *   - Chevron rotates 90° when expanded (standard disclosure pattern).
 *   - Keyboard accessible: the whole header is a <button>, Enter/Space
 *     toggle, focus ring inherits from .v2-focus-ring.
 *
 * This deliberately doesn't show counts/badges in the collapsed header
 * because the child components (SpiffeIdentityPanel / IntentProfilesEditor)
 * lazy-load their own data. Surfacing a count here would require
 * duplicating that fetch, which we'd rather avoid for a UX-only change.
 *
 * J1c — currently UNUSED: Identity/Intent moved from above-nav collapsibles into
 * first-class journey steps. Retained as a reusable section primitive (cheaper
 * than re-adding it later); lint-silenced rather than deleted to keep this change
 * surgical and regression-free. */
// eslint-disable-next-line no-unused-vars
function CollapsibleSection({
  agentId,
  sectionKey,
  title,
  icon,
  rightHint,
  defaultCollapsed = true,
  children,
}) {
  const storageKey = agentId ? `agentlets:collapse:${agentId}:${sectionKey}` : null;
  const initial = React.useMemo(() => {
    if (!storageKey) return defaultCollapsed;
    try {
      const v = window.localStorage.getItem(storageKey);
      if (v === '1') return true;
      if (v === '0') return false;
    } catch (_) { /* localStorage may throw in private mode */ }
    return defaultCollapsed;
  }, [storageKey, defaultCollapsed]);

  const [collapsed, setCollapsed] = React.useState(initial);

  const toggle = React.useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      if (storageKey) {
        try { window.localStorage.setItem(storageKey, next ? '1' : '0'); }
        catch (_) { /* ignore quota / private mode errors */ }
      }
      return next;
    });
  }, [storageKey]);

  return (
    <div className="bg-v2-surface border-b border-v2-border-subtle px-7">
      <div className="max-w-[1400px] mx-auto">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-controls={`section-${sectionKey}`}
          className={
            'v2-focus-ring w-full flex items-center gap-2 py-2.5 ' +
            'text-left rounded-v2-sm transition-colors ' +
            'hover:bg-v2-muted/30'
          }
        >
          <span
            aria-hidden="true"
            className={
              'inline-flex items-center justify-center w-4 h-4 ' +
              'text-v2-ink-muted transition-transform duration-v2 ' +
              (collapsed ? '' : 'rotate-90')
            }
          >
            <ChevronRightIcon size={14} />
          </span>
          {icon && (
            <span aria-hidden="true" className="inline-flex items-center text-v2-ink-muted">
              {icon}
            </span>
          )}
          <span className="text-[12.5px] font-semibold text-v2-ink uppercase tracking-wider">
            {title}
          </span>
          {rightHint && (
            <span className="ml-auto text-[11px] text-v2-ink-muted font-medium normal-case tracking-normal">
              {rightHint}
            </span>
          )}
        </button>
        {!collapsed && (
          <div id={`section-${sectionKey}`} className="pb-3">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Build tab ────────────────────────────────────────────────── */
function BuildTab({ agent, onAddSkill, onAdvanceToDecide, archetype, reloadAgentFromParent, onJumpToRegistrationCard,
  /* P7J-RUN-037 Phase 3b — Discover capabilities lives on this tab's
   * Skills card now. The gate reads registration.platformConnectorId,
   * the button fires handleDiscoverInline, and discoveryRunning drives
   * the loading state. All three are owned by AgentDetailPageV2Inner
   * (lifted from RegistrationTab) so that surface and this one share
   * one canonical handler + state machine — no cross-component closures. */
  registration,
  discoveryRunning,
  handleDiscoverInline }) {
  const skills = agent.skills || [];
  /* G83 — BuildTab is rendered as a sub-component of the parent
   * detail page, so it doesn't inherit the parent's `addToast` /
   * `id` / `reloadAgent` / `api` references via closure (they were
   * intentionally scoped to the parent). The G57 onClick handler
   * called those names without acquiring them inside BuildTab,
   * which threw `ReferenceError: addToast is not defined` the
   * moment the user clicked "Generate evals →". Acquire them
   * locally now: `useToast` for toasts, `useParams` for the agent
   * id (already in URL), and `useNavigate` for navigation actions
   * we may add. The reload-agent helper has to be passed via prop
   * since it's owned by the parent's state.
   *
   * G152 — Defensive wrapper. We've seen ReferenceError: addToast
   * is not defined surface in production despite this destructure
   * being in scope, which suggests a stale bundle or a minifier
   * edge case dropping the binding from a column render closure.
   * Wrap in useCallback so we hold a stable reference, and guard
   * against useToast() returning a context where addToast is
   * somehow not a function (e.g. if a future refactor breaks the
   * provider contract). Net effect: a click NEVER crashes the
   * page; worst case it falls through to a console warning. */
  const toastCtx = useToast();
  const addToast = React.useCallback((arg, type, duration) => {
    if (toastCtx && typeof toastCtx.addToast === 'function') {
      return toastCtx.addToast(arg, type, duration);
    }
    // Last-ditch fallback: log so the operator notices, never throw.
    // eslint-disable-next-line no-console
    console.warn('[AgentDetailPageV2/BuildTab] toast unavailable, dropped:', arg);
    return null;
  }, [toastCtx]);
  const { id } = useParams();
  const navigate = useNavigate();
  /* G248 — Critical bug fix: the parent (AgentDetailPageV2Inner line 124)
   * holds the canonical async reloadAgent that re-fetches the agent
   * record via api.getAgent and calls setAgent. Previously this tab
   * declared a local fallback that did `window.location.reload()`, which
   * caused the eval-detail modal to vanish mid-Run because we were
   * BLOWING AWAY the entire app. We now accept the parent's reloadAgent
   * via prop and only fall back to the destructive reload if the prop
   * is genuinely missing (e.g. during a test render outside the parent). */
  const reloadAgent = React.useCallback(async () => {
    if (typeof reloadAgentFromParent === 'function') {
      try { await reloadAgentFromParent(); return; } catch (_e) { /* fall through */ }
    }
    // Last-resort fallback — should never fire in production.
    try { window.location.reload(); } catch (_e) { /* ignore */ }
  }, [reloadAgentFromParent]);
  /* G248 — V1 parity: connectors come from PlatformContext, marketplace
   * comes from useMarketplaceCatalogue. We filter installed connectors
   * whose marketplace manifest says isLlmProvider===true AND that are in
   * 'connected' status. This is the ONLY way an Anthropic/OpenAI/Gemini
   * connector shows up in the eval picker — api.getLlmProviders does NOT
   * return them on this codebase. Mirrors AgentsPage.jsx:639-663. */
  const platformCtx = usePlatform();
  const installedConnectors = platformCtx?.connectors || [];
  const { connectors: marketplaceConnectors } = useMarketplaceCatalogue();
  /* G248 — Defensive load. PlatformContext.connectors may be empty when
   * AgentDetailPageV2 is the first route the user lands on (the Agents
   * inventory page loads them on its own mount). Without this, the LLM
   * picker would only ever show Platform default even when Anthropic /
   * OpenAI / Gemini are connected. Single-shot, fires once per tab mount. */
  React.useEffect(() => {
    if (installedConnectors.length > 0) return;
    const loader = platformCtx?.loadConnectors;
    if (typeof loader === 'function') {
      loader({}).catch(() => { /* silent — picker still works with platform-only */ });
    }
  }, []); // eslint-disable-line

  /* G192 — Per-skill Edit / Push / Unpublish / Delete state. Replaces the
   * dead V2 "Edit" button (no onClick) and adds back the Push/Unpublish/Delete
   * flows V1 had per skill (AgentsPage.jsx). All four actions call existing
   * backend endpoints exposed in services/api.js — no new backend needed:
   *   - api.editSkill(agentId, skillId, payload)    PUT /api/v1/agents/{id}/skills/{sid}
   *   - api.pushSkill(agentId, skillId)             POST .../push
   *   - api.unpublishSkill(agentId, skillId)        POST .../unpublish
   *   - api.deleteSkill(agentId, skillId)           DELETE .../{sid}
   *
   * UI: small Edit modal with name/description/risk/autonomy/category/
   * inferenceMode fields, plus a Delete confirm modal. Push/Unpublish are
   * immediate one-click actions with toast feedback and a per-skill loading
   * flag so the row doesn't re-fire while a request is in flight. */
  const [editingSkill, setEditingSkill] = React.useState(null);
  const [editSkillForm, setEditSkillForm] = React.useState({});
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState(null);
  const [deletingSkill, setDeletingSkill] = React.useState(null);
  /* Step 6 (Platform-Eng workflow) — per-skill structured invocation
   * state. Null when closed; carries the skill row when the operator
   * clicks "Run…". The slideout itself owns the form state, so this
   * is just an open/close handle plus the target row. */
  const [invokingSkill, setInvokingSkill] = React.useState(null);
  /* Step 5 (Platform-Eng workflow) — add-existing-skill slideout
   * open/close state. The slideout owns its own search + install
   * state, so this is just a visibility handle. Independent from
   * the "New skill" MenuDropdown — both stay available. */
  const [addExistingOpen, setAddExistingOpen] = React.useState(false);
  const [deleteSaving, setDeleteSaving] = React.useState(false);
  const [pushingSkillId, setPushingSkillId] = React.useState(null);
  /* G247 — Per-skill advance/regress state. advancingSkillId and
   * regressingSkillId are simple loading flags. regressConfirm holds the
   * skill currently in the regress-confirm dialog (null when closed). */
  const [advancingSkillId, setAdvancingSkillId] = React.useState(null);
  const [regressingSkillId, setRegressingSkillId] = React.useState(null);
  const [regressConfirm, setRegressConfirm] = React.useState(null);

  /* Advance a single skill via api.advanceSkill. Backend gates the
   * transition; we render the resulting message verbatim so operators
   * see exactly why a transition was refused (e.g. "needs review",
   * "eval score < 90% for autonomous"). */
  const handleAdvanceSkill = React.useCallback(async (s) => {
    const fn = api['advanceSkill'];
    if (typeof fn !== 'function') {
      addToast({ tone: 'warning', title: 'Advance skill not wired',
        description: 'api.advanceSkill is missing in this build.' });
      return;
    }
    setAdvancingSkillId(s.id);
    try {
      const res = await fn(id, s.id);
      const data = res?.data || {};
      if (res?.ok && data.advanced === true) {
        addToast({ tone: 'success', title: `${s.name} advanced`,
          description: data.message || `${data.fromPhase} → ${data.toPhase}` });
        await refreshPhaseGate();
        await reloadAgent();
      } else if (res?.ok && data.advanced === false) {
        // Backend returned 200 with advanced:false + a reason — surface verbatim.
        addToast({ tone: 'warning', title: `${s.name} not advanced`,
          description: data.message || 'Backend refused the transition.' });
      } else {
        addToast({ tone: 'danger', title: 'Advance failed',
          description: res?.error || res?.data?.error || `HTTP ${res?.status || '?'}` });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Advance threw', description: e?.message || String(e) });
    } finally {
      setAdvancingSkillId(null);
    }
  }, [api, id, addToast, reloadAgent]);

  /* Regress a single skill to Build via api.regressSkill. Power-action
   * — confirm modal gates the actual call. AgentService.regressSkill
   * bumps regressionCount and stamps previousPhase for the audit trail. */
  const handleRegressSkill = React.useCallback(async () => {
    if (!regressConfirm?.id) return;
    const fn = api['regressSkill'];
    if (typeof fn !== 'function') {
      addToast({ tone: 'warning', title: 'Regress not wired',
        description: 'api.regressSkill is missing in this build.' });
      return;
    }
    setRegressingSkillId(regressConfirm.id);
    try {
      const res = await fn(id, regressConfirm.id);
      if (res?.ok) {
        addToast({ tone: 'success', title: `${regressConfirm.name} sent to Build`,
          description: 'Generate a new eval and run it to re-advance.' });
        setRegressConfirm(null);
        await refreshPhaseGate();
        await reloadAgent();
      } else {
        addToast({ tone: 'danger', title: 'Regress failed',
          description: res?.error || res?.data?.error || `HTTP ${res?.status || '?'}` });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Regress threw', description: e?.message || String(e) });
    } finally {
      setRegressingSkillId(null);
    }
  }, [api, id, regressConfirm, addToast, reloadAgent]);

  const openEditSkill = (s) => {
    setEditingSkill(s);
    setEditError(null);
    setEditSkillForm({
      name: s.name || '',
      description: s.description || '',
      category: s.category || '',
      riskLevel: s.riskLevel || 'medium',
      autonomy: s.autonomy || 'HUMAN_CONTROLLED',
      inferenceMode: s.inferenceMode || 'PLATFORM_INFERENCE',
      /* G264 / AGENT_EXECUTION_MODEL §2.2 — per-skill execution logic.
       * Backed by V172 schema. Render JSON fields as serialised text so
       * operators can paste in raw JSON; we parse on save with an
       * explicit error if malformed. */
      callability: s.callability || 'always',
      preconditionsText: s.preconditions
        ? (typeof s.preconditions === 'string'
            ? s.preconditions
            : JSON.stringify(s.preconditions, null, 2))
        : '',
      parallelGroup: s.parallelGroup || '',
      retryPolicyText: s.retryPolicy
        ? (typeof s.retryPolicy === 'string'
            ? s.retryPolicy
            : JSON.stringify(s.retryPolicy, null, 2))
        : '',
      timeoutMs: s.timeoutMs == null ? '' : String(s.timeoutMs),
      idempotencyKeyTemplate: s.idempotencyKeyTemplate || '',
      rateLimitsText: s.rateLimits
        ? (typeof s.rateLimits === 'string'
            ? s.rateLimits
            : JSON.stringify(s.rateLimits, null, 2))
        : '',
    });
  };

  /**
   * Deep-link: /agents/{id}?tab=build&skill={skillId} opens that skill's editor directly.
   *
   * The Policies wizard returns the operator here after activating a policy skill, and "back"
   * has to mean the SKILL they left — not merely the agent page with the panel closed. Declared
   * BELOW openEditSkill on purpose: referencing a `const` arrow function from above its
   * declaration is a temporal-dead-zone hazard, and ESLint won't catch it here.
   *
   * The disable comment is bare, not rule-named: this project's ESLint config does not load the
   * react-hooks plugin, so naming `react-hooks/exhaustive-deps` is itself a build-breaking error
   * ("Definition for rule ... was not found"). The same trap is already documented at lines ~349
   * and in WorkflowIntelligencePage / DashboardLayoutV2.
   */
  React.useEffect(() => {
    if (editingSkill) return;
    const wanted = new URLSearchParams(window.location.search).get('skill');
    if (!wanted) return;
    const list = agent?.skills || [];
    if (list.length === 0) return;          // skills not loaded yet — retry on the next pass
    const match = list.find((s) => String(s.id) === String(wanted));
    if (match) openEditSkill(match);
    // Consume the param so a refresh (or closing the editor) doesn't silently re-open it.
    const params = new URLSearchParams(window.location.search);
    params.delete('skill');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    // eslint-disable-next-line
  }, [agent?.skills]);

  const handleSaveSkillEdit = async () => {
    if (!editingSkill?.id) return;
    if (!editSkillForm.name?.trim()) {
      setEditError('Name is required');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const editFn = api['editSkill'];
      if (typeof editFn !== 'function') {
        setEditError('api.editSkill is not wired in this build.');
        return;
      }
      /* G264 — Parse the new execution-logic JSON fields strictly. If
       * any are malformed, surface an inline error before firing the
       * PUT so we don't silently drop the operator's intent. Empty
       * string maps to null (clears the column). */
      let preconditions = undefined;
      let retryPolicy = undefined;
      let rateLimits = undefined;
      let timeoutMs = undefined;
      try {
        const txt = (editSkillForm.preconditionsText || '').trim();
        preconditions = txt === '' ? null : JSON.parse(txt);
        if (preconditions !== null && !Array.isArray(preconditions)) {
          throw new Error('preconditions must be a JSON array of expression strings');
        }
      } catch (e) {
        setEditError('Preconditions: ' + (e?.message || 'invalid JSON'));
        return;
      }
      try {
        const txt = (editSkillForm.retryPolicyText || '').trim();
        retryPolicy = txt === '' ? null : JSON.parse(txt);
        if (retryPolicy !== null && (typeof retryPolicy !== 'object' || Array.isArray(retryPolicy))) {
          throw new Error('retryPolicy must be a JSON object {maxAttempts, backoffMs}');
        }
      } catch (e) {
        setEditError('Retry policy: ' + (e?.message || 'invalid JSON'));
        return;
      }
      try {
        const txt = (editSkillForm.rateLimitsText || '').trim();
        rateLimits = txt === '' ? null : JSON.parse(txt);
        if (rateLimits !== null && (typeof rateLimits !== 'object' || Array.isArray(rateLimits))) {
          throw new Error('rateLimits must be a JSON object {perMinute, perAgent}');
        }
      } catch (e) {
        setEditError('Rate limits: ' + (e?.message || 'invalid JSON'));
        return;
      }
      if (editSkillForm.timeoutMs === '' || editSkillForm.timeoutMs == null) {
        timeoutMs = null;
      } else {
        const n = parseInt(editSkillForm.timeoutMs, 10);
        if (!Number.isFinite(n) || n < 0) {
          setEditError('Timeout (ms) must be a non-negative integer');
          return;
        }
        timeoutMs = n;
      }

      const res = await editFn(id, editingSkill.id, {
        name: editSkillForm.name.trim(),
        description: editSkillForm.description?.trim() || '',
        category: editSkillForm.category?.trim() || 'general',
        riskLevel: editSkillForm.riskLevel,
        autonomy: editSkillForm.autonomy,
        inferenceMode: editSkillForm.inferenceMode,
        /* G264 — execution-logic fields (V172 schema). */
        callability: editSkillForm.callability || 'always',
        preconditions,
        parallelGroup: (editSkillForm.parallelGroup || '').trim() || null,
        retryPolicy,
        timeoutMs,
        idempotencyKeyTemplate: (editSkillForm.idempotencyKeyTemplate || '').trim() || null,
        rateLimits,
      });
      if (!res || res.ok === false) {
        /* G192 — Translate backend status into a clear operator-facing
         * message. 409 specifically means the skill is locked because it
         * advanced past Build phase — explain how to fix. */
        const status = res?.status;
        const backendMsg = res?.error || res?.data?.error;
        if (status === 409) {
          setEditError(
            backendMsg ||
            'This skill has advanced past Build phase and is locked. Regress it to Build phase before editing.');
        } else if (status === 403) {
          setEditError(backendMsg || 'You do not have skill.edit permission.');
        } else {
          setEditError(backendMsg || `Backend rejected the update (HTTP ${status || '?'}).`);
        }
        return;
      }
      addToast({ tone: 'success', title: 'Skill updated', description: editSkillForm.name });
      setEditingSkill(null);
      await reloadAgent();
    } catch (e) {
      setEditError(e?.message || String(e));
    } finally {
      setEditSaving(false);
    }
  };

  const handleConfirmDeleteSkill = async () => {
    if (!deletingSkill?.id) return;
    setDeleteSaving(true);
    try {
      const delFn = api['deleteSkill'];
      if (typeof delFn !== 'function') {
        addToast({ tone: 'danger', title: 'Delete failed', description: 'api.deleteSkill is not wired in this build.' });
        return;
      }
      const res = await delFn(id, deletingSkill.id);
      if (!res || res.ok === false) {
        const status = res?.status;
        const backendMsg = res?.error || res?.data?.error;
        let desc;
        if (status === 409) {
          desc = backendMsg || 'Skill is locked (past Build phase). Regress to Build before deleting.';
        } else if (status === 403) {
          desc = backendMsg || 'You do not have skill.delete permission.';
        } else {
          desc = backendMsg || `Backend rejected the delete (HTTP ${status || '?'}).`;
        }
        addToast({ tone: 'danger', title: 'Delete failed', description: desc });
        return;
      }
      addToast({ tone: 'success', title: 'Skill deleted', description: deletingSkill.name });
      setDeletingSkill(null);
      await reloadAgent();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Delete failed', description: e?.message || String(e) });
    } finally {
      setDeleteSaving(false);
    }
  };

  const handlePushSkill = async (s) => {
    if (!s?.id) return;
    setPushingSkillId(s.id);
    try {
      const fn = s.pushed ? api['unpublishSkill'] : api['pushSkill'];
      if (typeof fn !== 'function') {
        addToast({ tone: 'danger', title: 'Action unavailable', description: `api.${s.pushed ? 'unpublishSkill' : 'pushSkill'} is not wired.` });
        return;
      }
      const res = await fn(id, s.id);
      if (!res || res.ok === false) {
        const status = res?.status;
        const backendMsg = res?.error || res?.data?.error;
        let desc;
        if (status === 409) {
          desc = backendMsg || `${s.pushed ? 'Unpublish' : 'Push'} requires the skill to be in Build phase. Regress it first.`;
        } else if (status === 403) {
          desc = backendMsg || 'You do not have skill.push permission.';
        } else {
          desc = backendMsg || `Backend rejected the request (HTTP ${status || '?'}).`;
        }
        addToast({ tone: 'danger',
          title: `${s.pushed ? 'Unpublish' : 'Push'} failed`,
          description: desc });
        return;
      }
      addToast({ tone: 'success',
        title: s.pushed ? 'Skill unpublished' : 'Skill pushed',
        description: `${s.name} is now ${s.pushed ? 'unpublished' : 'published'}.` });
      await reloadAgent();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Action failed', description: e?.message || String(e) });
    } finally {
      setPushingSkillId(null);
    }
  };

  /* G195 — Revised-runbook upload for AutoLets. The button was a dead
   * stub; V1 has an upload-then-regenerate flow that re-runs the AutoLet
   * pipeline against a new runbook revision. V2 wires the same upload
   * via api.stageAutoCreateRunbook (POST /api/v1/auto-create/upload as
   * multipart) and on success directs the operator to the Auto-Create
   * review surface to commit. */
  const runbookFileRef = React.useRef(null);
  const [runbookUploading, setRunbookUploading] = React.useState(false);
  const handleRunbookUploadClick = () => runbookFileRef.current?.click();
  const handleRunbookFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so re-selecting the same file fires
    if (!file) return;
    setRunbookUploading(true);
    try {
      const stage = api['stageAutoCreateRunbook'];
      if (typeof stage !== 'function') {
        addToast({ tone: 'danger', title: 'Upload unavailable', description: 'api.stageAutoCreateRunbook is not wired.' });
        return;
      }
      const res = await stage(file);
      const runId = res?.runId || res?.id;
      addToast({
        tone: 'success',
        title: 'Runbook staged',
        description: runId
          ? `Auto-create run ${String(runId).slice(0, 8)}… is ready for review.`
          : 'New runbook staged. Open the auto-create wizard to commit.',
      });
      if (runId) navigate(`/auto-create/runs/${runId}`);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Runbook upload failed', description: err?.message || String(err) });
    } finally {
      setRunbookUploading(false);
    }
  };

  /* G196 — Policy-document RAG upload. The "Upload document" button on the
   * Policy documents (RAG) card was a dead stub. V1 has a per-policy-skill
   * upload that calls /api/v1/skills/{id}/documents (multipart). V2 picks
   * which policy_skill to attach the doc to (since each skill has its own
   * RAG corpus + enforcement level), then uploads. If the agent has no
   * policy_skill type skills, the modal surfaces that fact instead of
   * showing an empty picker. */
  const [policyDocOpen, setPolicyDocOpen] = React.useState(false);
  const [policyDocSkillId, setPolicyDocSkillId] = React.useState('');
  const [policyDocEnforcement, setPolicyDocEnforcement] = React.useState('advisory');
  const [policyDocFile, setPolicyDocFile] = React.useState(null);
  const [policyDocUploading, setPolicyDocUploading] = React.useState(false);
  const [policyDocError, setPolicyDocError] = React.useState(null);
  // Bumped after a successful upload so the per-skill policy panel auto-refreshes
  // its doc list (DEF-LLC-POLICYDOC-REFRESH-001).
  const [policyDocReloadToken, setPolicyDocReloadToken] = React.useState(0);
  const policySkills = skills.filter((s) => s.skillType === 'policy_skill');
  const openPolicyDocUpload = () => {
    setPolicyDocError(null);
    setPolicyDocFile(null);
    setPolicyDocEnforcement('advisory');
    setPolicyDocSkillId(policySkills[0]?.id || '');
    setPolicyDocOpen(true);
  };
  const handlePolicyDocSubmit = async () => {
    setPolicyDocError(null);
    if (!policyDocSkillId) { setPolicyDocError('Select a policy_skill to attach this document to.'); return; }
    if (!policyDocFile)    { setPolicyDocError('Choose a file to upload.'); return; }
    setPolicyDocUploading(true);
    try {
      const upload = api['uploadPolicyDocument'];
      if (typeof upload !== 'function') {
        setPolicyDocError('api.uploadPolicyDocument is not wired in this build.');
        return;
      }
      const res = await upload(policyDocSkillId, policyDocFile, policyDocEnforcement, 0);
      if (res?.ok === false) {
        setPolicyDocError(res?.error || res?.data?.error || `Backend rejected upload (HTTP ${res?.status || '?'}).`);
        return;
      }
      addToast({ tone: 'success', title: 'Policy document uploaded', description: `Indexed for ${policySkills.find(s => s.id === policyDocSkillId)?.name || 'skill'}.` });
      setPolicyDocOpen(false);
      setPolicyDocReloadToken((t) => t + 1); // auto-refresh the per-skill doc panel
      await reloadAgent();
    } catch (e) {
      setPolicyDocError(e?.message || String(e));
    } finally {
      setPolicyDocUploading(false);
    }
  };

  /* G171 — Import-from-connector modal state. Lives in BuildTab because
   * the [Import from connector] button is rendered here, and the modal
   * needs id + reloadAgent + addToast which are all already in scope. */
  const [importModalOpen, setImportModalOpen] = React.useState(false);
  const handleImported = React.useCallback(() => {
    addToast({ tone: 'success', title: 'Skills imported', description: 'Refreshing agent skill list…' });
    reloadAgent();
  }, [addToast]);
  /* GOLDEN_CEG_RULES §1.2 + §1.3 — when an AutoLet is runbook-managed,
   * the entire skill set + CEG are immutable. The frontend must hide
   * every mutation control (per §9.2 Wave 3 hide-controls list):
   *   - [+ New skill] / [Import from connector]
   *   - per-skill Edit / Delete / Re-push
   *   - Generate eval IS still allowed (§2 allowed table)
   *   - Phase advancement IS still allowed
   *
   * Primary signal: `agent.isRunbookManaged === true` (Lombok @Data
   * → camelCase JSON, set by Wave 1 backend during Auto Create
   * REVIEW commit). Fallback heuristic — if the backend hasn't
   * tagged the agent yet (W1 not deployed in this environment, or
   * the seal step skipped), the `AutoLets-` name prefix is the
   * v1-era proxy. Either signal locks the UI; the user uploads a
   * revised runbook to make changes either way. Mirrors
   * `constants/agentTypes.js:classifyAgentType` line 156 logic. */
  const isRunbookManaged = agent?.isRunbookManaged === true
    || (typeof agent?.name === 'string' && agent.name.startsWith('AutoLets-'));

  /* Source-aware AutoLet copy. An AutoLet is derived from a versioned source —
   * a Workflow, a Runbook, an Approved URL, or a Doc Library (GOLDEN_CEG_RULES
   * §11). The Build banner + Skills subtitle must name the ACTUAL source rather
   * than hardcoding "runbook" (workflow-sourced AutoLets carry sourceWorkflowId,
   * V238). `srcNoun` = what produced it; `srcReviseVerb` = how to change it. */
  const isWorkflowSourced = !!agent?.sourceWorkflowId;
  // Process Library AutoLets (docs/spreadsheets) reuse the runbook lineage and
  // carry no distinct source flag on the agent, so use neutral "source document"
  // copy that reads correctly for an uploaded runbook OR process file.
  const srcNoun = isWorkflowSourced ? 'workflow' : 'source document';
  const srcVersion = isWorkflowSourced ? agent?.sourceWorkflowVersion : agent?.runbookVersion;
  const srcRevise = isWorkflowSourced
    ? 'publish a revised version of the source workflow'
    : 'upload a revised source document';

  /* G179 — Fetch authoritative Build→Decide phase-gate from the backend.
   * V2 used to hand-code criteria that diverged from PhaseGateService:
   *   - hand-coded "every skill has eval_score > 0" but backend only
   *     requires "at least one skill is eval-ready" (partial advancement).
   *   - hand-coded "valid gateway URL" gate that the backend doesn't check
   *     for the Build→Decide transition at all.
   * The frontend now reads the backend's checks array and renders them
   * one-for-one, so whatever rules the platform enforces are what the user
   * sees. The remediations below map check.action hints into actionable
   * buttons (Generate evals, etc.). */
  const [phaseGateChecks, setPhaseGateChecks] = useState([]);
  const [phaseGateLoading, setPhaseGateLoading] = useState(true);
  const refreshPhaseGate = React.useCallback(async () => {
    if (!id) return;
    setPhaseGateLoading(true);
    try {
      const preview = api['previewPhaseGate'];
      if (typeof preview !== 'function') return;
      const res = await preview(id);
      const checks = Array.isArray(res?.data?.checks) ? res.data.checks : [];
      setPhaseGateChecks(checks);
    } catch (e) {
      setPhaseGateChecks([{ check: 'preview_error', passed: false,
        message: e?.message || 'Failed to load phase gate preview' }]);
    } finally {
      setPhaseGateLoading(false);
    }
  }, [id]);
  /* Re-key the gate refresh on a signature of the fields the gate actually reads
   * (phase, eval-readiness, review) — NOT just skills.length. A per-skill Advance /
   * Regress / Review changes a skill's phase or eval without changing the count, so
   * keying on length alone left the gate stale until a manual reload. The string
   * signature re-fires the effect on any gate-relevant change. */
  const skillsGateSig = React.useMemo(
    () => skills.map((s) => `${s.id}:${s.skillPhase || 'build'}:${s.evalScore ?? ''}:${s.reviewStatus || ''}`).join('|'),
    [skills],
  );
  React.useEffect(() => { refreshPhaseGate(); }, [refreshPhaseGate, skillsGateSig]);

  const buildLabelFor = (c) => {
    switch (c.check) {
      case 'at_least_one_skill':      return 'Agent has at least 1 skill configured';
      case 'at_least_one_eval_ready': return 'At least one skill is eval-ready (completed eval + passing score)';
      case 'skills_remaining_in_build': return 'Skills remaining in Build (informational)';
      case 'failed_evals_warning':     return 'Failed eval suites (informational)';
      /* R2.b — ExteLet gate keys returned by evaluateBuildGateExternal. */
      case 'registration_exists':         return 'External agent registration record exists';
      case 'at_least_one_skill_mapping':  return 'At least 1 skill mapping is on file';
      case 'at_least_one_test_passed':    return 'At least 1 PASSED integration test recorded';
      case 'connector_healthy':           return 'Bound platform connector is healthy';
      default:                         return c.check || 'Backend check';
    }
  };

  /* G245 — V1 eval modal parity. The original V1 page (AgentsPage.jsx)
   * had TWO real modals for the eval lifecycle that V2 collapsed into a
   * single auto-fire button (which the user correctly flagged as a
   * shortcut). We rebuild both here as V2-native <Modal>s:
   *
   *   1. Generate Eval picker — LLM provider radio list, model picker,
   *      "what gets generated" preview. Fires api.generateEval.
   *   2. Eval Detail viewer — 5 KPI cards (Score / Status / Passed /
   *      Failed / Total), LLM provider pill, phase-readiness banner,
   *      test-case browser with pass/fail status + reasons, run history,
   *      Delete / Run / Re-run footer actions.
   *
   * The detail modal opens automatically after generation completes (the
   * suite has no runs yet — operator clicks Run to execute), and is also
   * accessible from the skill row's "View evals" button when a suite
   * already exists.
   *
   * State map:
   *   pickerSkill          — non-null when the Generate picker is open
   *   llmProviders         — fetched once when picker first opens
   *   selectedProvider     — radio selection
   *   selectedModel        — radio selection (only for non-platform)
   *   generating           — bool while api.generateEval is in flight
   *   evalDetailSuite      — non-null when the Detail viewer is open
   *   evalDetailLoading    — bool while test-cases + runs are fetching
   *   evalDetailTestCases  — array, refreshed after each run
   *   evalDetailRuns       — array, refreshed after each run
   *   runningEval          — bool while api.runEval is in flight (in detail)
   *   deletingEval         — bool while api.deleteEvalSuite is in flight
   *   deleteConfirm        — bool to gate destructive action
   */
  const [pickerSkill, setPickerSkill] = React.useState(null);
  const [selectedProvider, setSelectedProvider] = React.useState('__platform__');
  const [selectedModel, setSelectedModel] = React.useState(null);
  const [generating, setGenerating] = React.useState(false);
  const [evalDetailSuite, setEvalDetailSuite] = React.useState(null);
  const [evalDetailLoading, setEvalDetailLoading] = React.useState(false);
  const [evalDetailTestCases, setEvalDetailTestCases] = React.useState([]);
  const [evalDetailRuns, setEvalDetailRuns] = React.useState([]);
  const [runningEval, setRunningEval] = React.useState(false);
  const [deletingEval, setDeletingEval] = React.useState(false);
  const [deleteConfirm, setDeleteConfirm] = React.useState(false);

  /* G248 — V1 parity LLM provider list. Built fresh from installed
   * connectors × marketplace manifest on every render rather than lazy-
   * fetched. This matches AgentsPage.jsx:639-663 exactly:
   *   1. Filter installed connectors by marketplace.isLlmProvider
   *      AND status === 'connected'.
   *   2. Enrich with marketplace vendor / icon / models for the radio UI.
   *   3. Prepend a synthetic Platform default so the picker is always
   *      submittable even when no LLM connector is connected.
   * useMemo keeps the array stable so the radio onChange doesn't churn. */
  const llmProviders = React.useMemo(() => {
    const llmConnectors = (installedConnectors || []).filter((c) => {
      const mc = findMarketplaceForInstalled(marketplaceConnectors, c);
      return mc?.isLlmProvider && (c.status === 'connected' || c.status === 'CONNECTED');
    });
    const platform = {
      id: '__platform__',
      name: 'Platform default',
      providerType: 'platform',
      modelId: 'agentlets-eval-v1',
      icon: '⚡',
      models: [],
    };
    const mapped = llmConnectors.map((c) => {
      const mc = findMarketplaceForInstalled(marketplaceConnectors, c);
      return {
        id: c.id,
        name: mc?.name || c.name,
        providerType: (mc?.vendor || '').toLowerCase() || 'custom',
        icon: mc?.icon || '🔧',
        models: Array.isArray(mc?.models) ? mc.models : [],
        connectorId: c.id,
      };
    });
    return [platform, ...mapped];
  }, [installedConnectors, marketplaceConnectors]);

  /* Open the Generate picker. Defaults to Platform provider; if the user
   * has connected LLMs we still default to platform (so the picker is
   * immediately submittable) but they're free to pick another. */
  const openGenerateEval = React.useCallback((skill) => {
    setPickerSkill(skill);
    setSelectedProvider('__platform__');
    setSelectedModel(null);
  }, []);

  /* Submit the picker. On success, close picker and open the detail
   * viewer with the freshly-created suite so the operator can see what
   * was generated and run it. Mirrors V1's handleGenerateEval flow but
   * uses the V2 toast/state idioms. */
  const openEvalDetail = React.useCallback(async (suite) => {
    setEvalDetailSuite(suite);
    setEvalDetailLoading(true);
    setEvalDetailTestCases([]);
    setEvalDetailRuns([]);
    setDeleteConfirm(false);
    try {
      const tcFn = api['getEvalTestCases'];
      const runsFn = api['getEvalRuns'];
      const [tcRes, runsRes] = await Promise.all([
        typeof tcFn === 'function' ? tcFn(id, suite.id) : Promise.resolve({ ok: false }),
        typeof runsFn === 'function' ? runsFn(id, suite.id) : Promise.resolve({ ok: false }),
      ]);
      setEvalDetailTestCases(Array.isArray(tcRes?.data) ? tcRes.data : []);
      setEvalDetailRuns(Array.isArray(runsRes?.data) ? runsRes.data : []);
    } finally {
      setEvalDetailLoading(false);
    }
  }, [api, id]);

  const handleGenerateSubmit = React.useCallback(async () => {
    if (!pickerSkill?.id) return;
    const gen = api['generateEval'];
    if (typeof gen !== 'function') {
      addToast({ tone: 'warning', title: 'Eval generator unavailable',
        description: 'api.generateEval is not wired in this build.' });
      return;
    }
    setGenerating(true);
    try {
      const payload = { skillId: pickerSkill.id };
      if (selectedProvider && selectedProvider !== '__platform__') {
        payload.llmProviderId = selectedProvider;
      }
      if (selectedModel) payload.llmModelId = selectedModel;
      const res = await gen(id, payload);
      const suite = res?.data;
      if (!res?.ok || !suite?.id) {
        addToast({ tone: 'danger', title: 'Generation failed',
          description: res?.error || res?.data?.error || 'Backend rejected the request.' });
        return;
      }
      addToast({ tone: 'success', title: `Generated ${suite.totalTests || '?'} test case(s)`,
        description: 'Open the suite below to review test cases and click Run Eval to execute.' });
      setPickerSkill(null);
      // Open detail so the operator immediately sees the new suite + can run it.
      await openEvalDetail(suite);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Generation threw', description: e?.message || String(e) });
    } finally {
      setGenerating(false);
    }
  }, [api, id, pickerSkill, selectedProvider, selectedModel, addToast, openEvalDetail]);

  /* Run the eval suite currently open in detail. Updates the detail
   * modal's test cases + runs in-place, then reloads the agent so the
   * skill row's evalScore + the phase gate refresh. */
  const handleRunInDetail = React.useCallback(async () => {
    if (!evalDetailSuite?.id) return;
    const runFn = api['runEval'];
    if (typeof runFn !== 'function') {
      addToast({ tone: 'warning', title: 'Run-eval not wired', description: 'api.runEval missing.' });
      return;
    }
    setRunningEval(true);
    try {
      const res = await runFn(id, evalDetailSuite.id);
      const runData = res?.data;
      if (!res?.ok || runData == null) {
        addToast({ tone: 'danger', title: 'Run failed',
          description: res?.error || res?.data?.error || 'Backend rejected the run.' });
        return;
      }
      const pct = typeof runData.score === 'number' ? Math.round(runData.score * 100) : null;
      addToast({
        tone: (runData.passedTests > 0) ? 'success' : 'warning',
        title: `Run complete: ${runData.passedTests || 0}/${runData.totalTests || 0} passed${pct != null ? ` (${pct}%)` : ''}`,
        description: (runData.passedTests > 0)
          ? 'Skill evalScore + suite status updated. Gate should refresh.'
          : 'Every test failed — review the test case panel and adjust the skill config or re-generate.',
      });
      // Re-fetch test cases (now have status=passed/failed + actualOutput) + runs (now has 1 more entry).
      const tcFn = api['getEvalTestCases'];
      const runsFn = api['getEvalRuns'];
      const [tcRes, runsRes] = await Promise.all([
        typeof tcFn === 'function' ? tcFn(id, evalDetailSuite.id) : Promise.resolve({ ok: false }),
        typeof runsFn === 'function' ? runsFn(id, evalDetailSuite.id) : Promise.resolve({ ok: false }),
      ]);
      setEvalDetailTestCases(Array.isArray(tcRes?.data) ? tcRes.data : []);
      setEvalDetailRuns(Array.isArray(runsRes?.data) ? runsRes.data : []);
      // Stamp the suite as completed so the modal header + KPI cards reflect the new state without a refetch.
      setEvalDetailSuite((prev) => prev ? {
        ...prev,
        status: 'completed',
        score: runData.score,
        passedTests: runData.passedTests,
        totalTests: runData.totalTests,
      } : prev);
      await refreshPhaseGate();
      await reloadAgent();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Run threw', description: e?.message || String(e) });
    } finally {
      setRunningEval(false);
    }
  }, [api, id, evalDetailSuite, addToast, refreshPhaseGate]);

  /* Delete the eval suite currently open in detail. Two-step confirm
   * inline in the footer to match V1's window.confirm pattern without
   * leaving the V2 toast/modal idioms. */
  const handleDeleteInDetail = React.useCallback(async () => {
    if (!evalDetailSuite?.id) return;
    const del = api['deleteEvalSuite'];
    if (typeof del !== 'function') {
      addToast({ tone: 'warning', title: 'Delete not wired', description: 'api.deleteEvalSuite missing.' });
      return;
    }
    setDeletingEval(true);
    try {
      const res = await del(id, evalDetailSuite.id);
      if (res?.ok === false) {
        addToast({ tone: 'danger', title: 'Delete failed',
          description: res?.error || res?.data?.error || `HTTP ${res?.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: 'Eval suite deleted',
        description: 'Generate a new one to re-evaluate this skill.' });
      setEvalDetailSuite(null);
      setDeleteConfirm(false);
      await refreshPhaseGate();
      await reloadAgent();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Delete threw', description: e?.message || String(e) });
    } finally {
      setDeletingEval(false);
    }
  }, [api, id, evalDetailSuite, addToast, refreshPhaseGate]);

  /* G249 — Quick Fix mapping. Mirrors V1 AgentsPage.jsx:2638-2671.
   * Maps a failed test scenario (from tc.input.scenario) into a concrete
   * skill-config edit the operator can apply inline:
   *   - boundary           → inferenceFallback (radio choices)
   *   - invalid_input      → guardrails (add inputValidation)
   *   - injection          → guardrails (add inputSanitization)
   *   - guardrail_violation→ guardrails (add policyEnforcement)
   *   - unauthorized       → autonomy (lower it)
   *   - privilege_escalation→ autonomy (lower it)
   *   - performance        → maxExecutionMs (number input)
   *   - happy_path         → description (text input)
   *   - idempotency        → description (text input)
   *
   * Returns null for scenarios with no known field-level fix, in which
   * case we fall back to the plain "Fix:" text + "Open full skill editor"
   * link at the bottom of the failed list. */
  const computeQuickFix = React.useCallback((tc, skill) => {
    if (tc.status !== 'failed' || !skill) return null;
    const scenario = tc.input?.scenario || '';
    switch (scenario) {
      case 'boundary':
        return {
          field: 'inferenceFallback',
          label: 'Inference fallback',
          current: skill.inferenceFallback || '(none)',
          options: ['default_response', 'queue', 'reject_gracefully'],
          desc: 'How the skill handles edge-case inputs when primary inference fails.',
        };
      case 'invalid_input':
      case 'injection':
      case 'guardrail_violation':
        return {
          field: 'guardrails',
          label: 'Guardrails',
          current: skill.guardrails ? JSON.stringify(skill.guardrails) : '(none)',
          addGuardrail: scenario === 'injection' ? 'inputSanitization'
            : scenario === 'invalid_input' ? 'inputValidation'
            : 'policyEnforcement',
          desc: scenario === 'injection' ? 'Add input sanitization to block SQL/XSS/prompt injection.'
            : scenario === 'invalid_input' ? 'Add input validation to reject malformed requests.'
            : 'Add guardrail policies to enforce safety boundaries.',
        };
      case 'unauthorized':
      case 'privilege_escalation':
        return {
          field: 'autonomy',
          label: 'Autonomy level',
          current: skill.autonomy || '(none)',
          options: ['HUMAN_CONTROLLED', 'SUPERVISED', 'ASSISTED'],
          desc: 'Reduce autonomy to require authorization checks.',
        };
      case 'performance':
        return {
          field: 'maxExecutionMs',
          label: 'SLA threshold (ms)',
          current: skill.maxExecutionMs || '(not set)',
          inputType: 'number',
          defaultValue: 5000,
          desc: 'Set the maximum acceptable execution time.',
        };
      case 'happy_path':
      case 'idempotency':
        return {
          field: 'description',
          label: 'Skill description',
          current: skill.description || '(empty)',
          inputType: 'text',
          desc: 'Add a meaningful description so the eval can validate behavior.',
        };
      default:
        return null;
    }
  }, []);

  /* Apply a single field edit via api.editSkill, then refresh the agent.
   * Surfaces the result so the operator immediately sees whether the fix
   * landed. Mirrors V1 handleQuickFix (AgentsPage.jsx:2674-2684). */
  const handleQuickFix = React.useCallback(async (skill, fieldName, value) => {
    const edit = api['editSkill'];
    if (typeof edit !== 'function') {
      addToast({ tone: 'warning', title: 'Edit endpoint missing', description: 'api.editSkill is not wired.' });
      return;
    }
    try {
      const res = await edit(id, skill.id, { [fieldName]: value });
      if (res?.ok) {
        addToast({ tone: 'success', title: `${fieldName} updated`,
          description: `Re-run the eval to verify the fix lands.` });
        await reloadAgent();
      } else {
        addToast({ tone: 'danger', title: 'Update failed',
          description: res?.error || res?.data?.error || 'Backend rejected the edit.' });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Update threw', description: e?.message || String(e) });
    }
  }, [api, id, addToast, reloadAgent]);

  /* Merge a new guardrail into the skill's existing guardrails JSON and
   * persist via editSkill. Treats both array and object shapes (legacy
   * vs current). Mirrors V1 handleAddGuardrail (AgentsPage.jsx:2687-2703). */
  const handleAddGuardrail = React.useCallback(async (skill, guardrailType) => {
    const edit = api['editSkill'];
    if (typeof edit !== 'function') {
      addToast({ tone: 'warning', title: 'Edit endpoint missing', description: 'api.editSkill is not wired.' });
      return;
    }
    try {
      const existing = Array.isArray(skill.guardrails) ? skill.guardrails
        : skill.guardrails && typeof skill.guardrails === 'object' ? Object.keys(skill.guardrails)
        : [];
      const next = {};
      existing.forEach((g) => { next[typeof g === 'string' ? g : g.type || g] = true; });
      next[guardrailType] = true;
      const res = await edit(id, skill.id, { guardrails: next });
      if (res?.ok) {
        addToast({ tone: 'success', title: `Added "${guardrailType}" guardrail`,
          description: 'Re-run the eval to verify enforcement.' });
        await reloadAgent();
      } else {
        addToast({ tone: 'danger', title: 'Guardrail update failed',
          description: res?.error || res?.data?.error || 'Backend rejected the edit.' });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Guardrail update threw', description: e?.message || String(e) });
    }
  }, [api, id, addToast, reloadAgent]);

  /* "View evals" entry point: look up the most recent suite for this
   * skill and open the detail viewer. If none exists, fall back to
   * opening the Generate picker so the operator isn't stuck in an empty
   * read-only state. */
  const openExistingEvalForSkill = React.useCallback(async (skill) => {
    const list = api['getEvals'];
    if (typeof list !== 'function') {
      // No way to list — fall through to picker.
      openGenerateEval(skill);
      return;
    }
    try {
      const res = await list(id);
      const all = Array.isArray(res?.data) ? res.data
        : Array.isArray(res?.data?.content) ? res.data.content : [];
      // Find the most-recently-created non-archived suite for this skill.
      const mine = all
        .filter((s) => s.skillId === skill.id && s.status !== 'archived')
        .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
      if (mine.length === 0) {
        openGenerateEval(skill);
        return;
      }
      await openEvalDetail(mine[0]);
    } catch (_e) {
      openGenerateEval(skill);
    }
  }, [api, id, openGenerateEval, openEvalDetail]);

  const gateCriteria = phaseGateChecks.map((c) => ({
    id: c.check,
    passed: !!c.passed,
    label: buildLabelFor(c),
    detail: c.message,
    /* Build phase remediation: open the Generate Eval picker for the
     * first skill that isn't eval-ready yet. The user picks an LLM
     * provider, generates, then runs explicitly from the detail modal —
     * same as V1, no silent auto-chain. After one skill is cleared they
     * click the gate button again for the next, or use each row's own
     * Generate button. */
    /* R2.b — Remediations. Each failing check has a one-click "fix me"
     * link in the PhaseGate row. Native checks route to the eval
     * generator; ExteLet checks switch to the Registration tab and
     * scroll the relevant card into view (via the parent's
     * onJumpToRegistrationCard callback). The data-card-id attributes
     * on the matching RegistrationTab cards are the scroll targets. */
    remediation: (() => {
      if (c.passed) return null;
      /* P7J-RUN-017 — native Let "at least 1 skill" gate had no
       * remediation; the row was just plain text, leaving the operator
       * to guess they needed to click "+ New skill" further down the
       * page. Add a click target that scrolls the Skills card into view
       * + flashes its border so the next action is obvious. */
      if (c.check === 'at_least_one_skill') {
        return {
          label: 'Add a skill',
          onClick: () => {
            const target = document.querySelector('[data-section="skills"]');
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              target.classList.add('ring-2', 'ring-v2-action');
              setTimeout(() => target.classList.remove('ring-2', 'ring-v2-action'), 1500);
            }
          },
        };
      }
      if (c.check === 'at_least_one_eval_ready' && skills.length > 0) {
        return {
          label: 'Open eval generator',
          onClick: () => {
            const missing = skills.filter((s) => !(typeof s.evalScore === 'number' && s.evalScore > 0));
            if (missing.length === 0) {
              addToast({ tone: 'info', title: 'All skills already eval-ready',
                description: 'Nothing to evaluate — try Advance to Decide.' });
              return;
            }
            if (missing.length > 1) {
              addToast({ tone: 'info', title: `${missing.length} skill(s) need an eval`,
                description: `Opening picker for "${missing[0].name}". Repeat for the rest after.` });
            }
            openGenerateEval(missing[0]);
          },
        };
      }
      /* Ownership consent — the accountable owner must accept (or be nominated) in the
       * Accountability Registry. The backend marks this check with fixAction "navigate_ownership";
       * deep-link the operator straight to Outcomes & Actions where the ceremony lives. */
      if (c.check === 'owner_consent_and_accountability' || c.fixAction === 'navigate_ownership') {
        return {
          label: 'Resolve in Accountability',
          onClick: () => navigate('/accountability?tab=outcomes'),
        };
      }
      /* ExteLet remediations — only meaningful when the parent supplied
       * the jump callback (the prop is undefined for v1 callers). */
      if (typeof onJumpToRegistrationCard === 'function') {
        if (c.check === 'registration_exists') {
          return { label: 'Open Registration tab', onClick: () => onJumpToRegistrationCard('registration-form') };
        }
        if (c.check === 'at_least_one_skill_mapping') {
          return { label: 'Add a skill mapping', onClick: () => onJumpToRegistrationCard('skill-mappings') };
        }
        if (c.check === 'at_least_one_test_passed') {
          return { label: 'Record a test', onClick: () => onJumpToRegistrationCard('integration-tests') };
        }
        if (c.check === 'connector_healthy') {
          return { label: 'Open connector page', onClick: () => navigate('/gel-connectors') };
        }
      }
      return null;
    })(),
  }));

  const advanceReady = !phaseGateLoading && gateCriteria.length > 0
    && gateCriteria.every((c) => c.passed);
  // Agent already past Build → the agent-level Build→Decide gate is moot (new/regressed
  // skills now progress individually via each skill card's Advance). Render an "advanced"
  // state instead of a stale "0 of 0 passing" with a permanently-disabled button.
  const agentPastBuild = ['decide', 'act'].includes((agent?.phase || 'build').toLowerCase());

  return (
    <div className="space-y-5">
      <PhaseGate
        phase="BUILD"
        criteria={gateCriteria}
        advanced={agentPastBuild}
        advanceAction={
          agentPastBuild ? (
            <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-v2-success-strong"
              title="The agent already advanced past Build. New or regressed skills now progress individually via each skill's Advance.">
              ✓ Advanced
            </span>
          ) : (
          /* G250 — Consistent gate UI: explicit "Advance to Decide" label
           * with disabled state when gate fails. The gate progress count
           * lives in the auxiliary label and the card header. */
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-v2-ink-muted tabular-nums">
              Gate: {gateCriteria.filter((c) => c.passed).length} of {gateCriteria.length}
            </span>
            <Button
              variant="primary"
              size="sm"
              disabled={!advanceReady}
              onClick={onAdvanceToDecide}
              title={advanceReady
                ? 'Promote agent from Build to Decide phase'
                : 'Gate not passing — generate + run an eval suite (score > 0) on at least one skill'}>
              Advance to Decide
            </Button>
          </div>
          )
        }
      />

      {/* G82 — runbook-managed banner per GOLDEN_CEG_RULES Wave 3.
        * This is the canonical "AutoLet is locked" affordance: it
        * tells the user why mutation buttons are missing and points
        * them at the runbook-revision flow that IS allowed. Mirrors
        * the v1 stone-grey banner (AgentsPage line ~3536). */}
      {isRunbookManaged && (
        <div className="px-4 py-3 rounded-v2-md border border-v2-border-subtle bg-v2-muted text-[12.5px] leading-relaxed text-v2-ink-secondary">
          <strong className="text-v2-ink">This is an AutoLet.</strong> Its skills and workflow graph were
          generated from your {srcNoun}{srcVersion ? ` (v${srcVersion})` : ''} and are read-only.
          You can still <strong>run evals</strong>, <strong>advance through Build → Decide → Act</strong>, and
          <strong> publish</strong>. To change what this AutoLet does, <strong>{srcRevise}</strong> — the
          platform will generate a new version that you can review and promote when ready.
        </div>
      )}

      {/* Bug B1 — restore V1's cross-phase context banner so an operator on the
          Build tab of an already-advanced agent understands skills stay editable
          here and new ones progress independently (AgentsPage.jsx:3555). */}
      {!isRunbookManaged && (agent?.phase || 'build').toLowerCase() !== 'build' && (
        <div className="px-4 py-3 rounded-v2-md border border-v2-border-subtle bg-v2-muted">
          <div className="text-[13px] font-semibold text-v2-ink">
            Agent is in {((agent.phase || '').charAt(0).toUpperCase() + (agent.phase || '').slice(1).toLowerCase())} phase
          </div>
          <div className="text-[12px] text-v2-ink-secondary mt-0.5">
            You can still add, edit, and manage skills here. New skills advance through the pipeline independently.
          </div>
        </div>
      )}

      {/* P7J-RUN-017 — data-section anchor lets the failed
        * "at_least_one_skill" gate row scroll the Skills card into view. */}
      <Card padding="none" data-section="skills">
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
          <h3 className="text-[14px] font-bold tracking-tight m-0">Skills · {skills.length}</h3>
          <span className="text-[11px] text-v2-ink-muted">{isRunbookManaged
            ? `Generated from the ${srcNoun} · ${srcRevise} to change them`
            : 'Author, test, and evaluate the building blocks of this Agentlet.'}</span>
          <div className="ml-auto flex items-center gap-2">
            {/* G82 — hide [Import from connector] + [+ New skill] when
              * the agent is runbook-managed. The user uploads a new
              * runbook to add skills; manual addition is forbidden. */}
            {!isRunbookManaged && (
              <>
                {/* P7J-RUN-037 — Skill Unification Phase 3b. Discover
                  * capabilities lives in the Skills card now (the
                  * canonical inventory), gated on archetype=ExteLet +
                  * bound platform connector. Same handler the standalone
                  * Skill Mappings card uses; identical write semantics
                  * (registration.skill_mappings JSONB + DISCOVERED
                  * registry rows via Phase 2 dual-write). The standalone
                  * card still renders below with a deprecation banner
                  * pointing operators here — both surfaces coexist for
                  * one release cycle so no data flow breaks. Phase 4
                  * removes the standalone card. */}
                {(agent?.archetype === 'ExteLet' || agent?.archetype === 'extelet') && registration?.platformConnectorId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<Search size={13} />}
                    onClick={handleDiscoverInline}
                    disabled={discoveryRunning}
                    loading={discoveryRunning}
                    title="Probe the bound platform connector for tools / functions / capabilities this ExteLet advertises">
                    {discoveryRunning ? 'Discovering…' : 'Discover capabilities'}
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setImportModalOpen(true)}>Import from connector</Button>
                {/* Step 5 (Platform-Eng workflow) — install a skill from
                  * the tenant's shared registry. Independent of the
                  * "New skill" menu below; the registry path is for
                  * shared catalog reuse, the new-skill path is for
                  * fresh authoring. Both stay available. */}
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<Plus size={13} />}
                  onClick={() => setAddExistingOpen(true)}
                  title="Pick a skill from the tenant's shared registry and install it on this agent">
                  Add existing skill
                </Button>
                {/* Per V2 redesign (2026-05-29): the "+ New skill" header
                  * button opens the SkillCreateSlideout directly so its
                  * Level 1 type picker (the SAME 5 cards: standard /
                  * workflow / connector / policy / wasm) renders inside
                  * the slideout — consistent with the empty-state button
                  * below (line ~3685). The previous MenuDropdown was a
                  * redundant pre-pick surface; users expected the
                  * slideout to be the single entry. Type-skip semantics
                  * are preserved — callers that want to pre-pick (e.g.
                  * runbook hints) still pass a string to onAddSkill. */}
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft={<Plus />}
                  onClick={() => onAddSkill()}
                >
                  New skill
                </Button>
              </>
            )}
            {isRunbookManaged && (
              isWorkflowSourced ? (
                /* Workflow-sourced AutoLet: changes are made by publishing a
                 * revised version of the SOURCE WORKFLOW, not by uploading a
                 * runbook. Send the user to the source workflow canvas. The
                 * "Upload revised runbook" affordance below is runbook-only. */
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<ExternalLink size={13} />}
                  onClick={() => navigate(`/canvas?workflowId=${agent.sourceWorkflowId}`)}
                  title="Open the source workflow to publish a revised version — that regenerates this AutoLet">
                  Open source workflow
                </Button>
              ) : (
                <>
                  <input
                    ref={runbookFileRef}
                    type="file"
                    accept=".md,.txt,.pdf,.docx,.json"
                    style={{ display: 'none' }}
                    onChange={handleRunbookFileChange}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft={<Plus size={13} />}
                    loading={runbookUploading}
                    onClick={handleRunbookUploadClick}
                    title="Upload a new source-document revision to regenerate this AutoLet">
                    Upload revised source
                  </Button>
                </>
              )
            )}
          </div>
        </div>

        {skills.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={<SparklesIcon size={20} />}
              title="No skills yet"
              description="Add your first skill to start the Build phase. Each skill is the smallest enforceable unit of capability for this Agentlet."
              primaryAction={<Button variant="primary" iconLeft={<Plus />} onClick={onAddSkill}>New skill</Button>}
            />
          </div>
        ) : (
          <div className="grid gap-3 p-4" data-section="skill-cards"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
            {/* #66 — V1-parity 2×2 skill cards on the native Build surface.
              * Shared <SkillPromotionCard> (same card + BE as ExteLet): it owns
              * advance/regress/delete/push + the locked-phase footer. Native-only
              * affordances (Configure=edit, eval generate/view, wasm Invoke) are
              * threaded in via onConfigure + renderEval so NOTHING regresses. */}
            {skills.map((s) => {
              const sInBuild = (s.skillPhase || 'build').toLowerCase() === 'build';
              return (
                <SkillPromotionCard
                  key={s.id || s.name}
                  agentId={id}
                  skill={s}
                  surface="build"
                  canMutate={!isRunbookManaged}
                  onChanged={reloadAgent}
                  onConfigure={(!isRunbookManaged && sInBuild) ? () => openEditSkill(s) : undefined}
                  renderEval={(sk) => (
                    <div className="flex flex-col gap-2">
                      <p className="text-[12px] text-v2-ink-muted m-0">
                        Generate or review the eval suite for <strong>{sk.name}</strong>. A passing eval
                        (≥ 70%) clears the Build → Decide gate.
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {(typeof sk.evalScore === 'number' && sk.evalScore > 0) && (
                          <Button variant="secondary" size="sm" onClick={() => openExistingEvalForSkill(sk)}>
                            View evals
                          </Button>
                        )}
                        {((sk.skillPhase || 'build').toLowerCase() === 'build') && (
                          <Button variant="primary" size="sm" onClick={() => openGenerateEval(sk)}>
                            {typeof sk.evalScore === 'number' && sk.evalScore > 0 ? 'Re-generate eval' : 'Generate eval'}
                          </Button>
                        )}
                        {sk.skillType === 'wasm_skill' && (
                          <Button variant="ghost" size="sm" onClick={() => setInvokingSkill(sk)}>Invoke…</Button>
                        )}
                      </div>
                    </div>
                  )}
                  onEval={(sk) => {
                    // One-slideout rule: when the only available eval action is
                    // "Generate eval" (no suite yet, non-wasm, Build phase), open the
                    // generate-config slideout DIRECTLY instead of first showing the
                    // card's intermediate Eval slideout whose sole button just opens
                    // it — a zero-value extra click. Returning true tells the card to
                    // skip its own slideout. Other cases (existing eval → View /
                    // Re-generate hub; wasm → Invoke) return false and keep the card's
                    // hub slideout unchanged, so nothing regresses.
                    const hasEval = typeof sk.evalScore === 'number' && sk.evalScore > 0;
                    const isWasm = sk.skillType === 'wasm_skill';
                    const inBuild = (sk.skillPhase || 'build').toLowerCase() === 'build';
                    if (!hasEval && !isWasm && inBuild) { openGenerateEval(sk); return true; }
                    return false;
                  }}
                />
              );
            })}
          </div>
        )}
      </Card>

      {/* DEF-LLC-POLICYDOC-DUP-001 — Removed the standalone "Policy documents
        * (RAG)" card. It was a V2-only "coming soon" stub (not a V1 surface):
        * V1 attaches docs per policy_skill from the skill detail panel, which
        * V2 also does via SkillLinkedResourcePanel → onRequestPolicyUpload.
        * The duplicate agent-level card just pointed users back to that flow,
        * so it was redundant + confusing. The upload modal itself is retained
        * (the per-skill panel opens it with the skill pre-selected). */}
      {/* G171 — Import-from-connector modal */}
      <ImportFromConnectorModalV2
        isOpen={importModalOpen}
        agentId={id}
        agentName={agent.name}
        onClose={() => setImportModalOpen(false)}
        onImported={handleImported}
      />

      {/* Step 6 (Platform-Eng workflow) — structured invocation form.
        * Opens via the per-skill "Run…" button on the BuildTab. The
        * slideout owns its own form state so it survives close/reopen
        * cleanly. Mount here next to the edit/delete modals so the
        * skill-mutation surfaces all live in one place. */}
      <SkillInvocationForm
        isOpen={!!invokingSkill}
        onClose={() => setInvokingSkill(null)}
        agentId={id}
        skill={invokingSkill}
      />

      {/* Step 5 (Platform-Eng workflow) — install an existing skill
        * from the tenant's shared registry catalog. onInstalled fires
        * after a successful install so we reload the agent and the
        * new Skill row appears in the BuildTab table immediately. */}
      <AddExistingSkillSlideout
        isOpen={addExistingOpen}
        onClose={() => setAddExistingOpen(false)}
        agentId={id}
        agentName={agent?.name}
        onInstalled={async () => {
          try { await reloadAgent(); } catch (_e) { /* best-effort */ }
        }}
      />

      {/* G192 — Per-skill Edit modal. Six fields mapping to the Skill model
       * (name, description, category, riskLevel, autonomy, inferenceMode).
       * Saves via api.editSkill which targets PUT /api/v1/agents/{id}/skills/{sid}. */}
      {/* Phase 5B — Edit skill: Modal → Slideout per G378. 6 fields
        * (name / description / category / riskLevel / autonomy /
        * inferenceMode) — slideout-shaped. */}
      <Slideout
        isOpen={!!editingSkill}
        onClose={editSaving ? undefined : () => setEditingSkill(null)}
        width={620}
        ariaLabel={editingSkill ? `Edit skill — ${editingSkill.name}` : 'Edit skill'}
        header={
          <h2 className="text-[15px] font-bold m-0 truncate">
            {editingSkill ? `Edit skill — ${editingSkill.name}` : 'Edit skill'}
          </h2>
        }
        footer={
          <div className="flex items-center justify-end gap-2 w-full">
            <Button variant="ghost" onClick={() => setEditingSkill(null)} disabled={editSaving}>Cancel</Button>
            <Button variant="primary" onClick={handleSaveSkillEdit} loading={editSaving}>
              {editSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        }
      >
        {editingSkill && (
          <div className="space-y-3 text-[12.5px]">
            <div>
              <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">Name</label>
              <input
                type="text"
                value={editSkillForm.name || ''}
                onChange={(e) => setEditSkillForm({ ...editSkillForm, name: e.target.value })}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px]"
                disabled={editSaving}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">Description</label>
              <textarea
                value={editSkillForm.description || ''}
                onChange={(e) => setEditSkillForm({ ...editSkillForm, description: e.target.value })}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px] resize-none"
                rows={3}
                disabled={editSaving}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">Category</label>
                <input
                  type="text"
                  value={editSkillForm.category || ''}
                  onChange={(e) => setEditSkillForm({ ...editSkillForm, category: e.target.value })}
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px]"
                  disabled={editSaving}
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">Risk level</label>
                <select
                  value={editSkillForm.riskLevel || 'medium'}
                  onChange={(e) => setEditSkillForm({ ...editSkillForm, riskLevel: e.target.value })}
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px]"
                  disabled={editSaving}
                >
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                  <option value="critical">critical</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">Autonomy</label>
                <select
                  value={editSkillForm.autonomy || 'HUMAN_CONTROLLED'}
                  onChange={(e) => setEditSkillForm({ ...editSkillForm, autonomy: e.target.value })}
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px]"
                  disabled={editSaving}
                >
                  {/* DEF-LLC-VOCAB-001 — canonical skill-autonomy options from the
                    * shared constants/autonomy module (same list create uses). A
                    * defensive "current" option preserves any unrecognised legacy
                    * value so it displays + round-trips without silent change. */}
                  {editSkillForm.autonomy
                    && !SKILL_AUTONOMY_OPTIONS.some((o) => o.value === editSkillForm.autonomy) && (
                    <option value={editSkillForm.autonomy}>{editSkillForm.autonomy} (current)</option>
                  )}
                  {SKILL_AUTONOMY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">Inference mode</label>
                <select
                  value={editSkillForm.inferenceMode || 'PLATFORM_INFERENCE'}
                  onChange={(e) => setEditSkillForm({ ...editSkillForm, inferenceMode: e.target.value })}
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px]"
                  disabled={editSaving}
                >
                  {/* Canonical inference vocabulary, matching the create slideout. */}
                  {editSkillForm.inferenceMode
                    && !['PLATFORM_INFERENCE', 'SELF_INFERENCE', 'HUMAN_APPROVAL'].includes(editSkillForm.inferenceMode) && (
                    <option value={editSkillForm.inferenceMode}>{editSkillForm.inferenceMode} (current)</option>
                  )}
                  <option value="PLATFORM_INFERENCE">Platform inference</option>
                  <option value="SELF_INFERENCE">Self inference</option>
                  <option value="HUMAN_APPROVAL">Human approval</option>
                </select>
              </div>
            </div>

            {/* Per-kind post-create editor surface. Each kind shows
              * the editor (wasm) or read-only linked-resource panel
              * (workflow / connector / policy) appropriate to its
              * binding shape. standard skills have no linked resource
              * and render nothing here. */}
            {(editingSkill.skillType === 'workflow_skill'
              || editingSkill.skillType === 'connector_skill'
              || editingSkill.skillType === 'policy_skill') && (
              <div className="mt-3 pt-3 border-t border-v2-border-subtle">
                <SkillLinkedResourcePanel
                  skill={editingSkill}
                  agentId={id}
                  /* RC1 — external (ExteLet) policy skills must not be routed to
                   * the non-materializing /policy-config wizard; the panel hides
                   * those links when isExternal. NATIVE keeps /policy-config. */
                  isExternal={archetype === 'ExteLet'}
                  reloadToken={policyDocReloadToken}
                  onRequestPolicyUpload={(s) => {
                    /* Pre-select the skill the operator is currently
                     * editing so the existing modal doesn't default
                     * to policySkills[0] and force a re-pick. */
                    setPolicyDocError(null);
                    setPolicyDocFile(null);
                    setPolicyDocEnforcement('advisory');
                    setPolicyDocSkillId(s.id);
                    setPolicyDocOpen(true);
                  }}
                />
              </div>
            )}

            {/* P0-2 — Wasm sandbox uploader. Mounts only for
              * wasm_skill rows; the existing skill metadata fields
              * above edit identically across every kind. The uploader
              * needs an existing skillId (the .wasm binary attaches
              * to a saved row), which the edit modal always satisfies. */}
            {editingSkill.skillType === 'wasm_skill' && (
              <div className="mt-3 pt-3 border-t border-v2-border-subtle">
                <WasmSkillUploader
                  agentId={id}
                  skillId={editingSkill.id}
                  currentBlob={editingSkill.wasmBlob}
                  defaults={{
                    entryFunction:   editingSkill.wasmEntryFunction,
                    maxMemoryMb:     editingSkill.wasmMaxMemoryMb,
                    maxExecutionMs:  editingSkill.wasmMaxExecutionMs,
                  }}
                  onUploaded={(saved) => {
                    /* Keep the edit form in sync so the next Save
                     * doesn't clobber the just-uploaded fields. */
                    setEditingSkill((prev) => ({ ...prev, ...saved }));
                  }}
                />
              </div>
            )}

            {/* G264 / AGENT_EXECUTION_MODEL §2.2 — Execution-logic editor.
              * These fields ARE honoured at runtime on the cognitive path
              * (CognitiveExecutionActivitiesImpl reads callability / preconditions
              * / timeout / retry; RateLimitService + IdempotencyCacheService read
              * the rest) and are now persisted on edit too (DEF-LLC-EDIT-001).
              * They only take effect when the Agentlet runs in cognitive/hybrid
              * mode — surfaced below for deterministic agents so the operator
              * isn't authoring inert config. */}
            <details className="mt-2 border-t border-v2-border-subtle pt-3" open>
              <summary className="cursor-pointer text-[11.5px] font-semibold text-v2-ink-secondary mb-2 select-none">
                Execution logic <span className="text-v2-ink-muted font-normal">(cognitive runtime overrides)</span>
              </summary>
              {agent?.executionMode && agent.executionMode !== 'cognitive' && agent.executionMode !== 'hybrid' && (
                <div className="text-[11px] text-v2-warning-strong bg-v2-warning-soft border border-v2-warning-border rounded-v2-sm px-2.5 py-1.5 mb-2">
                  This Agentlet runs in <strong>{agent.executionMode}</strong> mode. These overrides only take effect for
                  <strong> cognitive</strong> / <strong>hybrid</strong> agents, so they won't change runtime behaviour here.
                  Switch execution mode from the Canvas banner to use them.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">Callability</label>
                  <select
                    value={editSkillForm.callability || 'always'}
                    onChange={(e) => setEditSkillForm({ ...editSkillForm, callability: e.target.value })}
                    className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px]"
                    disabled={editSaving}>
                    <option value="always">always — LLM may pick anytime</option>
                    <option value="guarded">guarded — preconditions must pass</option>
                    <option value="startup">startup — runs once at start</option>
                    <option value="manual">manual — human-triggered only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">Parallel group</label>
                  <input
                    type="text"
                    value={editSkillForm.parallelGroup || ''}
                    onChange={(e) => setEditSkillForm({ ...editSkillForm, parallelGroup: e.target.value })}
                    placeholder="e.g. analysis (empty = serialised)"
                    className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px]"
                    disabled={editSaving}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">Timeout (ms)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editSkillForm.timeoutMs || ''}
                    onChange={(e) => setEditSkillForm({ ...editSkillForm, timeoutMs: e.target.value })}
                    placeholder="30000"
                    className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px]"
                    disabled={editSaving}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">Idempotency key template</label>
                  <input
                    type="text"
                    value={editSkillForm.idempotencyKeyTemplate || ''}
                    onChange={(e) => setEditSkillForm({ ...editSkillForm, idempotencyKeyTemplate: e.target.value })}
                    placeholder="{user.id}-{intent.hash}"
                    className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[12.5px] font-mono-v2"
                    disabled={editSaving}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">
                    Preconditions <span className="text-v2-ink-muted font-normal normal-case">(JSON array of guard expressions)</span>
                  </label>
                  <textarea
                    value={editSkillForm.preconditionsText || ''}
                    onChange={(e) => setEditSkillForm({ ...editSkillForm, preconditionsText: e.target.value })}
                    placeholder={'[\n  "skill_X.last_outcome === \'success\'",\n  "context.has_pii === false"\n]'}
                    rows={3}
                    className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[11.5px] font-mono-v2 resize-y"
                    disabled={editSaving}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">
                    Retry policy <span className="text-v2-ink-muted font-normal normal-case">(JSON)</span>
                  </label>
                  <textarea
                    value={editSkillForm.retryPolicyText || ''}
                    onChange={(e) => setEditSkillForm({ ...editSkillForm, retryPolicyText: e.target.value })}
                    placeholder={'{ "maxAttempts": 3, "backoffMs": 1000 }'}
                    rows={3}
                    className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[11.5px] font-mono-v2 resize-y"
                    disabled={editSaving}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">
                    Rate limits <span className="text-v2-ink-muted font-normal normal-case">(JSON)</span>
                  </label>
                  <textarea
                    value={editSkillForm.rateLimitsText || ''}
                    onChange={(e) => setEditSkillForm({ ...editSkillForm, rateLimitsText: e.target.value })}
                    placeholder={'{ "perMinute": 60, "perAgent": 1000 }'}
                    rows={3}
                    className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-1.5 text-[11.5px] font-mono-v2 resize-y"
                    disabled={editSaving}
                  />
                </div>
              </div>
            </details>

            {editError && (
              <div className="rounded-v2-sm border border-v2-danger-border bg-v2-danger-soft px-3 py-2 text-[12px] text-v2-danger">
                {editError}
              </div>
            )}
          </div>
        )}
      </Slideout>

      {/* G196 — Policy-document upload modal. */}
      <Modal
        isOpen={policyDocOpen}
        onClose={policyDocUploading ? undefined : () => setPolicyDocOpen(false)}
        size="md"
        title="Upload policy document"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPolicyDocOpen(false)} disabled={policyDocUploading}>Cancel</Button>
            <Button variant="primary" onClick={handlePolicyDocSubmit} loading={policyDocUploading}
              disabled={policySkills.length === 0}>
              {policyDocUploading ? 'Uploading…' : 'Upload + index'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-[12.5px]">
          {policySkills.length === 0 ? (
            <div className="rounded-v2-sm border border-v2-warning-border bg-v2-warning-soft px-3 py-2 text-v2-warning">
              This Agentlet has no <strong>policy_skill</strong> type skills yet. Create one from
              the "New skill → Policy skill" menu in the skills toolbar before uploading documents — each
              document must be attached to a specific policy skill's RAG corpus.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Attach to policy skill</label>
                <select
                  value={policyDocSkillId}
                  onChange={(e) => setPolicyDocSkillId(e.target.value)}
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2 py-1.5 text-[12.5px]"
                  disabled={policyDocUploading}>
                  {policySkills.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Enforcement</label>
                <select
                  value={policyDocEnforcement}
                  onChange={(e) => setPolicyDocEnforcement(e.target.value)}
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2 py-1.5 text-[12.5px]"
                  disabled={policyDocUploading}>
                  <option value="advisory">advisory — surface as hint in retrieval</option>
                  <option value="recommended">recommended — bias generation toward this doc</option>
                  <option value="enforced">enforced — block on violation</option>
                </select>
              </div>
              <div>
                <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">File</label>
                <input
                  type="file"
                  accept=".md,.txt,.pdf,.docx,.html,.json"
                  onChange={(e) => setPolicyDocFile(e.target.files?.[0] || null)}
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2 py-1.5 text-[12.5px]"
                  disabled={policyDocUploading}
                />
                {policyDocFile && (
                  <div className="text-[11px] text-v2-ink-muted mt-1">
                    {policyDocFile.name} · {Math.round(policyDocFile.size / 1024)} KB
                  </div>
                )}
              </div>
            </>
          )}
          {policyDocError && (
            <div className="rounded-v2-sm border border-v2-danger-border bg-v2-danger-soft px-3 py-2 text-[12px] text-v2-danger">
              {policyDocError}
            </div>
          )}
        </div>
      </Modal>

      {/* G192 — Per-skill Delete confirm modal. */}
      <Modal
        isOpen={!!deletingSkill}
        onClose={deleteSaving ? undefined : () => setDeletingSkill(null)}
        size="sm"
        title="Delete skill?"
        dismissOnBackdrop={false}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeletingSkill(null)} disabled={deleteSaving}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirmDeleteSkill} loading={deleteSaving}>
              {deleteSaving ? 'Deleting…' : 'Delete skill'}
            </Button>
          </>
        }
      >
        {deletingSkill && (
          <div className="text-[12.5px] text-v2-ink leading-relaxed">
            Permanently delete <strong>{deletingSkill.name}</strong>?
            {deletingSkill.pushed && (
              <div className="mt-2 text-v2-warning">
                This skill is currently published. Unpublish it first if you want to revoke without removing.
              </div>
            )}
            <div className="text-v2-ink-muted text-[11.5px] mt-2">
              A record of this deletion will be preserved in the audit trail. Any phase advancement that depended
              on this skill being eval-ready will lose that signal.
            </div>
          </div>
        )}
      </Modal>

      {/* G248 — V2 Slideout per UI/UX redesign. Was a Modal in G245 (wrong
        * — every other detail surface in V2 uses Slideout). Width 520 matches
        * ConnectorsPageV2 / AgentsInventoryPageV2 conventions. */}
      <Slideout
        isOpen={!!pickerSkill}
        onClose={generating ? () => {} : () => setPickerSkill(null)}
        width={520}
        ariaLabel={pickerSkill ? `Generate eval suite for ${pickerSkill.name}` : 'Generate eval suite'}
        header={pickerSkill && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Pill tone="cyan" size="sm">Build phase</Pill>
              <span className="text-[10.5px] text-v2-ink-muted">{pickerSkill.skillType || pickerSkill.category || 'standard'}</span>
            </div>
            <h2 className="text-[16px] font-bold tracking-tight m-0">Generate eval suite</h2>
            <p className="text-[12px] text-v2-ink-secondary m-0">{pickerSkill.name}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={handleGenerateSubmit}
                loading={generating}
                disabled={generating || (selectedProvider !== '__platform__' && !selectedModel)}
                iconLeft={<FlaskConical size={13} />}>
                {generating ? 'Generating…' : 'Generate test cases'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setPickerSkill(null)} disabled={generating}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      >
        {pickerSkill && (
          <div className="space-y-4 text-[12.5px]">
            {/* Info banner */}
            <div className="rounded-v2-md border border-v2-cyan-border bg-v2-cyan-soft px-3 py-2.5">
              <div className="font-semibold text-v2-cyan">Context-sensitive eval generation</div>
              <div className="text-v2-ink-secondary mt-0.5">
                The selected LLM analyses the skill's metadata, guardrails, and connector config to
                produce tailored test cases (functional, negative, security, guardrail, performance).
              </div>
            </div>

            {/* Provider picker */}
            <div>
              <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-2">
                LLM provider
              </label>
              <div className="space-y-2">
                {llmProviders.map((p) => {
                  const isSelected = selectedProvider === p.id;
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-v2-md cursor-pointer border transition-colors ${
                        isSelected
                          ? 'border-v2-accent bg-v2-accent-soft'
                          : 'border-v2-border hover:bg-v2-muted'
                      }`}>
                      <input
                        type="radio"
                        name="llmProvider"
                        checked={isSelected}
                        onChange={() => {
                          setSelectedProvider(p.id);
                          setSelectedModel(p.models?.[0]?.id || null);
                        }}
                      />
                      <span className="text-[16px]">{p.icon}</span>
                      <div className="flex-1">
                        <div className="font-semibold text-v2-ink">{p.name}</div>
                        <div className="text-[11px] text-v2-ink-muted">
                          {p.providerType === 'platform'
                            ? 'agentlets-eval-v1 · built-in'
                            : `${p.models?.length || 0} model(s) · connected`}
                        </div>
                      </div>
                      <Pill tone={p.providerType === 'platform' ? 'cyan' : 'success'} size="sm">
                        {p.providerType === 'platform' ? 'BUILT-IN' : 'CONNECTED'}
                      </Pill>
                    </label>
                  );
                })}
              </div>
              {llmProviders.length <= 1 && (
                <div className="mt-2 rounded-v2-sm border border-v2-warning-border bg-v2-warning-soft px-3 py-2 text-[11px] text-v2-ink-secondary">
                  No external LLM connectors connected. Add an Anthropic / OpenAI / Gemini / Azure
                  connector on the Connectors page to use external models, or proceed with the platform default.
                </div>
              )}
            </div>

            {/* Model picker — only for non-platform providers with models */}
            {(() => {
              const provider = llmProviders.find((p) => p.id === selectedProvider);
              if (!provider || provider.providerType === 'platform' || !provider.models?.length) return null;
              return (
                <div>
                  <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-2">
                    Model
                  </label>
                  <div className="space-y-1.5">
                    {provider.models.map((m) => {
                      const isSelected = selectedModel === m.id;
                      return (
                        <label
                          key={m.id}
                          className={`flex items-center gap-3 px-3 py-2 rounded-v2-sm cursor-pointer border transition-colors ${
                            isSelected
                              ? 'border-v2-accent bg-v2-accent-soft'
                              : 'border-v2-border hover:bg-v2-muted'
                          }`}>
                          <input
                            type="radio"
                            name="llmModel"
                            checked={isSelected}
                            onChange={() => setSelectedModel(m.id)}
                          />
                          <div className="flex-1">
                            <div className="font-semibold text-v2-ink">{m.name || m.id}</div>
                            <div className="text-[10.5px] text-v2-ink-muted">
                              {m.id}{m.context ? ` · ${m.context} context` : ''}
                            </div>
                          </div>
                          {m.tier && <Pill tone="muted" size="sm">{m.tier}</Pill>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Generation preview */}
            <div className="rounded-v2-md border border-v2-border-subtle bg-v2-muted px-3 py-2.5 text-[11.5px] text-v2-ink-secondary leading-relaxed">
              <div className="font-semibold text-v2-ink mb-1">Generation will include:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Functional tests (happy-path + boundary)</li>
                <li>Negative tests (invalid input + unauthorized access)</li>
                <li>Security tests (injection defence — high / critical risk only)</li>
                <li>Guardrail enforcement tests (if guardrails configured)</li>
                <li>Performance tests (SLA threshold compliance)</li>
                <li>Category-specific tests (based on skill type)</li>
              </ul>
            </div>
          </div>
        )}
      </Slideout>

      {/* G248 — V2 Slideout per UI/UX redesign. Was a Modal in G245 (wrong).
        * Wider than the picker (640px) because of the 5 KPI cards row.
        * Header carries the primary actions (Run / Re-run / Delete / Close)
        * since Slideout has no footer prop. */}
      <Slideout
        isOpen={!!evalDetailSuite}
        onClose={runningEval || deletingEval ? () => {} : () => { setEvalDetailSuite(null); setDeleteConfirm(false); }}
        width={640}
        ariaLabel={evalDetailSuite ? `Eval suite ${evalDetailSuite.name}` : 'Eval suite'}
        header={evalDetailSuite && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Pill
                tone={evalDetailSuite.status === 'completed' ? 'success'
                  : evalDetailSuite.status === 'failed' ? 'danger'
                  : evalDetailSuite.status === 'archived' ? 'muted' : 'cyan'}
                size="sm">
                {evalDetailSuite.status || 'created'}
              </Pill>
              {evalDetailSuite.llmProvider && (
                <span className="text-[10.5px] text-v2-ink-muted">
                  {evalDetailSuite.llmProvider}{evalDetailSuite.llmModel ? ` · ${evalDetailSuite.llmModel}` : ''}
                </span>
              )}
            </div>
            <h2 className="text-[16px] font-bold tracking-tight m-0 pr-8 leading-tight">
              {evalDetailSuite.name || 'Eval suite'}
            </h2>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {/* PROVE7-JOURNEY sweep 2026-05-25 — Eval re-runs invoke the
                * skill which a Decide/Act skill cannot accept new evals for
                * (skills are immutable past Build per backend rule). The
                * button used to be enabled-then-rejected by a 409 toast;
                * lock it here too so the rule is visible up-front. */}
              {evalDetailSuite.status !== 'archived' && (() => {
                const phase = (skills.find((sk) => sk.id === evalDetailSuite?.skillId)?.skillPhase || 'build').toLowerCase();
                const skillIsInBuild = phase === 'build';
                return (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleRunInDetail}
                    loading={runningEval}
                    disabled={runningEval || deletingEval || !skillIsInBuild}
                    title={!skillIsInBuild
                      ? `Skill is in ${phase} phase. Regress to Build to re-run the eval.`
                      : (evalDetailSuite.status === 'completed' ? 'Re-run the eval suite — overwrites previous score' : 'Run the eval suite — populates score + fail/pass breakdown')}>
                    {runningEval ? 'Running…' : (evalDetailSuite.status === 'completed' ? 'Re-run eval' : 'Run eval')}
                  </Button>
                );
              })()}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setEvalDetailSuite(null); setDeleteConfirm(false); }}
                disabled={runningEval || deletingEval}>
                Close
              </Button>
              <div className="flex-1" />
              {!deleteConfirm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="!text-v2-danger hover:!bg-v2-danger-soft"
                  onClick={() => setDeleteConfirm(true)}
                  disabled={runningEval || deletingEval}>
                  Delete suite
                </Button>
              )}
              {deleteConfirm && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(false)} disabled={deletingEval}>
                    Cancel
                  </Button>
                  <Button variant="danger" size="sm" onClick={handleDeleteInDetail} loading={deletingEval}>
                    {deletingEval ? 'Deleting…' : 'Confirm delete'}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      >
        {evalDetailSuite && (
          <div className="space-y-4 text-[12.5px]">
            {/* 5 KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {(() => {
                const score = evalDetailSuite.score;
                const pct = (score != null && Number.isFinite(Number(score)))
                  ? `${Math.round(Number(score) * (Number(score) <= 1 ? 100 : 1))}%` : '—';
                const passed = evalDetailSuite.passedTests ?? 0;
                const total = evalDetailSuite.totalTests ?? evalDetailTestCases.length ?? 0;
                const failed = Math.max(0, total - passed);
                const cards = [
                  { label: 'Score',  value: pct },
                  { label: 'Status', value: evalDetailSuite.status || '—' },
                  { label: 'Passed', value: String(passed) },
                  { label: 'Failed', value: String(failed) },
                  { label: 'Total',  value: String(total) },
                ];
                return cards.map((c) => (
                  <div key={c.label}
                       className="rounded-v2-md border border-v2-border-subtle bg-v2-surface px-3 py-2 text-center">
                    <div className="text-[9px] uppercase tracking-wider text-v2-ink-muted">{c.label}</div>
                    <div className="text-[18px] font-bold text-v2-ink mt-0.5">{c.value}</div>
                  </div>
                ));
              })()}
            </div>

            {/* (LLM provider info now lives in the slideout header — no duplicate.) */}

            {/* Phase readiness banner */}
            {(() => {
              const raw = evalDetailSuite.score;
              const score01 = (raw == null) ? null : (Number(raw) <= 1 ? Number(raw) : Number(raw) / 100);
              const isCompleted = evalDetailSuite.status === 'completed';
              const ready = isCompleted && score01 != null && score01 >= 0.7;
              const meta = ready
                ? {
                    title: 'Ready for phase promotion',
                    body: score01 >= 0.9
                      ? `Score ${Math.round(score01 * 100)}% — meets the autonomous threshold (≥ 90%).`
                      : `Score ${Math.round(score01 * 100)}% — passes minimum (70%) but below 90% for autonomous operation. Re-run after improvements.`,
                    tone: 'success',
                  }
                : score01 == null
                  ? {
                      title: 'Awaiting first run',
                      body: 'Click Run Eval below to execute every test case and produce a score.',
                      tone: 'muted',
                    }
                  : {
                      title: 'Not ready — improvements needed',
                      body: `Score ${Math.round(score01 * 100)}% is below the 70% minimum. Review failed tests below and re-run.`,
                      tone: 'danger',
                    };
              const palette = meta.tone === 'success'
                ? 'border-v2-success-border bg-v2-success-soft text-v2-success'
                : meta.tone === 'danger'
                  ? 'border-v2-danger-border bg-v2-danger-soft text-v2-danger'
                  : 'border-v2-border-subtle bg-v2-muted text-v2-ink-secondary';
              return (
                <div className={`rounded-v2-md border px-3 py-2.5 ${palette}`}>
                  <div className="font-semibold">{meta.title}</div>
                  <div className="text-[11.5px] text-v2-ink-secondary mt-0.5">{meta.body}</div>
                </div>
              );
            })()}

            {/* Test cases */}
            <div>
              <div className="text-[13px] font-semibold text-v2-ink mb-2 flex items-center gap-2">
                <FlaskConical size={14} /> Test cases ({evalDetailTestCases.length})
              </div>
              {evalDetailLoading ? (
                <div className="text-center py-6 text-v2-ink-muted text-[11.5px]">Loading test cases…</div>
              ) : evalDetailTestCases.length === 0 ? (
                <div className="rounded-v2-md border border-v2-border-subtle bg-v2-muted px-3 py-3 text-v2-ink-muted text-[11.5px] italic">
                  No test cases on this suite yet. If the suite was just generated, refresh after a few seconds.
                </div>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    // Resolve the skill the suite belongs to so per-test Quick
                    // Fix panels can read current config values. Memo not
                    // needed — skills are already a useMemo-ed prop.
                    const targetSkill = skills.find((s) => s.id === evalDetailSuite?.skillId) || null;
                    return evalDetailTestCases.map((tc, idx) => {
                      const actual = tc.actualOutput || {};
                      const status = tc.status || 'pending';
                      const palette = status === 'passed'
                        ? 'border-v2-success-border bg-v2-success-soft'
                        : status === 'failed'
                          ? 'border-v2-danger-border bg-v2-danger-soft'
                          : 'border-v2-border-subtle bg-v2-muted';
                      const quickFix = computeQuickFix(tc, targetSkill);
                      const qfInputId = `qf-${tc.id || idx}-${quickFix?.field || ''}`;
                      return (
                      <div key={tc.id || idx} className={`rounded-v2-md border px-3 py-2 ${palette}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Pill tone={status === 'passed' ? 'success' : status === 'failed' ? 'danger' : 'muted'} size="sm">
                            {status}
                          </Pill>
                          {tc.testType && <Pill tone="muted" size="sm">{tc.testType}</Pill>}
                          {tc.severity && <span className="text-[10px] text-v2-ink-muted">severity: {tc.severity}</span>}
                        </div>
                        <div className="font-semibold text-v2-ink">{tc.description || `Test #${idx + 1}`}</div>
                        {status === 'failed' && actual.reason && (
                          <div className="mt-1 text-[11.5px] text-v2-danger">
                            <strong>Why it failed:</strong> {actual.reason}
                          </div>
                        )}
                        {status === 'failed' && actual.suggestedFix && (
                          <div className="mt-1 text-[11.5px] text-v2-warning">
                            <strong>Fix:</strong> {actual.suggestedFix}
                          </div>
                        )}

                        {/* G249 — Inline AI Quick Fix panel. Mirrors V1
                          * AgentsPage.jsx:2762-2858. Forbidden on AutoLets
                          * per GOLDEN_CEG_RULES §1 — skills are immutable and
                          * the wizard rebinds via a new runbook version. */}
                        {quickFix && isRunbookManaged && (
                          <div className="mt-2 rounded-v2-sm border border-v2-border-subtle bg-v2-muted px-3 py-2 text-[11px] text-v2-ink-secondary">
                            <strong className="text-v2-ink">This is an AutoLet — Quick Fix isn't available.</strong>{' '}
                            Skills are immutable once created. To change{' '}
                            <code className="px-1 py-0.5 rounded-v2-sm bg-v2-surface border border-v2-border-subtle">{quickFix.label}</code>,
                            {' '}{srcRevise} to create v{((isWorkflowSourced ? agent.sourceWorkflowVersion : agent.runbookVersion) ?? 1) + 1}.
                          </div>
                        )}
                        {/* PROVE7-JOURNEY sweep 2026-05-25 — Quick-fix actions
                          * mutate the skill (inferenceFallback, guardrails,
                          * SLA threshold, description) — all backend-rejected
                          * with 409 on skills past Build. Hide the panel and
                          * surface a small "locked" notice pointing at the
                          * Regress action. Per-test-case "Why it failed" copy
                          * above stays visible since it's read-only context. */}
                        {quickFix && !isRunbookManaged && targetSkill
                          && (targetSkill.skillPhase || 'build').toLowerCase() !== 'build' && (
                          <div className="mt-2 rounded-v2-sm border border-v2-border-subtle bg-v2-muted px-3 py-2 text-[10.5px] text-v2-ink-muted italic">
                            Quick-fix actions are unavailable while this skill is in {targetSkill.skillPhase} phase.
                            Use <strong>↻ Build</strong> in the Skills table to regress, then re-open this suite to apply the fix.
                          </div>
                        )}
                        {quickFix && !isRunbookManaged && targetSkill
                          && (targetSkill.skillPhase || 'build').toLowerCase() === 'build' && (
                          <div className="mt-2 rounded-v2-sm border border-v2-action-soft bg-v2-surface px-3 py-2">
                            <div className="text-[10.5px] text-v2-ink-secondary mb-1">
                              <strong className="text-v2-action-strong">Quick fix:</strong> {quickFix.desc}
                            </div>
                            <div className="text-[10px] text-v2-ink-muted mb-2">
                              Field <code className="px-1 py-0.5 rounded-v2-sm bg-v2-muted">{quickFix.label}</code>
                              {' '}· current{' '}
                              <code className="px-1 py-0.5 rounded-v2-sm bg-v2-danger-soft text-v2-danger">
                                {typeof quickFix.current === 'string' && quickFix.current.length > 60
                                  ? quickFix.current.slice(0, 60) + '…'
                                  : String(quickFix.current)}
                              </code>
                            </div>
                            {quickFix.options && (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {quickFix.options.map((opt) => (
                                  <Button
                                    key={opt}
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleQuickFix(targetSkill, quickFix.field, opt)}>
                                    Set to "{opt.replace(/_/g, ' ')}"
                                  </Button>
                                ))}
                              </div>
                            )}
                            {quickFix.addGuardrail && (
                              <Button
                                variant="secondary"
                                size="sm"
                                iconLeft={<Plus size={11} />}
                                onClick={() => handleAddGuardrail(targetSkill, quickFix.addGuardrail)}>
                                Add "{quickFix.addGuardrail}" guardrail
                              </Button>
                            )}
                            {quickFix.inputType === 'number' && (
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="number"
                                  id={qfInputId}
                                  defaultValue={quickFix.defaultValue}
                                  className="v2-focus-ring w-[100px] bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2 py-1 text-[11.5px]"
                                />
                                <span className="text-[10.5px] text-v2-ink-muted">ms</span>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    const el = document.getElementById(qfInputId);
                                    const v = parseInt(el?.value, 10);
                                    if (Number.isFinite(v) && v > 0) handleQuickFix(targetSkill, quickFix.field, v);
                                  }}>
                                  Apply
                                </Button>
                              </div>
                            )}
                            {quickFix.inputType === 'text' && (
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="text"
                                  id={qfInputId}
                                  placeholder="Enter skill description…"
                                  defaultValue={typeof quickFix.current === 'string' && !quickFix.current.startsWith('(')
                                    ? quickFix.current : ''}
                                  className="v2-focus-ring flex-1 bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2 py-1 text-[11.5px]"
                                />
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    const el = document.getElementById(qfInputId);
                                    const v = el?.value?.trim();
                                    if (v) handleQuickFix(targetSkill, quickFix.field, v);
                                  }}>
                                  Save
                                </Button>
                              </div>
                            )}
                          </div>
                        )}

                        {status === 'passed' && (actual.reason || actual.evaluatedField) && (
                          <div className="mt-1 text-[11px] text-v2-ink-muted">
                            {actual.reason || 'Test passed'}
                            {actual.evaluatedField && <span className="ml-1.5">({actual.evaluatedField})</span>}
                          </div>
                        )}
                        {(tc.input || tc.expectedOutput || tc.actualOutput) && (
                          <details className="mt-1.5">
                            <summary className="text-[10.5px] text-v2-ink-muted cursor-pointer">Show test I/O</summary>
                            <div className="mt-1 space-y-1 text-[10.5px] font-mono-v2 text-v2-ink-secondary">
                              {tc.input && <div><strong>Input:</strong> {JSON.stringify(tc.input)}</div>}
                              {tc.expectedOutput && <div><strong>Expected:</strong> {JSON.stringify(tc.expectedOutput)}</div>}
                              {tc.actualOutput && <div><strong>Actual:</strong> {JSON.stringify(tc.actualOutput)}</div>}
                            </div>
                          </details>
                        )}
                      </div>
                    );
                    });
                  })()}
                </div>
              )}
            </div>

            {/* G248 — AI Recommendations panel (V1 parity, AgentsPage.jsx
              * lines 2927-2950). Reads `recommendations.items` off the most
              * recent run that has them. Collapsed by default so it doesn't
              * dominate the modal. */}
            {(() => {
              const latestRunWithRecs = evalDetailRuns.find((r) => r.recommendations);
              const recs = latestRunWithRecs?.recommendations;
              const items = Array.isArray(recs?.items) ? recs.items
                : Array.isArray(recs) ? recs : [];
              if (items.length === 0) return null;
              const toneFor = (pri) => {
                switch (String(pri || '').toLowerCase()) {
                  case 'critical': return 'danger';
                  case 'high':     return 'warning';
                  case 'medium':   return 'cyan';
                  default:         return 'muted';
                }
              };
              return (
                <details className="rounded-v2-md border border-v2-border-subtle bg-v2-surface" open>
                  <summary className="cursor-pointer px-3 py-2 text-[13px] font-semibold text-v2-ink flex items-center gap-2">
                    <SparklesIcon size={14} /> AI recommendations ({items.length})
                  </summary>
                  <div className="px-3 pb-3 space-y-2">
                    {items.map((rec, i) => (
                      <div key={i} className="rounded-v2-sm border border-v2-border-subtle bg-v2-muted px-3 py-2">
                        <div className="flex items-center gap-2 mb-0.5">
                          <Pill tone={toneFor(rec.priority)} size="sm">{rec.priority || 'info'}</Pill>
                          <span className="font-semibold text-v2-ink text-[12px]">{rec.title}</span>
                        </div>
                        {rec.detail && (
                          <div className="text-[11.5px] text-v2-ink-secondary leading-relaxed">{rec.detail}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              );
            })()}

            {/* Run history */}
            <div>
              <div className="text-[13px] font-semibold text-v2-ink mb-2 flex items-center gap-2">
                <RefreshCw size={14} /> Run history ({evalDetailRuns.length})
              </div>
              {evalDetailRuns.length === 0 ? (
                <div className="text-[11.5px] text-v2-ink-muted italic">
                  No runs yet. Click Run Eval to execute every test case.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {evalDetailRuns.map((run, idx) => {
                    const score01 = run.score == null ? null : (Number(run.score) <= 1 ? Number(run.score) : Number(run.score) / 100);
                    return (
                      <div key={run.id || idx} className="flex items-center gap-3 rounded-v2-md border border-v2-border-subtle bg-v2-surface px-3 py-2">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-[11.5px] ${
                          score01 == null ? 'bg-v2-muted text-v2-ink-muted'
                            : score01 >= 0.9 ? 'bg-v2-success-soft text-v2-success'
                            : score01 >= 0.7 ? 'bg-v2-warning-soft text-v2-warning'
                            : 'bg-v2-danger-soft text-v2-danger'
                        }`}>
                          {score01 != null ? Math.round(score01 * 100) : '—'}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-v2-ink">Run #{evalDetailRuns.length - idx}</div>
                          <div className="text-[10.5px] text-v2-ink-muted">
                            {(run.passedTests ?? 0)}/{run.totalTests ?? 0} passed
                            {run.durationMs ? ` · ${run.durationMs}ms` : ''}
                            {run.completedAt ? ` · ${new Date(run.completedAt).toLocaleString()}` : ''}
                          </div>
                        </div>
                        <Pill tone={run.status === 'completed' ? 'success' : 'muted'} size="sm">{run.status || '—'}</Pill>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {deleteConfirm && (
              <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft px-3 py-2.5 text-[11.5px] text-v2-danger">
                Confirm delete in the header: this permanently removes the suite and all its test cases.
                The skill's evalScore won't change retroactively but the gate will re-evaluate.
              </div>
            )}
          </div>
        )}
      </Slideout>

      {/* G247 — Regress-to-Build confirm. Power-action that resets the
        * skill phase and bumps regressionCount (AgentService.regressSkill). */}
      <Modal
        isOpen={!!regressConfirm}
        onClose={regressingSkillId ? undefined : () => setRegressConfirm(null)}
        size="sm"
        title={regressConfirm ? `Regress ${regressConfirm.name} to Build?` : 'Regress skill'}
        dismissOnBackdrop={false}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRegressConfirm(null)} disabled={!!regressingSkillId}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRegressSkill} loading={!!regressingSkillId}>
              {regressingSkillId ? 'Regressing…' : 'Send to Build'}
            </Button>
          </>
        }
      >
        {regressConfirm && (
          <div className="text-[12.5px] text-v2-ink leading-relaxed">
            This sends <strong>{regressConfirm.name}</strong> from
            <code className="mx-1 px-1.5 py-0.5 rounded-v2-sm bg-v2-muted text-[11.5px]">
              {regressConfirm.skillPhase || 'decide'}
            </code>
            back to <code className="mx-1 px-1.5 py-0.5 rounded-v2-sm bg-v2-muted text-[11.5px]">build</code>.
            The backend bumps <code className="text-[11px]">regressionCount</code> and stamps
            <code className="text-[11px] ml-1">previousPhase</code> so the audit trail records the move.
            <div className="mt-2 text-v2-ink-muted text-[11.5px]">
              To re-advance: generate a new eval, run it, then promote from this row again.
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ── Decide tab (Phase 4b) ───────────────────────────────────
 * Per Design Principles §4 of the AgentsPage analysis: read-only eval
 * results + explicit per-skill decision (autonomous / human_approval /
 * blocked) + Decide → Act gate validation.
 *
 * Decisions are LOCAL state until "Save decisions" persists them via
 * api.saveSkillDecisions (graceful fallback to a toast when the backend
 * call isn't wired). This is the same pattern the v1 page uses.
 */
function DecideTab({ agent, onAdvanceToAct, reloadAgentFromParent }) {
  const skills = agent.skills || [];
  /* G152 — defensive wrapper, same as BuildTab. */
  const toastCtx = useToast();
  const addToast = React.useCallback((arg, type, duration) => {
    if (toastCtx && typeof toastCtx.addToast === 'function') {
      return toastCtx.addToast(arg, type, duration);
    }
    // eslint-disable-next-line no-console
    console.warn('[AgentDetailPageV2/DecideTab] toast unavailable, dropped:', arg);
    return null;
  }, [toastCtx]);
  /* G179 — Need agent id for previewPhaseGate. Other tabs pull it from
   * useParams, do the same here for parity. */
  const { id } = useParams();
  // Canonical review→choice mapping. The backend persists a reviewed skill as
  // reviewStatus ∈ {promoted, reviewed, blocked} and autonomy ∈ {SELF_AUTONOMOUS,
  // HUMAN_CONTROLLED, BLOCKED}. The old seed only matched lowercase 'autonomous' /
  // 'SUPERVISED', so after a reload a genuinely-reviewed skill seeded choice=null
  // and the decision matrix showed "0 of N reviewed" while the banner (which reads
  // reviewStatus) said "all reviewed". Map BOTH sources (+ legacy lowercase) so the
  // matrix pre-populates from the authoritative review state.
  const choiceFromSkill = (s) => {
    const rs = String(s.reviewStatus || '').toLowerCase();
    if (rs === 'promoted') return 'autonomous';
    if (rs === 'reviewed') return 'human_approval';
    if (rs === 'blocked') return 'blocked';
    const a = String(s.autonomy || '').toUpperCase();
    if (a === 'SELF_AUTONOMOUS' || a === 'AUTONOMOUS') return 'autonomous';
    if (a === 'HUMAN_CONTROLLED' || a === 'SUPERVISED' || a === 'HUMAN_APPROVAL') return 'human_approval';
    if (a === 'BLOCKED' || s.blocked === true) return 'blocked';
    return null;
  };
  const [decisions, setDecisions] = useState(() => {
    const initial = {};
    for (const s of skills) {
      initial[s.id || s.name] = {
        choice: choiceFromSkill(s),
        justification: s.decisionJustification || '',
      };
    }
    return initial;
  });
  const [saving, setSaving] = useState(false);

  /* G250 — Per-skill Review state. V1 pattern (AgentsPage.jsx:2077) used a
   * modal opened from a Review button. We render a V2 right-edge Slideout
   * instead — same affordance, V2 design system. */
  const [reviewingSkill, setReviewingSkill] = React.useState(null);
  const [reviewForm, setReviewForm] = React.useState({ decision: '', justification: '' });
  const [reviewSubmitting, setReviewSubmitting] = React.useState(false);
  const [reviewError, setReviewError] = React.useState(null);

  const openReview = React.useCallback((skill) => {
    setReviewError(null);
    setReviewingSkill(skill);
    const existingChoice = decisions[skill.id || skill.name]?.choice || '';
    const existingJust = decisions[skill.id || skill.name]?.justification || '';
    setReviewForm({ decision: existingChoice, justification: existingJust });
  }, [decisions]);

  /* G250 — handleSubmitReview is declared AFTER refreshDecideGate (below)
   * to avoid the temporal-dead-zone error that bricked the previous build.
   * The deps array must be evaluated each render, so any binding it
   * captures has to already be declared at that point. */

  const setChoice = (skillKey, choice) => setDecisions((p) => ({ ...p, [skillKey]: { ...(p[skillKey] || {}), choice } }));
  const setJust   = (skillKey, justification) => setDecisions((p) => ({ ...p, [skillKey]: { ...(p[skillKey] || {}), justification } }));

  // Count reviewed skills by the canonical reviewStatus — the SAME source the
  // "all skills reviewed" banner uses — so the counter and banner can never
  // disagree. (Was derived from the autonomy-seeded decisions map, which missed
  // the persisted autonomy enums and under-counted to 0 after a reload.)
  const reviewedCount = skills.filter((s) => s.reviewStatus && s.reviewStatus !== 'pending_review').length;
  const highRiskUnreviewed = skills.filter((s) =>
    (s.riskLevel === 'high' || s.riskLevel === 'critical') &&
    !decisions[s.id || s.name]?.choice
  );
  /* Fix G48: backend returns eval_score on 0-1 scale (e.g. 0.92) but the
   * Decide gate criterion historically read "≥ 90" assuming a 0-100
   * scale. Normalise both forms to a single 0-100 value here so the
   * threshold and the displayed value finally agree. */
  const normalizeEvalTo100 = (raw) => {
    const v = Number(raw);
    if (!Number.isFinite(v) || v <= 0) return 0;
    return v <= 1 ? v * 100 : v;
  };
  const autonomousScored = skills.filter((s) => decisions[s.id || s.name]?.choice === 'autonomous');
  const lowScoreAutonomous = autonomousScored.filter((s) => normalizeEvalTo100(s.evalScore) < 90);
  const someExecutable = skills.some((s) => decisions[s.id || s.name]?.choice && decisions[s.id || s.name].choice !== 'blocked');

  /* G179 — Authoritative Decide→Act gate from backend.
   * PhaseGateService.evaluateDecideGate enforces:
   *   - at_least_one_reviewed_skill (BLOCKING)
   *   - autonomous_score_threshold ≥ 90% (BLOCKING)
   *   - at_least_one_executable_skill (non-blocked) (BLOCKING)
   *   - unreviewed_skills_info (INFORMATIONAL, doesn't block)
   * V2 used to enforce "every skill must have a decision" which is
   * stricter than backend reality (backend supports partial advancement
   * via review-each-skill-independently). Render whatever the backend
   * authoritative list says. */
  const [decideChecks, setDecideChecks] = useState([]);
  const [decideLoading, setDecideLoading] = useState(true);
  // Whether the agent is actually AT the Decide gate (phase==decide) — only then is
  // "Advance to Act" clickable. When the agent is still in Build, we explicitly ask
  // for the Decide→Act gate ('decide') so the panel shows the REAL Decide criteria
  // (not the Build gate mislabeled), but as a forward preview.
  const [decideActionable, setDecideActionable] = useState(false);
  const refreshDecideGate = React.useCallback(async () => {
    if (!id) return;
    setDecideLoading(true);
    try {
      const preview = api['previewPhaseGate'];
      if (typeof preview !== 'function') return;
      // Always request the Decide→Act gate explicitly, regardless of the agent's
      // current phase, so this tab renders its own gate ("displayed == enforced").
      const res = await preview(id, 'decide');
      const checks = Array.isArray(res?.data?.checks) ? res.data.checks : [];
      setDecideChecks(checks);
      setDecideActionable(res?.data?.actionable !== false
        && (res?.data?.currentPhase ? res.data.currentPhase === 'decide' : true));
    } catch (e) {
      setDecideChecks([{ check: 'preview_error', passed: false,
        message: e?.message || 'Failed to load Decide phase gate preview' }]);
    } finally {
      setDecideLoading(false);
    }
  }, [id]);
  /* Re-key on a signature of the gate-relevant skill fields (phase, eval, review),
   * not just reviewedCount — a per-skill Advance (decide→act) or Regress changes a
   * skill's phase without changing the reviewed count, and must refresh the gate
   * without a manual reload. */
  const decideGateSig = React.useMemo(
    () => skills.map((s) => `${s.id}:${s.skillPhase || 'build'}:${s.evalScore ?? ''}:${s.reviewStatus || ''}`).join('|'),
    [skills],
  );
  React.useEffect(() => { refreshDecideGate(); }, [refreshDecideGate, decideGateSig]);

  /* G250 — Submit a single skill review. Declared HERE (after
   * refreshDecideGate) instead of next to its state to avoid the temporal-
   * dead-zone error that bricked the build. Mirrors V1 handleReviewSkill:
   *   - Validates decision is selected
   *   - POSTs /agents/{id}/skills/{sid}/review
   *   - Backend SkillReviewService writes SkillReview row + flips
   *     Skill.autonomy + Skill.reviewStatus per the decision
   *   - Surfaces backend error verbatim (e.g. "Cannot promote skill to
   *     autonomous: evalScore < 90%")
   *   - Refreshes the Decide gate so at_least_one_reviewed_skill flips */
  const handleSubmitReview = React.useCallback(async () => {
    if (!reviewingSkill?.id) return;
    if (!reviewForm.decision) {
      setReviewError('Select a decision (autonomous / human approval / blocked).');
      return;
    }
    const reviewFn = api['reviewSkill'];
    if (typeof reviewFn !== 'function') {
      setReviewError('api.reviewSkill is not wired in this build.');
      return;
    }
    setReviewSubmitting(true);
    setReviewError(null);
    try {
      const res = await reviewFn(id, reviewingSkill.id, {
        decision: reviewForm.decision,
        justification: reviewForm.justification || '',
      });
      if (res?.ok && res?.data?.id) {
        addToast({ tone: 'success', title: `${reviewingSkill.name} reviewed`,
          description: `Decision: ${reviewForm.decision}.` });
        const key = reviewingSkill.id || reviewingSkill.name;
        setDecisions((p) => ({
          ...p, [key]: { choice: reviewForm.decision, justification: reviewForm.justification },
        }));
        setReviewingSkill(null);
        await refreshDecideGate();
        if (typeof reloadAgentFromParent === 'function') {
          try { await reloadAgentFromParent(); } catch (_e) { /* ignore */ }
        }
      } else {
        const msg = res?.error || res?.data?.error || `HTTP ${res?.status || '?'}`;
        setReviewError(msg);
      }
    } catch (e) {
      setReviewError(e?.message || String(e));
    } finally {
      setReviewSubmitting(false);
    }
  }, [api, id, reviewingSkill, reviewForm, addToast, refreshDecideGate, reloadAgentFromParent]);

  const decideLabelFor = (c) => {
    switch (c.check) {
      case 'at_least_one_reviewed_skill':   return 'At least one skill reviewed';
      case 'autonomous_score_threshold':    return 'Autonomous skills meet score threshold (≥ 90%)';
      case 'at_least_one_executable_skill': return 'At least one reviewed skill is non-blocked';
      case 'unreviewed_skills_info':        return 'Unreviewed skills (informational)';
      default:                              return c.check || 'Backend check';
    }
  };

  const gateCriteria = decideChecks.map((c) => ({
    id: c.check,
    passed: !!c.passed,
    label: decideLabelFor(c),
    detail: c.message,
  }));

  const gatePassed = gateCriteria.length > 0 && gateCriteria.every((c) => c.passed);
  // Advance is only offered when the gate passes AND the agent is actually at the
  // Decide phase. A passing Decide gate while the agent is still in Build is a valid
  // forward preview, but the Build→Decide advance must happen first.
  const advanceReady = !decideLoading && gatePassed && decideActionable;
  // Agent already in Act → the agent-level Decide→Act gate is moot (new/regressed skills
  // now progress individually via each skill card's Advance). Render an "advanced" state
  // instead of a stale "0 of 0 passing" with a permanently-disabled button.
  const agentPastDecide = (agent?.phase || 'build').toLowerCase() === 'act';

  const handleSave = async () => {
    setSaving(true);
    try {
      /* G246 — V1→V2 lifecycle regression fix. Previously this called
       * api.saveSkillDecisions which is a STUB (services/api.js:2336
       * returns {ok: false, error: 'not implemented yet'}). The real
       * persistence endpoint is per-skill:
       *   POST /api/v1/agents/{agentId}/skills/{skillId}/review
       * already exposed as `api.reviewSkill(agentId, skillId, payload)`
       * and backed by SkillReviewService.reviewSkill (real, transactional,
       * writes SkillReview rows + updates Skill.autonomy + Skill.reviewStatus).
       *
       * Without writing SkillReview rows, the Decide→Act gate's
       * `at_least_one_reviewed_skill` check (PhaseGateService line 346)
       * cannot clear. V1 looped per-skill via reviewSkillApi; the V2
       * rewrite collapsed that into a single stub that was never wired.
       * We loop per skill here, aggregate results, and surface honest
       * pass/fail counts. Justification is sent through verbatim.
       *
       * Two important behaviours to preserve:
       *   1. The backend rejects autonomous decision when evalScore < 0.90
       *      with HTTP 422 — we surface that per-skill (skill-level error,
       *      not a global failure) so the operator can adjust just that
       *      row's decision.
       *   2. Only skills with a `choice` value are submitted. Empty
       *      decisions stay local; this matches the previous V1 semantic. */
      const review = api['reviewSkill'];
      if (typeof review !== 'function') {
        addToast({
          tone: 'warning',
          title: 'Review endpoint missing in this build',
          description: 'api.reviewSkill is not wired. Decisions stay local until the API is added.',
        });
        return;
      }
      // Build the list of (skill, decision) tuples to submit. Skip
      // skills with no choice — the backend treats absence as
      // "still unreviewed" and the gate's informational check
      // surfaces them.
      const submissions = [];
      for (const s of skills) {
        const key = s.id || s.name;
        const dec = decisions[key];
        if (!dec?.choice || !s.id) continue;
        submissions.push({ skill: s, choice: dec.choice, justification: dec.justification || '' });
      }
      if (submissions.length === 0) {
        addToast({ tone: 'info', title: 'No decisions to save',
          description: 'Pick autonomous / human approval / blocked on at least one skill first.' });
        return;
      }

      let ok = 0;
      const failures = []; // {name, error}
      for (const sub of submissions) {
        // Serial to avoid hammering the backend and to keep the
        // audit trail (which uses createdAt as the sort key) in a
        // deterministic order.
        const res = await review(id, sub.skill.id, {
          decision: sub.choice,
          justification: sub.justification,
        });
        if (res?.ok && res?.data?.id) {
          ok++;
        } else {
          const msg = res?.error || res?.data?.error || `HTTP ${res?.status || '?'}`;
          failures.push({ name: sub.skill.name, error: msg });
        }
      }
      if (failures.length === 0) {
        addToast({
          tone: 'success',
          title: 'Decisions saved',
          description: `${ok} skill review(s) recorded. Refreshing gate…`,
        });
      } else if (ok === 0) {
        addToast({
          tone: 'danger',
          title: 'Save failed for every skill',
          description: failures.slice(0, 3).map((f) => `${f.name}: ${f.error}`).join(' · '),
        });
      } else {
        addToast({
          tone: 'warning',
          title: `Saved ${ok}/${submissions.length} decisions`,
          description: 'Some skills were rejected by the backend — typically autonomous decisions with evalScore < 90%. ' +
            failures.slice(0, 2).map((f) => `${f.name}: ${f.error}`).join(' · '),
        });
      }
      // Re-fetch the Decide gate so the `at_least_one_reviewed_skill`
      // check flips green and the Advance-to-Act button enables.
      await refreshDecideGate();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Save failed', description: err?.message || String(err) });
    } finally {
      setSaving(false);
    }
  };

  if (skills.length === 0) {
    return (
      <Card>
        <Card.Body>
          <EmptyState
            icon={<SparklesIcon size={20} />}
            title="No skills to decide on"
            description="Author your first skill in the Build tab before advancing to Decide."
          />
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <PhaseGate
        phase="DECIDE"
        criteria={gateCriteria}
        advanced={agentPastDecide}
        advanceAction={
          agentPastDecide ? (
            <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-v2-success-strong"
              title="The agent already advanced to Act. New or regressed skills now progress individually via each skill's Advance.">
              ✓ Advanced
            </span>
          ) : (
          /* G250 — Always render "Advance to Act" as a button. Previously the
           * label flipped to "Gate: 0 of 1" when disabled, which looked like a
           * status pill rather than a clickable affordance. The gate progress
           * is already surfaced in the card header ("Decide → Act gate · X of
           * Y passing"), so the button just needs an explicit "Advance to Act"
           * label and a disabled state. */
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-v2-ink-muted tabular-nums">
              Gate: {gateCriteria.filter((c) => c.passed).length} of {gateCriteria.length}
            </span>
            <Button
              variant="primary"
              size="sm"
              disabled={!advanceReady}
              onClick={onAdvanceToAct}
              title={advanceReady
                ? 'Promote agent from Decide to Act phase'
                : (!decideActionable
                    ? 'The agent is still in Build. Clear the Build → Decide gate first (Build tab), then advance to Act here.'
                    : 'Gate not passing — review at least one skill (non-blocked, autonomous needs evalScore ≥ 90%)')}>
              Advance to Act
            </Button>
          </div>
          )
        }
      />

      {/* Agent hasn't reached Decide yet — the panel above is a real forward
          preview of the Decide→Act gate (not the Build gate mislabeled). Guide the
          operator to clear Build→Decide first. */}
      {!decideActionable && !decideLoading && (agent?.phase || 'build').toLowerCase() !== 'act' && (
        <div className="rounded-v2-md px-4 py-3" style={{ background: 'var(--v2-cyan-soft)', color: 'var(--v2-cyan)' }}>
          <div className="text-[13px] font-semibold">
            {gatePassed
              ? 'Decide → Act gate is ready — advance Build → Decide first'
              : 'Agent is still in Build'}
          </div>
          <div className="text-[12px] mt-0.5">
            The agent advances one phase at a time. This preview shows the real Decide → Act
            criteria for your reviewed skills; advance the agent <strong>Build → Decide</strong> on
            the Build tab, then return here to advance to Act.
          </div>
        </div>
      )}

      {/* Bug C1/C2 — restore V1's Decide-phase context banner: "completed" once
          all skills are reviewed (agent in Act), or "N skills need review" when an
          Act-phase agent has new unreviewed skills to activate individually. */}
      {(() => {
        const agentPastDecide = (agent?.phase || '').toLowerCase() === 'act';
        if (!agentPastDecide) return null;
        const unreviewed = skills.filter((s) => !(s.reviewStatus && s.reviewStatus !== 'pending_review'));
        const done = unreviewed.length === 0;
        return (
          <div className="rounded-v2-md px-4 py-3" style={done
            ? { background: 'var(--v2-success-soft)', color: 'var(--v2-success)' }
            : { background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)' }}>
            <div className="text-[13px] font-semibold">
              {done ? 'Decide phase completed' : `${unreviewed.length} skill${unreviewed.length > 1 ? 's need' : ' needs'} review`}
            </div>
            <div className="text-[12px] mt-0.5">
              {done
                ? 'All skills have been reviewed. This agent is now in the Act phase.'
                : 'This agent is already in the Act phase. Review and approve each new skill individually to activate it alongside your existing skills.'}
            </div>
          </div>
        );
      })()}

      <Card padding="none">
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
          <h3 className="text-[14px] font-bold tracking-tight m-0">Decision matrix · skill review</h3>
          <span className="text-[11px] text-v2-ink-muted">{reviewedCount} of {skills.length} reviewed</span>
        </div>
        {/* #66 — V1-parity 2×2 skill cards on the native Decide surface. Shared
          * <SkillPromotionCard surface="decide"> owns review (slideout → reviewSkill) /
          * advance / regress via the same backend. Mirrors the ExteLet Decide pattern:
          * only Decide/Act skills get a card; Build-phase skills are noted below. */}
        <div className="p-4 space-y-3">
          <p className="text-[12.5px] text-v2-ink-muted m-0">
            Reviewing a skill sets its autonomy: <strong>Autonomous</strong> (no-touch, needs eval ≥ 90%),
            <strong> Human approval</strong> (each call surfaces for approval), or <strong>Blocked</strong>.
            After a non-blocked review, promote it <strong>→ Act</strong>. At least one non-blocked review is
            required to advance the agent to Act.
          </p>
          {(() => {
            const decideAndAct = skills.filter((s) => (s.skillPhase || 'build').toLowerCase() !== 'build');
            const buildCount = skills.length - decideAndAct.length;
            if (skills.length === 0) {
              return (
                <p className="text-[12px] text-v2-ink-muted m-0">
                  No skills yet. Add and eval skills on the <strong>Build</strong> tab, then promote them to Decide.
                </p>
              );
            }
            return (
              <>
                {decideAndAct.length === 0 ? (
                  <p className="text-[12px] text-v2-ink-muted m-0">
                    No skills in Decide yet. Promote a skill to Decide on the <strong>Build</strong> tab, then review it here.
                  </p>
                ) : (
                  <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
                    {decideAndAct.map((s) => (
                      <SkillPromotionCard
                        key={s.id || s.name}
                        agentId={id}
                        skill={s}
                        surface="decide"
                        onChanged={async () => {
                          if (typeof reloadAgentFromParent === 'function') await reloadAgentFromParent();
                          refreshDecideGate();
                        }}
                      />
                    ))}
                  </div>
                )}
                {buildCount > 0 && (
                  <div className="rounded-v2-md px-3 py-2 text-[11.5px] text-v2-ink-muted bg-v2-muted">
                    {buildCount} skill{buildCount > 1 ? 's are' : ' is'} still in <strong>Build</strong> — promote
                    {buildCount > 1 ? ' them' : ' it'} to Decide on the Build tab to review here.
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </Card>

      {/* #66 / conformance generalization — native Decide-phase Trust baseline.
        * Same scoring-config + golden-baseline BE as the ExteLet Decide tab.
        * AutoLets get the source-workflow oracle automatically; Lets/SysLets use
        * a golden baseline. Advisory — never blocks the Decide → Act gate. */}
      <NativeConformanceCard
        agent={agent}
        onChanged={() => { refreshDecideGate(); if (typeof reloadAgentFromParent === 'function') reloadAgentFromParent(); }}
      />

      {/* G250 — Per-skill Review Slideout. Mirrors V1's modal exactly
        * (AgentsPage.jsx:2077-2137) using the V2 Slideout pattern. Width
        * 520 matches the Generate Eval picker for visual consistency. */}
      <Slideout
        isOpen={!!reviewingSkill}
        onClose={reviewSubmitting ? () => {} : () => setReviewingSkill(null)}
        width={520}
        ariaLabel={reviewingSkill ? `Review skill ${reviewingSkill.name}` : 'Review skill'}
        header={reviewingSkill && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Pill tone="cyan" size="sm">Decide phase</Pill>
              <Pill
                tone={reviewingSkill.riskLevel === 'critical' ? 'danger'
                  : reviewingSkill.riskLevel === 'high' ? 'warning' : 'muted'}
                size="sm">
                {reviewingSkill.riskLevel || 'risk —'}
              </Pill>
            </div>
            <h2 className="text-[16px] font-bold tracking-tight m-0 leading-tight">Review skill</h2>
            <p className="text-[12px] text-v2-ink-secondary m-0">{reviewingSkill.name}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmitReview}
                loading={reviewSubmitting}
                disabled={reviewSubmitting || !reviewForm.decision}>
                {reviewSubmitting ? 'Saving…' : 'Confirm review'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setReviewingSkill(null)} disabled={reviewSubmitting}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      >
        {reviewingSkill && (
          <div className="space-y-4 text-[12.5px]">
            {/* Skill context summary */}
            <div className="rounded-v2-md border border-v2-cyan-border bg-v2-cyan-soft px-3 py-2.5 text-v2-cyan">
              <div className="grid grid-cols-2 gap-2 text-[12px]">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-v2-ink-muted">Eval score</div>
                  <div className="text-[16px] font-bold text-v2-ink mt-0.5">
                    {reviewingSkill.evalScore != null
                      ? `${Math.round(normalizeEvalTo100(reviewingSkill.evalScore))}%`
                      : 'no eval'}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-v2-ink-muted">Risk level</div>
                  <div className="text-[14px] font-semibold text-v2-ink mt-0.5 capitalize">
                    {reviewingSkill.riskLevel || '—'}
                  </div>
                </div>
              </div>
              {reviewingSkill.description && (
                <div className="mt-2 pt-2 border-t border-v2-cyan-border text-[11.5px] text-v2-ink-secondary leading-relaxed">
                  {reviewingSkill.description}
                </div>
              )}
            </div>

            {/* Decision radios */}
            <div>
              <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-2">
                Autonomy decision
              </label>
              <div className="space-y-2">
                {[
                  { id: 'autonomous',     label: 'Autonomous',     tone: 'success', hint: 'No-touch execution. Requires evalScore ≥ 90%.' },
                  { id: 'human_approval', label: 'Human approval', tone: 'warning', hint: 'Each execution surfaces for human approval before running.' },
                  { id: 'blocked',        label: 'Block skill',    tone: 'danger',  hint: 'Skill cannot be invoked. Stays in catalog but every call is rejected.' },
                ].map((opt) => {
                  const checked = reviewForm.decision === opt.id;
                  const tooLowAuto = opt.id === 'autonomous'
                    && reviewingSkill.evalScore != null
                    && normalizeEvalTo100(reviewingSkill.evalScore) < 90;
                  return (
                    <label
                      key={opt.id}
                      className={`flex items-start gap-3 px-3 py-2.5 rounded-v2-md cursor-pointer border transition-colors ${
                        checked
                          ? (opt.tone === 'success' ? 'border-v2-success bg-v2-success-soft'
                            : opt.tone === 'warning' ? 'border-v2-warning bg-v2-warning-soft'
                            : 'border-v2-danger bg-v2-danger-soft')
                          : 'border-v2-border hover:bg-v2-muted'
                      } ${reviewSubmitting ? 'opacity-60 pointer-events-none' : ''}`}>
                      <input
                        type="radio"
                        name="reviewDecision"
                        value={opt.id}
                        checked={checked}
                        onChange={() => setReviewForm({ ...reviewForm, decision: opt.id })}
                        disabled={reviewSubmitting}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-v2-ink">{opt.label}</div>
                        <div className="text-[11.5px] text-v2-ink-secondary leading-relaxed mt-0.5">{opt.hint}</div>
                        {tooLowAuto && (
                          <div className="text-[11px] text-v2-warning mt-1">
                            ⚠ Current eval score ({Math.round(normalizeEvalTo100(reviewingSkill.evalScore))}%) is below 90% —
                            backend will reject this decision. Pick human approval or improve the score first.
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Justification */}
            <div>
              <label className="block text-[11px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-1">
                Justification
              </label>
              <textarea
                value={reviewForm.justification}
                onChange={(e) => setReviewForm({ ...reviewForm, justification: e.target.value })}
                disabled={reviewSubmitting}
                rows={4}
                placeholder="Explain the reasoning for this decision. Recorded in the audit trail."
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-3 py-2 text-[12.5px] resize-none"
              />
            </div>

            {reviewError && (
              <div className="rounded-v2-sm border border-v2-danger-border bg-v2-danger-soft px-3 py-2 text-[12px] text-v2-danger">
                <strong>Save failed:</strong> {reviewError}
              </div>
            )}
          </div>
        )}
      </Slideout>
    </div>
  );
}

/* ── Act tab (Phase 4c) ───────────────────────────────────────
 * The publish-and-operate phase. Composes:
 *   - Publish gate (6 criteria)
 *   - User / group assignment summary
 *   - Inference mode toggle (Self / Platform)
 *   - Live execution feed (link-out to v1 LiveExecutionCenter for now)
 *   - Publish history + rollback
 *   - Kill-switch cascade visualization
 */
function ActTab({ agent, killSwitchActive, reloadAgentFromParent }) {
  /* G152 — defensive wrapper, same as BuildTab. */
  const toastCtx = useToast();
  const addToast = React.useCallback((arg, type, duration) => {
    if (toastCtx && typeof toastCtx.addToast === 'function') {
      return toastCtx.addToast(arg, type, duration);
    }
    // eslint-disable-next-line no-console
    console.warn('[AgentDetailPageV2/ActTab] toast unavailable, dropped:', arg);
    return null;
  }, [toastCtx]);
  const navigate = useNavigate();
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [inferenceMode, setInferenceMode] = useState(agent.inferenceMode || 'self');
  // The agent-level inference mode is persisted to InferenceOverrides (NOT a column on
  // the agent), so `agent.inferenceMode` is always undefined and the toggle wrongly reset
  // to "self" on every load. Read the persisted override on mount so the card reflects the
  // saved state (platformModeEnabled → platform, else self).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getOverrides(agent.id);
        const ov = r?.data ?? r;
        if (!cancelled && ov && (ov.platformModeEnabled != null || ov.selfModeEnabled != null)) {
          setInferenceMode(ov.platformModeEnabled ? 'platform' : 'self');
        }
      } catch { /* keep current default */ }
    })();
    return () => { cancelled = true; };
  }, [agent.id]);
  /* G193 — Rollback dialog state for the publish-history table. Operators
   * must confirm before rolling back a published version; the action calls
   * api.rollbackPublication which POSTs to /api/v1/agents/{id}/publish/{pubId}/rollback. */
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const auth = useAuth();
  const userId = auth?.session?.userId;

  /* G194 — Assignment manager modal state. The "Manage" button on the
   * Assignment card was a dead stub; V1 has an inline modal that lists
   * current assignments and lets operators add/remove user or group
   * grants. V2 surfaces the same flow via api.getAssignments /
   * createAssignment / removeAssignment. */
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [addAssignKind, setAddAssignKind] = useState('user'); // 'user' | 'group'
  const [addAssignTargetId, setAddAssignTargetId] = useState('');
  const [addAssignAccess, setAddAssignAccess] = useState('execute');
  const [addingAssign, setAddingAssign] = useState(false);
  const [removingAssignId, setRemovingAssignId] = useState(null);

  const refreshAssignments = React.useCallback(async () => {
    if (!agent?.id) return;
    setAssignmentsLoading(true);
    setAssignError(null);
    try {
      const fn = api['getAssignments'];
      if (typeof fn !== 'function') { setAssignError('api.getAssignments is not wired.'); return; }
      const res = await fn(agent.id);
      const items = Array.isArray(res?.data?.items) ? res.data.items
                  : Array.isArray(res?.data) ? res.data
                  : Array.isArray(res?.items) ? res.items
                  : Array.isArray(res) ? res : [];
      setAssignments(items);
    } catch (e) {
      setAssignError(e?.message || String(e));
    } finally {
      setAssignmentsLoading(false);
    }
  }, [agent?.id]);

  const openAssignmentManager = () => { setAssignOpen(true); refreshAssignments(); };

  const handleAddAssignment = async () => {
    setAssignError(null);
    if (!addAssignTargetId.trim()) {
      setAssignError(`${addAssignKind === 'user' ? 'User' : 'Group'} ID is required (find UUIDs in the Team page).`);
      return;
    }
    setAddingAssign(true);
    try {
      const fn = api['createAssignment'];
      if (typeof fn !== 'function') { setAssignError('api.createAssignment is not wired.'); return; }
      const payload = {
        accessLevel: addAssignAccess,
        assignedBy: userId,
      };
      if (addAssignKind === 'user') payload.userId = addAssignTargetId.trim();
      else                          payload.groupId = addAssignTargetId.trim();
      const res = await fn(agent.id, payload);
      if (!res || res.ok === false) {
        const status = res?.status;
        const backendMsg = res?.error || res?.data?.error;
        if (status === 409 && res?.data?.duplicate) {
          setAssignError(`Already assigned at access level "${res.data.existingAccessLevel}". Remove and re-add to change level, or pass override=true.`);
        } else {
          setAssignError(backendMsg || `Backend rejected the assignment (HTTP ${status || '?'}).`);
        }
        return;
      }
      setAddAssignTargetId('');
      addToast({ tone: 'success', title: 'Assignment added', description: `${addAssignKind} grant created.` });
      await refreshAssignments();
    } catch (e) {
      setAssignError(e?.message || String(e));
    } finally {
      setAddingAssign(false);
    }
  };

  const handleRemoveAssignment = async (assignment) => {
    if (!assignment?.id) return;
    setRemovingAssignId(assignment.id);
    try {
      const fn = api['removeAssignment'];
      if (typeof fn !== 'function') {
        addToast({ tone: 'danger', title: 'Remove failed', description: 'api.removeAssignment is not wired.' });
        return;
      }
      const res = await fn(agent.id, assignment.id);
      if (!res || res.ok === false) {
        addToast({ tone: 'danger', title: 'Remove failed', description: res?.error || res?.data?.error || `HTTP ${res?.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: 'Assignment removed' });
      await refreshAssignments();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Remove failed', description: e?.message || String(e) });
    } finally {
      setRemovingAssignId(null);
    }
  };
  const handleRollbackConfirm = async () => {
    if (!rollbackTarget?.id && !rollbackTarget?.version) return;
    setRollingBack(true);
    try {
      const rb = api['rollbackPublication'];
      if (typeof rb !== 'function') {
        addToast({ tone: 'danger', title: 'Rollback unavailable', description: 'api.rollbackPublication is not wired in this build.' });
        return;
      }
      const pubId = rollbackTarget.id || rollbackTarget.version;
      const res = await rb(agent.id, pubId, userId);
      if (!res || res.ok === false) {
        const status = res?.status;
        const backendMsg = res?.error || res?.data?.error;
        const desc = status === 403 ? (backendMsg || 'You do not have publish.create permission.')
                   : status === 404 ? (backendMsg || 'Publication not found — it may have been removed.')
                   : (backendMsg || `Backend rejected the rollback (HTTP ${status || '?'}).`);
        addToast({ tone: 'danger', title: 'Rollback failed', description: desc });
        return;
      }
      addToast({ tone: 'success', title: 'Rolled back', description: `Version ${rollbackTarget.version || rollbackTarget.id} is now live.` });
      setRollbackTarget(null);
      /* G252 — Refresh state instead of window.location.reload (which
       * blew away the eval Slideout in G248). Calls all three lists +
       * publish-gate so the rolled-back version shows up + counts shift. */
      await refreshPublications();
      await refreshChecks();
      await refreshAgentlets();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Rollback failed', description: e?.message || String(e) });
    } finally {
      setRollingBack(false);
    }
  };

  /* G255 — Derive assignment counts from the live backend assignments
   * already loaded via api.getAssignments (state: `assignments`, see
   * refreshAssignments below). The agent record itself does NOT include
   * assignedUsers / assignedGroups, so the previous fallback to
   * `agent.assignedUsers || agent.users` resolved to empty arrays and the
   * publish-confirm modal summary undercounted to "0 user(s), 0 group(s)"
   * even when assignments existed. The Manage in Teams card already
   * reads the same `assignments` state — keep them aligned. */
  const skills = agent.skills || [];

  /* G252 — V1 Act-phase parity. V2 stubbed three sections that V1 had as
   * full tables (AgentsPage.jsx:6400+, 6911+, 7003+):
   *   - Publication History (real api.getPublications, paginated)
   *   - Recent Executions (api.getExecutions)
   *   - Agentlets Live (api.getAgentlets + per-row ping)
   * We fetch all three on mount + after publish/rollback so operators see
   * the live state without a manual refresh. */
  const [publications, setPublications] = useState([]);
  const [publicationsLoading, setPublicationsLoading] = useState(false);
  const [executions, setExecutions] = useState([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  /* G395 — Pagination for the two unbounded tables in this tab.
   *
   * Publish history: silently 50-capped by the backend `getPublications(id, 0, 50)`
   * call. Operators with > 50 versions never see the older entries. Until we wire
   * server-side paging through, at least give them client-side paging over what
   * the backend returns so the table doesn't grow vertically.
   *
   * Executions: rendered with `.slice(0, 25)` previously — silent truncation.
   * Active agents emit hundreds-to-thousands of executions per day; client-side
   * pagination is the minimum fix while we wait for server-side filtering. */
  const pgPublications = usePaginationState(10);
  const pgExecutions = usePaginationState(25);
  const [agentlets, setAgentlets] = useState([]);
  const [agentletsLoading, setAgentletsLoading] = useState(false);
  const [pingingAgentletId, setPingingAgentletId] = useState(null);

  const refreshPublications = React.useCallback(async () => {
    if (!agent?.id) return;
    setPublicationsLoading(true);
    try {
      const fn = api['getPublications'];
      if (typeof fn !== 'function') return;
      const res = await fn(agent.id, 0, 50);
      // Backend may return Spring Pageable {content:[]} or a raw array.
      const items = Array.isArray(res?.data?.content) ? res.data.content
        : Array.isArray(res?.data) ? res.data
        : Array.isArray(res?.content) ? res.content : [];
      setPublications(items);
    } catch (_e) { /* swallow — render empty state */ }
    finally { setPublicationsLoading(false); }
  }, [agent?.id]);

  const refreshExecutions = React.useCallback(async () => {
    if (!agent?.id) return;
    setExecutionsLoading(true);
    try {
      const fn = api['getExecutions'];
      if (typeof fn !== 'function') return;
      const res = await fn(agent.id);
      const items = Array.isArray(res?.data?.content) ? res.data.content
        : Array.isArray(res?.data) ? res.data
        : Array.isArray(res?.content) ? res.content : [];
      setExecutions(items);
    } catch (_e) { /* swallow */ }
    finally { setExecutionsLoading(false); }
  }, [agent?.id]);

  const refreshAgentlets = React.useCallback(async () => {
    setAgentletsLoading(true);
    try {
      const fn = api['getAgentlets'];
      if (typeof fn !== 'function') return;
      /* RC-LLC-CONTAINER-002 — Scope the panel to THIS agent's tenant-or-global
       * set, the exact set the Act→Published gate counts (PublishService
       * findByTenantIdOrGlobal). Without this the panel called getAgentlets({})
       * → backend findAll() → it showed containers from OTHER tenants (e.g. the
       * platform default-tenant) that the gate correctly refuses to route a
       * publish through — so the panel said "1 connected" while the gate said
       * "No agentlets registered". tenantOrGlobal disables the dev findAll
       * fallback so the two surfaces always agree. */
      const res = await fn({ tenantId: agent?.tenantId, tenantOrGlobal: true });
      /* G252 — Backend wraps in {agentlets: [...], total, page, size, connected}
       * (verified via in-browser fetch). Not Spring Pageable's {content} shape. */
      const items = Array.isArray(res?.data?.agentlets) ? res.data.agentlets
        : Array.isArray(res?.data?.content) ? res.data.content
        : Array.isArray(res?.data) ? res.data
        : Array.isArray(res?.content) ? res.content : [];
      setAgentlets(items);
    } catch (_e) { /* swallow */ }
    finally { setAgentletsLoading(false); }
  }, [agent?.tenantId]);

  useEffect(() => { refreshPublications(); }, [refreshPublications]);
  useEffect(() => { refreshExecutions(); }, [refreshExecutions]);
  useEffect(() => { refreshAgentlets(); }, [refreshAgentlets]);
  /* G253 — Load assignments eagerly so the read-only table renders
   * immediately on mount (assignments live on /team but operators need
   * to see them here without clicking anything). */
  useEffect(() => { refreshAssignments(); }, [refreshAssignments]);

  const handlePingAgentlet = React.useCallback(async (agentlet) => {
    if (!agentlet?.id) return;
    setPingingAgentletId(agentlet.id);
    try {
      const fn = api['pingAgentlet'];
      if (typeof fn !== 'function') {
        addToast({ tone: 'warning', title: 'Ping unavailable', description: 'api.pingAgentlet is not wired.' });
        return;
      }
      const res = await fn(agentlet.id);
      if (res?.ok) {
        addToast({ tone: 'success', title: `Pinged ${agentlet.name || agentlet.id.slice(0, 8)}`,
          description: `Latency ${res?.data?.latencyMs ?? '—'}ms · status ${res?.data?.status || agentlet.status}` });
        await refreshAgentlets();
      } else {
        addToast({ tone: 'danger', title: 'Ping failed', description: res?.error || `HTTP ${res?.status || '?'}` });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Ping threw', description: e?.message || String(e) });
    } finally {
      setPingingAgentletId(null);
    }
  }, [addToast, refreshAgentlets]);
  /* G183/G255 — Skills that will be in the publish bundle. V1 counted
   *   actReadySkills = allSkills.filter(s => s.skillPhase === 'act')
   * (AgentsPage.jsx:6363) — these are the skills cleared through the gate
   * and ready to push. The previous narrower `s.pushed||s.published`
   * filter undercounted because freshly-promoted act-phase skills don't
   * have pushed=true until publish completes — making the publish modal
   * show "Skills published: 0" right before the publish that would
   * make it 1. */
  const publishedSkills = skills.filter((s) => s.skillPhase === 'act');

  /* G255 — Derive assignment counts from live `assignments` state, which
   * api.getAssignments populates on mount and refreshes after every
   * add/remove. Partition by userId vs groupId to match V1's display. */
  const assignedUsers = (assignments || []).filter((a) => !!a.userId);
  const assignedGroups = (assignments || []).filter((a) => !!a.groupId);

  /* G178 — Pull authoritative publish-validation checks from the backend
   * (POST /api/v1/agents/{id}/publish/validate). V1's PublishService.
   * runPublishValidation enforces 8 checks; V2 previously hand-coded only 6
   * of them locally, drifting from backend reality and silently letting
   * Publish through when 6 of 8 backend conditions were unmet.
   *
   * On mount + on agent.id change we fetch the backend's checks and render
   * each as a PhaseGate criterion. Kill-switch + lifecycle workflow remain
   * client-only gates because V1 doesn't expose them in
   * runPublishValidation but V2 should still block on them. */
  const [backendChecks, setBackendChecks] = useState([]);
  const [validating, setValidating] = useState(false);
  const refreshChecks = React.useCallback(async () => {
    if (!agent?.id) return;
    setValidating(true);
    try {
      const validate = api['validatePublish'];
      if (typeof validate !== 'function') return;
      const res = await validate(agent.id);
      const checks = Array.isArray(res?.data?.checks) ? res.data.checks
                   : Array.isArray(res?.checks) ? res.checks : [];
      setBackendChecks(checks);
    } catch (e) {
      // Surface a single synthetic check so the user sees the failure rather
      // than a silent empty gate.
      setBackendChecks([{ check: 'backend_validate', passed: false, status: 'error',
                          message: e?.message || 'Failed to fetch publish validation' }]);
    } finally {
      setValidating(false);
    }
    // Re-fetch when the agent's last test-run timestamp changes (a fresh Test Run
    // reloads the agent via onTestPassed → reloadAgent), so the cached
    // "Successful Test Run recorded" check flips as soon as the run is recorded.
  }, [agent?.id, agent?.lastTestRunAt]);
  useEffect(() => { refreshChecks(); }, [refreshChecks]);

  /* Translate backend checks into PhaseGate's expected shape. Labels mirror
   * what V1 shows in its publish validation modal so operators see the
   * same wording across UIs. */
  const labelFor = (c) => {
    switch (c.check) {
      case 'user_assignments_exist':      return '≥ 1 user or group assigned';
      /* V332 — parallel registry: PublishService emits this check key; without a label here it
       * would render as the raw identifier. An agent with a human-gated skill and no approver
       * cannot be published, because at runtime the approval would be assigned to nobody and
       * would be invisible in every inbox. */
      case 'human_gated_skills_have_approver':
                                          return 'Every human-gated skill has an approver';
      case 'no_blocked_skills':           return 'No blocked skills';
      case 'eval_scores_above_threshold': return 'All eval scores ≥ 70% threshold';
      case 'agentlet_reachable':          return '≥ 1 reachable agentlet container';
      case 'agent_active':                return 'Agent is operational (active / healthy / connected)';
      case 'act_phase_skills_available':  return 'Act-phase skills available to publish';
      case 'workflow_links_valid':        return 'Workflow-skill links are valid (no orphans)';
      case 'test_run_passed':             return 'Successful Test Run recorded';
      default:                            return c.check || 'Backend check';
    }
  };

  const backendGate = backendChecks.map((c) => ({
    id: c.check,
    passed: !!c.passed,
    label: labelFor(c),
    detail: c.message,
    /* Translate backend hint into a remediation block where it's actionable.
     * Each hint maps to a navigation target the operator can click. */
    remediation: (() => {
      if (c.passed) return null;
      /* Ownership consent — the accountable owner must accept in the
       * Accountability Registry. Deep-link to Outcomes & Actions where the
       * nominate->accept ceremony lives (mirrors the Build-gate remediation). */
      if (c.check === 'owner_consent_and_accountability'
          || c.action === 'navigate_ownership' || c.fixAction === 'navigate_ownership') {
        return { label: 'Resolve in Accountability', onClick: () => navigate('/accountability?tab=outcomes') };
      }
      if (!c.hint) return null;
      return { label: c.hint, onClick: () => {
      switch (c.action) {
        case 'navigate_assignments': /* anchor below — scroll into view */
          document.getElementById('assignments-section')?.scrollIntoView({ behavior: 'smooth' });
          break;
        case 'navigate_decide': /* parent owns tab state — addToast a hint */
          addToast({ tone: 'info', title: 'Open Decide phase', description: 'Click the Decide tab above to re-review skill autonomy.' });
          break;
        case 'navigate_build':
          addToast({ tone: 'info', title: 'Open Build phase', description: 'Click the Build tab to re-run evals for low-scoring skills.' });
          break;
        case 'navigate_agentlets':
          navigate('/agentlets'); break;
        case 'navigate_skills':
          addToast({ tone: 'info', title: 'Fix workflow-skill links', description: 'Re-link orphaned workflow skills in the Build tab or remove them.' });
          break;
        case 'open_test_run':
          addToast({ tone: 'info', title: 'Run a Test Run', description: 'Click "Test Run" in the header to chat with the agent — a successful round-trip satisfies this gate.' });
          break;
        default:
          break;
      }
      } };
    })(),
  }));

  /* Keep the V2-only checks the backend doesn't surface. */
  const v2ExtraGate = [
    { id: 'no-kill',
      passed: !killSwitchActive,
      label: 'No active kill switch',
      detail: killSwitchActive ? 'Kill switch is engaged' : null,
      remediation: killSwitchActive ? { label: 'Release in header' } : null },
    { id: 'workflow',
      passed: agent.lifecycleWorkflowHealthy !== false,
      label: 'AgentLifecycleWorkflow reports healthy' },
  ];

  const publishGate = [...backendGate, ...v2ExtraGate];
  const canPublish = publishGate.length > 0 && publishGate.every((c) => c.passed) && !validating;

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const publish = api['publishAgent'];
      if (typeof publish !== 'function') {
        addToast({ tone: 'danger', title: 'Publish unavailable', description: 'api.publishAgent is not wired.' });
        return;
      }
      /* G252 — Publish endpoint requires userId in body (controller line ~).
       * Surface backend errors verbatim instead of green-toasting silently.
       * After success, refresh the publication history so the new entry
       * shows up + refresh agent state so publishVersion increments. */
      const res = await publish(agent.id, userId);
      if (!res || res.ok === false) {
        const status = res?.status;
        const backendMsg = res?.error || res?.data?.error;
        addToast({ tone: 'danger', title: 'Publish failed',
          description: backendMsg || `Backend rejected the publish (HTTP ${status || '?'}).` });
        return;
      }
      const newVersion = res?.data?.version;
      addToast({ tone: 'success', title: 'Published',
        description: `${agent.name} now live${newVersion ? ` at v${newVersion}` : ''}.` });
      setPublishModalOpen(false);
      await refreshPublications();
      await refreshChecks();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Publish failed', description: err?.message || String(err) });
    } finally {
      setPublishing(false);
    }
  };

  const setInference = async (mode) => {
    const previous = inferenceMode;
    setInferenceMode(mode);
    try {
      // Bracket-notation soft-dependency guard — see W134 above.
      const setMode = api['setInferenceMode'];
      if (typeof setMode === 'function') {
        const res = await setMode(agent.id, mode);
        // apiFetch resolves with {ok:false} on a non-2xx rather than throwing, so the
        // success toast must check the result — otherwise a failed save still claimed success.
        if (res && res.ok === false) {
          throw new Error(res.error || res.message || 'Inference mode update failed');
        }
      }
      addToast({ tone: 'success', title: 'Inference mode updated', description: `${agent.name} → ${mode === 'self' ? 'Self inference' : 'Platform inference'}` });
    } catch (err) {
      setInferenceMode(previous);
      addToast({ tone: 'danger', title: 'Update failed', description: err?.message || String(err) });
    }
  };

  return (
    <div className="space-y-5">
      <PhaseGate
        phase="ACT"
        criteria={publishGate}
        advanceAction={
          /* G252 — Consistent gate UI with Build / Decide. Always explicit
           * "Publish Agentlet" label, disabled when gate fails. The X/Y
           * counter lives in the auxiliary label, not the button text. */
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-v2-ink-muted tabular-nums">
              Gate: {publishGate.filter((c) => c.passed).length} of {publishGate.length}
            </span>
            <Button
              variant="primary"
              size="sm"
              disabled={!canPublish}
              onClick={() => setPublishModalOpen(true)}
              title={canPublish
                ? 'Validate + deploy this Agentlet to its assigned agentlet container(s)'
                : 'Gate not passing — resolve each red check below before publishing'}>
              Publish Agentlet
            </Button>
          </div>
        }
      />

      {/* #66 — V1-parity 2×2 cards on the native Act surface. Shared
          <SkillPromotionCard surface="act"> (Locked(ACT) + Re-push + Regress) replaces
          ActSkillShells so Act matches Build/Decide + ExteLet. Preserves the Bug D1/D2
          intent (skills visible in Act): live act-phase skills as cards, a note for any
          still in Build/Decide. Same shared backend (regressSkill / pushSkill). */}
      {(() => {
        const actSkills = skills.filter((s) => (s.skillPhase || 'build').toLowerCase() === 'act');
        const nonAct = skills.length - actSkills.length;
        return (
          <Card padding="none">
            <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
              <h3 className="text-[14px] font-bold tracking-tight m-0">Live skills · {actSkills.length}</h3>
              <span className="text-[11px] text-v2-ink-muted">Reviewed skills published to this Agentlet.</span>
            </div>
            <div className="p-4 space-y-3">
              {skills.length === 0 ? (
                <p className="text-[12px] text-v2-ink-muted m-0">No skills yet.</p>
              ) : actSkills.length === 0 ? (
                <p className="text-[12px] text-v2-ink-muted m-0">
                  No skills in Act yet. Review and promote skills on the <strong>Decide</strong> tab to publish them here.
                </p>
              ) : (
                <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
                  {actSkills.map((s) => (
                    <SkillPromotionCard
                      key={s.id || s.name}
                      agentId={agent.id}
                      skill={s}
                      surface="act"
                      onChanged={reloadAgentFromParent}
                    />
                  ))}
                </div>
              )}
              {nonAct > 0 && (
                <div className="rounded-v2-md px-3 py-2 text-[11.5px] text-v2-ink-muted bg-v2-muted">
                  {nonAct} skill{nonAct > 1 ? 's are' : ' is'} still in Build/Decide — {nonAct > 1 ? 'they go' : 'it goes'} live once reviewed + advanced to Act.
                </div>
              )}
            </div>
          </Card>
        );
      })()}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          {/* G253 — V1 v5.3.0 architecture: assignments are READ-ONLY in
            * the agent detail page. All add/remove flows go through the
            * /team page (AgentsPage.jsx:6535,6847 explicitly commented:
            * "Read-only assignments: all management goes through Teams page").
            * The previous G194 inline modal was a regression. */}
          <Card.Header
            title="Assignment"
            actions={
              <Button
                variant="primary"
                size="sm"
                /* G254 — Navigate to the Teams page already filtered to
                 * THIS agent. Use the object form of navigate() because
                 * the string form was stripping query params in this
                 * routing configuration (verified via Chrome inspection
                 * — manual URL with the same params worked, but the
                 * string-form navigate dropped them at history level).
                 * Object form is explicit about pathname + search. */
                /* G367 — Was a legacy=1 bounce. V2 AssignmentsInventory
                    is now feature-complete (G359) with per-agent drill-
                    in slideout; the `agent` query param is read by V2
                    via the slideout's openAgentDetail flow when the
                    user lands on the Assignments tab. */
                onClick={() => navigate({
                  pathname: '/team',
                  search: `?tab=assignments&agent=${agent.id}`,
                })}
                iconLeft={<ExternalLink size={13} />}
                title={`Open Teams assignments filtered to ${agent.name}`}>
                Manage in Teams
              </Button>
            }
          />
          <Card.Body>
            <div className="rounded-v2-sm border border-v2-cyan-border bg-v2-cyan-soft px-3 py-2 text-[11.5px] text-v2-cyan mb-3">
              Assignments are managed on the <strong>Teams</strong> page. This card shows the live state read
              from the backend.
            </div>
            {assignmentsLoading && assignments.length === 0 ? (
              <div className="text-[12px] text-v2-ink-muted text-center py-3">Loading assignments…</div>
            ) : assignments.length === 0 ? (
              <div className="text-[12px] text-v2-ink-muted text-center py-3">
                No users or groups assigned to this Agentlet. Open Teams to add the first one.
              </div>
            ) : (
              <Table
                ariaLabel={`${agent.name} assignments`}
                density="compact"
                columns={[
                  { key: 'type', header: 'Type', render: (a) => (
                    <Pill tone={a.userId ? 'cyan' : 'trust'} size="sm">
                      {a.userId ? 'user' : 'group'}
                    </Pill>
                  )},
                  { key: 'name', header: 'Name', render: (a) => (
                    <span className="font-mono-v2 text-[11px]">
                      {a.userName || a.groupName || (a.userId || a.groupId || '').slice(0, 8) + '…'}
                    </span>
                  )},
                  { key: 'access', header: 'Access', render: (a) => (
                    <Pill tone={a.accessLevel === 'admin' ? 'danger'
                              : a.accessLevel === 'manage' ? 'warning' : 'success'} size="sm">
                      {a.accessLevel}
                    </Pill>
                  )},
                  { key: 'by', header: 'By', render: (a) => (
                    <span className="text-[11px] text-v2-ink-muted">
                      {a.assignedByName || (a.assignedBy ? a.assignedBy.slice(0, 8) + '…' : '—')}
                    </span>
                  )},
                  { key: 'when', header: 'Assigned', render: (a) => (
                    <span className="text-[11px] text-v2-ink-muted">
                      {a.createdAt ? new Date(a.createdAt).toLocaleString() : '—'}
                    </span>
                  )},
                ]}
                rows={assignments}
                rowKey={(a) => a.id}
              />
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="Inference mode" />
          <Card.Body>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                { id: 'self',     label: 'Self inference',     desc: 'Direct, sub-100 ms latency. Lighter governance.' },
                { id: 'platform', label: 'Platform inference', desc: 'Routes through lifecycle-service. Adds 200–500 ms.' },
              ].map((opt) => {
                const active = inferenceMode === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setInference(opt.id)}
                    className={
                      'v2-focus-ring text-left p-3 rounded-v2-sm border transition-colors duration-v2 ' +
                      (active
                        ? 'bg-v2-action-soft border-v2-action text-v2-action-strong'
                        : 'bg-v2-surface border-v2-border-subtle text-v2-ink-secondary hover:border-v2-border')
                    }
                  >
                    <div className="text-[12.5px] font-semibold">{opt.label}</div>
                    <div className="text-[11px] text-v2-ink-muted mt-0.5">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
            <p className="text-[11.5px] text-v2-ink-muted">
              Fallback automatically toggles to the alternate path on inference provider failure.
            </p>
          </Card.Body>
        </Card>
      </div>

      {/* G252 — Agentlets Live (V1 parity, AgentsPage.jsx:6400+). Shows
        * auto-discovered agentlet containers registered to the platform
        * — operators need to know where the agent will execute when
        * published. Per-row Ping fires a health check round-trip. */}
      <Card>
        <Card.Header
          title="Agentlets Live"
          actions={
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-v2-ink-muted">
                {agentletsLoading ? 'Loading…' : `${agentlets.length} container(s)`}
              </span>
              <Button variant="ghost" size="sm" onClick={refreshAgentlets} disabled={agentletsLoading}>
                Refresh
              </Button>
            </div>
          }
        />
        <Card.Body>
          {agentlets.length === 0 ? (
            <div className="text-[12.5px] text-v2-ink-muted py-3 space-y-3">
              <p>
                No agentlet container is registered <strong className="text-v2-ink">for this tenant</strong>.
                The publish gate only counts a container tied to this agent&apos;s tenant (or a global one),
                so start one scoped to this tenant — it will appear here and clear the
                <span className="font-medium"> “≥ 1 reachable agentlet container” </span> gate.
              </p>
              {/* RC-LLC-CONTAINER-002 — TENANT_ID is the critical line. Without it the
                  standalone container registers as global/default-tenant and the strict,
                  SEC-21-aligned publish gate will not count it for this tenant's agent. */}
              <pre className="font-mono-v2 text-[11px] leading-relaxed bg-v2-muted text-v2-ink-secondary rounded-v2-sm px-3 py-2.5 overflow-x-auto whitespace-pre text-left">
{`PLATFORM_URL=${window.location.origin.replace(/:\d+$/, ':8443')}/api/v1
PLATFORM_WS_URL=${window.location.origin.replace(/^http/, 'ws').replace(/:\d+$/, ':8443')}/ws
TENANT_ID=${agent?.tenantId || '<this tenant id>'}
AGENTLETS_API_KEY=<agt_wk_… worker key scoped to this tenant>`}
              </pre>
              <p className="text-[11px]">
                <strong className="text-v2-ink">TENANT_ID</strong> ties the container to this tenant.
                Generate the <span className="font-mono-v2">agt_wk_</span> worker key in the gateway key
                settings for this tenant. An existing container can be re-pointed by restarting it with
                these values — it re-registers under this tenant.
              </p>
            </div>
          ) : (
            <Table
              ariaLabel="Agentlets live"
              density="compact"
              columns={[
                { key: 'status', header: 'Status', render: (a) => {
                  const status = String(a.status || 'unknown').toLowerCase();
                  const tone = status === 'connected' ? 'success'
                    : status === 'stale' ? 'warning'
                    : status === 'disconnected' ? 'danger' : 'muted';
                  return <Pill tone={tone} size="sm" dot>{status}</Pill>;
                }},
                { key: 'id', header: 'Agentlet ID', render: (a) => (
                  <span className="font-mono-v2 text-[11px] text-v2-ink-secondary">
                    {a.id ? a.id.slice(0, 8) : '—'}…
                  </span>
                )},
                { key: 'url', header: 'Gateway URL', render: (a) => (
                  <span className="font-mono-v2 text-[11px] text-v2-ink-secondary">{a.gatewayUrl || a.url || '—'}</span>
                )},
                { key: 'version', header: 'Version', render: (a) => (
                  <span className="font-mono-v2 text-[11px]">{a.version || '—'}</span>
                )},
                { key: 'heartbeat', header: 'Last heartbeat', render: (a) => (
                  <span className="text-[11px] text-v2-ink-muted">
                    {a.lastHeartbeat ? new Date(a.lastHeartbeat).toLocaleString() : '—'}
                  </span>
                )},
                { key: 'actions', header: '', align: 'right', render: (a) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={pingingAgentletId === a.id}
                    disabled={pingingAgentletId === a.id}
                    onClick={() => handlePingAgentlet(a)}
                    title="Send a health-check ping to this agentlet">
                    Ping
                  </Button>
                )},
              ]}
              rows={agentlets}
              rowKey={(a) => a.id}
            />
          )}
        </Card.Body>
      </Card>

      {/* G252 — Publish history (V1 parity, AgentsPage.jsx:6911+). Calls
        * api.getPublications and renders the full V1 column set:
        * Version / Status / Target / Published By / Published / Rollback. */}
      <Card>
        <Card.Header
          title="Publish history"
          actions={
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-v2-ink-muted">
                {publicationsLoading ? 'Loading…' : `${publications.length} version(s)`}
              </span>
              <Button variant="ghost" size="sm" onClick={refreshPublications} disabled={publicationsLoading}>
                Refresh
              </Button>
            </div>
          }
        />
        <Card.Body>
          {publications.length === 0 ? (
            <p className="text-[12.5px] text-v2-ink-muted">This Agentlet has not been published yet.</p>
          ) : (
            <Table
              ariaLabel={`${agent.name} publish history`}
              density="compact"
              columns={[
                { key: 'version', header: 'Version', render: (p) => (
                  <span className="font-mono-v2 text-[12px] font-semibold">v{p.version || p.id?.slice(0, 4) || '?'}</span>
                )},
                { key: 'status',  header: 'Status', render: (p) => {
                  const s = String(p.status || 'pending').toLowerCase();
                  const tone = s === 'published' ? 'success'
                    : s === 'rolled_back' ? 'muted'
                    : s === 'unpublished' ? 'muted'
                    : s === 'failed' ? 'danger'
                    : s === 'publishing' ? 'warning' : 'cyan';
                  return <Pill tone={tone} size="sm">{s.replace(/_/g, ' ')}</Pill>;
                }},
                { key: 'target',  header: 'Target',     render: (p) => (
                  <span className="text-[11.5px]">{p.targetType || 'webchat'}</span>
                )},
                { key: 'by',      header: 'Published by', render: (p) => (
                  <span className="text-[11.5px] text-v2-ink-secondary">
                    {p.publishedByName || (p.publishedBy ? p.publishedBy.slice(0, 8) + '…' : '—')}
                  </span>
                )},
                { key: 'when',    header: 'Published',  render: (p) => (
                  <span className="text-[11px] text-v2-ink-muted">
                    {p.publishedAt ? new Date(p.publishedAt).toLocaleString() : '—'}
                  </span>
                )},
                { key: 'actions', header: '', align: 'right', render: (p) => (
                  p.status === 'published' ? (
                    <Button variant="ghost" size="sm" onClick={() => setRollbackTarget(p)}
                      title="Roll back to this version">
                      Rollback
                    </Button>
                  ) : null
                )},
              ]}
              rows={pgPublications.paginate(publications)}
              rowKey={(p) => p.id || `v${p.version}`}
            />
          )}
          {publications.length > pgPublications.pageSize && (
            <div className="mt-3">
              <Pagination
                totalItems={publications.length}
                page={pgPublications.page}
                pageSize={pgPublications.pageSize}
                onPageChange={pgPublications.setPage}
                onPageSizeChange={pgPublications.setPageSize}
                noun="versions"
              />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* G252 — Recent executions (V1 parity, AgentsPage.jsx:7003+).
        * Reads api.getExecutions. Post-publish observability surface so
        * operators can confirm live traffic is flowing through the agent. */}
      <Card>
        <Card.Header
          title="Recent executions"
          actions={
            <div className="flex items-center gap-2">
              <Pill tone={killSwitchActive ? 'danger' : 'success'} dot size="sm">
                {killSwitchActive ? 'Paused (kill switch)' : 'Streaming'}
              </Pill>
              <span className="text-[11px] text-v2-ink-muted">
                {executionsLoading ? 'Loading…' : `${executions.length} record(s)`}
              </span>
              <Button variant="ghost" size="sm" onClick={refreshExecutions} disabled={executionsLoading}>
                Refresh
              </Button>
            </div>
          }
        />
        <Card.Body>
          {executions.length === 0 ? (
            <div className="text-[12.5px] text-v2-ink-muted text-center py-4">
              No executions recorded yet. Once the agent is published and assigned to a user/group,
              each invocation will be recorded here with skill, status, latency, and mode.
            </div>
          ) : (
            <Table
              ariaLabel={`${agent.name} recent executions`}
              density="compact"
              columns={[
                { key: 'skill',  header: 'Skill',  render: (e) => e.skillName || '—' },
                { key: 'status', header: 'Status', render: (e) => {
                  const s = String(e.status || 'unknown').toLowerCase();
                  const tone = s === 'success' || s === 'completed' ? 'success'
                    : s === 'failed' || s === 'error' ? 'danger'
                    : s === 'running' ? 'cyan' : 'muted';
                  return <Pill tone={tone} size="sm">{s}</Pill>;
                }},
                { key: 'mode',   header: 'Mode',  render: (e) => (
                  <span className="text-[11.5px]">{(e.mode || '—').replace(/_/g, ' ').toLowerCase()}</span>
                )},
                { key: 'dur',    header: 'Duration', align: 'right', render: (e) => (
                  <span className="font-mono-v2 text-[11.5px] tabular-nums">{e.durationMs != null ? `${e.durationMs}ms` : '—'}</span>
                )},
                { key: 'when',   header: 'Created',  render: (e) => (
                  <span className="text-[11px] text-v2-ink-muted">
                    {e.createdAt ? new Date(e.createdAt).toLocaleString() : '—'}
                  </span>
                )},
              ]}
              rows={pgExecutions.paginate(executions)}
              rowKey={(e) => e.id || `${e.skillName}-${e.createdAt}`}
            />
          )}
          {executions.length > pgExecutions.pageSize && (
            <div className="mt-3">
              <Pagination
                totalItems={executions.length}
                page={pgExecutions.page}
                pageSize={pgExecutions.pageSize}
                onPageChange={pgExecutions.setPage}
                onPageSizeChange={pgExecutions.setPageSize}
                noun="executions"
              />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Kill-switch cascade visualization */}
      <Card>
        <Card.Header title="Kill-Switch Cascade" actions={<Pill tone={killSwitchActive ? 'danger' : 'success'} size="sm">{killSwitchActive ? 'ENGAGED' : 'Ready'}</Pill>} />
        <Card.Body>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {['Platform Admin', 'Org Admin', 'Tenant Admin', 'Developer'].map((tier) => (
              <div key={tier} className="bg-v2-muted rounded-v2-sm px-3 py-2 text-center">
                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted">{tier}</div>
                <div className="text-[12.5px] font-semibold text-v2-ink mt-0.5">
                  <span aria-hidden="true" className={'inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ' + (killSwitchActive ? 'bg-v2-danger animate-pulse' : 'bg-v2-success')} />
                  {killSwitchActive ? 'engaged' : 'ready'}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11.5px] text-v2-ink-muted mt-3">
            Activation priority: Platform Admin → Org Admin → Tenant Admin → Developer. Any level can engage;
            release requires the same or higher cascade level.
          </p>
        </Card.Body>
      </Card>

      {/* Publish confirmation modal */}
      <Modal
        isOpen={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        title={`Publish ${agent.name}?`}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPublishModalOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={publishing} onClick={handlePublish}>Publish to production</Button>
          </>
        }
      >
        <p className="text-[13px] text-v2-ink leading-relaxed mb-3">
          All gate criteria pass. Publishing will deploy this Agentlet to the standalone webchat and route
          live traffic through the configured inference mode.
        </p>
        <div className="bg-v2-muted rounded-v2-sm p-3 text-[12px] text-v2-ink-secondary space-y-1">
          <div><strong className="text-v2-ink">Skills published:</strong> {publishedSkills.length}</div>
          <div><strong className="text-v2-ink">Inference mode:</strong> {inferenceMode === 'self' ? 'Self' : 'Platform'}</div>
          <div><strong className="text-v2-ink">Assigned to:</strong> {assignedUsers.length} user(s), {assignedGroups.length} group(s)</div>
        </div>
      </Modal>

      {/* G194 — Assignment manager modal. Lists current user/group grants
       * with a remove button per row, plus a small "Add assignment" form
       * that accepts a UUID + access level. Calls api.createAssignment /
       * removeAssignment which target /api/v1/agents/{id}/assignments. */}
      {/* Phase 5B — Manage assignments: Modal → Slideout per G378.
        * Multi-row CRUD over users + groups with add/remove and
        * access-level pickers — definitively slideout-shaped. */}
      <Slideout
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        width={780}
        ariaLabel="Manage assignments"
        header={<h2 className="text-[15px] font-bold m-0">Manage assignments</h2>}
        footer={
          <div className="flex items-center justify-end w-full">
            <Button variant="ghost" onClick={() => setAssignOpen(false)}>Close</Button>
          </div>
        }
      >
        <div className="space-y-4 text-[12.5px]">
          {/* Current assignments table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[12.5px] font-bold text-v2-ink m-0">Current assignments</h4>
              <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={11} />}
                onClick={refreshAssignments} disabled={assignmentsLoading}>
                Reload
              </Button>
            </div>
            {assignmentsLoading ? (
              <div className="text-v2-ink-muted">Loading…</div>
            ) : assignments.length === 0 ? (
              <div className="text-v2-ink-muted">No assignments yet. Add one below.</div>
            ) : (
              <div className="border border-v2-border-subtle rounded-v2-sm overflow-hidden">
                {assignments.map((a) => (
                  <div key={a.id}
                    className="flex items-center gap-3 px-3 py-2 border-b border-v2-border-subtle last:border-b-0 text-[12px]">
                    <Pill tone={a.userId ? 'cyan' : 'trust'} size="sm">
                      {a.userId ? 'User' : 'Group'}
                    </Pill>
                    <span className="font-mono-v2 text-[11px] text-v2-ink-secondary flex-1 min-w-0 truncate">
                      {a.userId || a.groupId || a.principalId || '—'}
                    </span>
                    <Pill tone="muted" size="sm">{a.accessLevel || 'execute'}</Pill>
                    <Button
                      variant="ghost" size="sm"
                      className="!text-v2-danger hover:!bg-v2-danger-soft"
                      loading={removingAssignId === a.id}
                      disabled={removingAssignId === a.id}
                      onClick={() => handleRemoveAssignment(a)}
                      title="Remove this assignment">
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add assignment form */}
          <div className="border-t border-v2-border-subtle pt-4">
            <h4 className="text-[12.5px] font-bold text-v2-ink mb-2">Add assignment</h4>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Type</label>
                <select
                  value={addAssignKind}
                  onChange={(e) => setAddAssignKind(e.target.value)}
                  className="v2-focus-ring bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2 py-1.5 text-[12.5px]">
                  <option value="user">User</option>
                  <option value="group">Group</option>
                </select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
                  {addAssignKind === 'user' ? 'User ID' : 'Group ID'}
                </label>
                <input
                  type="text"
                  value={addAssignTargetId}
                  onChange={(e) => setAddAssignTargetId(e.target.value)}
                  placeholder="paste UUID from the Team page"
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2 py-1.5 text-[12.5px] font-mono-v2"
                />
              </div>
              <div>
                <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Access level</label>
                <select
                  value={addAssignAccess}
                  onChange={(e) => setAddAssignAccess(e.target.value)}
                  className="v2-focus-ring bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2 py-1.5 text-[12.5px]">
                  <option value="execute">execute</option>
                  <option value="approve">approve</option>
                  <option value="admin">admin</option>
                </select>
              </div>
              <Button variant="primary" onClick={handleAddAssignment} loading={addingAssign}>
                Add
              </Button>
            </div>
            <div className="text-[11px] text-v2-ink-muted mt-2">
              UUIDs for users/groups come from the Team page (click a row to reveal the ID).
            </div>
          </div>

          {assignError && (
            <div className="rounded-v2-sm border border-v2-danger-border bg-v2-danger-soft px-3 py-2 text-[12px] text-v2-danger">
              {assignError}
            </div>
          )}
        </div>
      </Slideout>

      {/* G193 — Rollback confirm modal. */}
      <Modal
        isOpen={!!rollbackTarget}
        onClose={rollingBack ? undefined : () => setRollbackTarget(null)}
        size="sm"
        title="Roll back to this version?"
        dismissOnBackdrop={false}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRollbackTarget(null)} disabled={rollingBack}>Cancel</Button>
            <Button variant="danger" onClick={handleRollbackConfirm} loading={rollingBack}>
              {rollingBack ? 'Rolling back…' : 'Roll back'}
            </Button>
          </>
        }
      >
        {rollbackTarget && (
          <div className="text-[12.5px] text-v2-ink leading-relaxed">
            Roll back to <strong>version {rollbackTarget.version || rollbackTarget.id}</strong>
            {rollbackTarget.publishedAt && (
              <> (published {new Date(rollbackTarget.publishedAt).toLocaleString()}
              {rollbackTarget.publishedBy ? ` by ${rollbackTarget.publishedBy}` : ''})</>
            )}?
            <div className="text-v2-ink-muted text-[11.5px] mt-2">
              This will replace the currently live agent bundle with the selected historical version.
              Connected agentlets will re-sync to the rolled-back skill set on next heartbeat.
              The rollback itself is recorded as a new publication entry in the audit trail.
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ── Reserved for any future placeholder tab use (currently unused). */
function PlaceholderTab({ phase, title, body }) {
  const navigate = useNavigate();
  return (
    <Card>
      <Card.Header title={`${phase} phase`} />
      <Card.Body>
        <h3 className="text-[15px] font-semibold text-v2-ink m-0 mb-2">{title}</h3>
        <p className="text-[13px] text-v2-ink-secondary leading-relaxed mb-4">{body}</p>
        <Button variant="primary" iconLeft={<ExternalLink />} onClick={() => navigate('/agents')}>
          Open the detail view
        </Button>
      </Card.Body>
    </Card>
  );
}

/* ── Resolver export ─────────────────────────────────────────── */
export default function ResolvedAgentDetailPage(props) {
  if (isRebirthAgentletDetailEnabled()) return <AgentDetailPageV2Inner {...props} />;
  // When the flag is off, redirect /agents/:id → /agents (v1 page).
  // Preserves bookmarks but uses the v1 selector for navigation.
  return <Navigate to="/agents" replace />;
}

export { AgentDetailPageV2Inner };
